import React, { useState, useRef, useEffect, useMemo } from "react";
import { detectLanguage, getTranslations, getAILanguage, GENRES_I18N, TONES_I18N, POV_I18N } from './i18n';
import { supabase } from './supabase';
import LandingPage from './LandingPage';
import HomePage from './HomePage';

function slugify(str) { return (str||"book").toLowerCase().replace(/\s+/g,"-").replace(/[^\w-]/g,""); }

// ─── SAFE JSON PARSER FOR AI RESPONSES ───────────────────────────────────────
// Handles: trailing commas, unescaped quotes in strings, control chars, markdown fences.
function safeParseAIJson(raw) {
  // 1. Strip markdown fences
  let s = raw.replace(/```json\s*/gi, "").replace(/```\s*/g, "").trim();

  // 2. Extract the first [...] or {...} block
  const arrStart = s.indexOf("["), objStart = s.indexOf("{");
  if (arrStart === -1 && objStart === -1) throw new Error("No JSON found in AI response");
  const isArr = arrStart !== -1 && (objStart === -1 || arrStart < objStart);
  const start = isArr ? arrStart : objStart;
  const end   = isArr ? s.lastIndexOf("]") : s.lastIndexOf("}");
  s = s.slice(start, end + 1);

  // 3. Replace real newlines/tabs (outside-string level, safe)
  s = s.replace(/[\r\n\t]+/g, " ");

  // 4. Remove trailing commas before ] or }
  s = s.replace(/,(\s*[}\]])/g, "$1");

  // 5. Try direct parse — works most of the time
  try { return JSON.parse(s); } catch (_) {}

  // 6. Fix unescaped double-quotes inside string values using a state machine
  //    e.g. "titulo": "O "Guardião" do Mundo"  →  "titulo": "O \"Guardião\" do Mundo"
  function repairQuotes(str) {
    let out = "", inStr = false, escaped = false;
    for (let i = 0; i < str.length; i++) {
      const ch = str[i];
      if (escaped) { out += ch; escaped = false; continue; }
      if (ch === "\\") { out += ch; escaped = true; continue; }
      if (ch === '"') {
        if (!inStr) { inStr = true; out += ch; continue; }
        // Inside a string — is this quote closing it, or is it an unescaped inner quote?
        // Look ahead past spaces to the next meaningful character
        let j = i + 1;
        while (j < str.length && str[j] === " ") j++;
        const next = str[j];
        const isStructural = next === ":" || next === "," || next === "}" || next === "]" || j >= str.length;
        if (isStructural) { inStr = false; out += ch; }   // closing quote
        else              { out += '\\"'; }                 // inner quote — escape it
        continue;
      }
      out += ch;
    }
    return out;
  }

  const repaired = repairQuotes(s);
  const cleaned  = repaired.replace(/,(\s*[}\]])/g, "$1");
  try { return JSON.parse(cleaned); } catch (_) {}

  // 7. Nuclear option — strip control chars and retry
  const stripControlChars = (value) => {
    let out = "";
    for (const ch of value) {
      const code = ch.charCodeAt(0);
      out += ((code <= 31) || (code >= 127 && code <= 159)) ? " " : ch;
    }
    return out;
  };
  const nuclear = stripControlChars(cleaned).replace(/,(\s*[}\]])/g, "$1");
  try { return JSON.parse(nuclear); } catch(finalErr) {
    // Log the raw string around the error position to help debug
    try {
      const pos = parseInt((finalErr.message.match(/position (\d+)/)||[])[1]||"0");
      console.error("[safeParseAIJson] Failed at pos", pos, "| context:", nuclear.slice(Math.max(0,pos-60), pos+60));
    } catch(_) {}
    throw finalErr;
  }
}

// ─── AI PROXY (Edge Function Supabase — DeepSeek key stays on server) ────────
async function callAI(messages, system, temperature = 0.4, maxTokens = 4000) {
  const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/ai-proxy`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ messages, system, temperature, max_tokens: maxTokens }),
  });
  if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e?.error?.message||`HTTP Error ${res.status}`); }
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data.text;
}

// ─── PDF ──────────────────────────────────────────────────────────────────────
async function generatePDF(config, characters, chapters, bookIdx=1) {
  await new Promise((resolve, reject) => {
    if (window.jspdf) return resolve();
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    s.onload = resolve; s.onerror = reject; document.head.appendChild(s);
  });
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ orientation:"portrait", unit:"mm", format:"a5" });
  const W=148,H=210,mX=18,mTop=22,mBot=18,cW=W-mX*2,bSz=10.5,lH=6;
  // jsPDF built-in Times font only supports Latin-1; use safe ornaments instead of Unicode symbols
  const ORN = "*  *  *";
  const ORN_SM = "* * *";

  // Strip markdown formatting from text before rendering in PDF
  const stripMd = (t="") => t
    .replace(/\*\*\*(.+?)\*\*\*/g, "$1")  // ***bold italic***
    .replace(/\*\*(.+?)\*\*/g, "$1")       // **bold**
    .replace(/\*(.+?)\*/g, "$1")           // *italic*
    .replace(/_{2}(.+?)_{2}/g, "$1")       // __underline__
    .replace(/^#+\s+/gm, "")              // # headings
    .replace(/^[-*]\s+/gm, "")            // bullet points
    .trim();

  // Translate character roles to Portuguese
  const translateRole = (r="") => {
    const map = {
      "protagonist":"Protagonista","protagonista":"Protagonista",
      "antagonist":"Antagonista","antagonista":"Antagonista",
      "supporting":"Coadjuvante","support":"Coadjuvante","coadjuvante":"Coadjuvante",
      "mentor":"Mentor","ally":"Aliado","aliado":"Aliado",
      "love interest":"Interesse Amoroso","sidekick":"Parceiro",
      "villain":"Vilao","heroine":"Heroina","hero":"Heroi",
    };
    return map[r.toLowerCase().trim()] || r;
  };
  let pn=1;
  const ap=()=>{doc.addPage();pn++;};
  const dH=(t)=>{doc.setFont("times","italic");doc.setFontSize(8);doc.setTextColor(120,100,70);doc.text(t,W/2,12,{align:"center"});doc.setDrawColor(180,155,100);doc.setLineWidth(0.3);doc.line(mX,14,W-mX,14);};
  const dF=()=>{doc.setFont("times","normal");doc.setFontSize(8);doc.setTextColor(150,130,90);doc.text(String(pn),W/2,H-8,{align:"center"});};
  const hLine=(y,x1=mX+15,x2=W-mX-15)=>{doc.setDrawColor(160,130,80);doc.setLineWidth(0.4);doc.line(x1,y,x2,y);};

  // ── CAPA ───────────────────────────────────────────────────────────────────
  doc.setFillColor(245,238,220); doc.rect(0,0,W,H,"F");
  doc.setDrawColor(160,130,80); doc.setLineWidth(0.8); doc.rect(8,8,W-16,H-16);
  doc.setLineWidth(0.3); doc.rect(10,10,W-20,H-20);

  // Resolve nomes: série vs. livro específico
  const seriesName  = config.chronicleName || config.title || ""; // ex: "Crônicas de Lúmina"
  const bookTitle   = (config.bookTitles && config.bookTitles[bookIdx-1]) || config.title || "Sem Título";
  const bookSuffix  = config.numBooks > 1 ? ` - Livro ${bookIdx}` : "";
  const bookDisplay = bookTitle + bookSuffix; // ex: "O Despertar de Valdora - Livro 1"
  const tagline     = config.subtitle || (config.numBooks > 1 ? `Uma saga de ${config.numBooks} livros` : "");

  let tY = 28;

  // 1. Nome da série — pequeno, dourado, no topo
  if (seriesName && seriesName !== bookTitle) {
    doc.setFont("times","italic"); doc.setFontSize(10); doc.setTextColor(150,115,55);
    doc.text(seriesName, W/2, tY, {align:"center"}); tY += 7;
    hLine(tY, mX+22, W-mX-22); tY += 10;
  } else {
    doc.setFont("times","normal"); doc.setFontSize(11); doc.setTextColor(160,130,80);
    doc.text(ORN, W/2, tY, {align:"center"}); tY += 14;
  }

  // 2. Título do livro — grande, negrito, centralizado
  doc.setFont("times","bold"); doc.setFontSize(22); doc.setTextColor(25,15,5);
  const tL = doc.splitTextToSize(bookDisplay.toUpperCase(), cW-8);
  tL.forEach(l => { doc.text(l, W/2, tY, {align:"center"}); tY += 10; });

  // 3. Tagline / subtítulo
  if (tagline) {
    tY += 3;
    doc.setFont("times","italic"); doc.setFontSize(11); doc.setTextColor(100,80,50);
    doc.splitTextToSize(tagline, cW-10).forEach(l => { doc.text(l, W/2, tY, {align:"center"}); tY += 7; });
  }

  // 4. Separador + gênero
  hLine(tY+6); tY += 14;
  doc.setFont("times","normal"); doc.setFontSize(9); doc.setTextColor(120,100,70);
  doc.text((config.genre||"").toUpperCase(), W/2, tY, {align:"center"}); tY += 10;
  doc.setFont("times","normal"); doc.setFontSize(10); doc.setTextColor(160,130,80);
  doc.text("- " + ORN_SM + " -", W/2, tY, {align:"center"}); tY += 10;

  // 5. Sinopse (até o espaço disponível)
  if (config.synopsis) {
    doc.setFont("times","italic"); doc.setFontSize(9); doc.setTextColor(70,55,35);
    const synLines = doc.splitTextToSize(`"${config.synopsis}"`, cW-14);
    const maxSynY = H - 46;
    synLines.forEach(l => { if(tY < maxSynY){ doc.text(l, W/2, tY, {align:"center"}); tY += 5.5; } });
  }

  // 6. Autor + ornamento rodapé
  doc.setFont("times","bold"); doc.setFontSize(11); doc.setTextColor(50,38,20);
  doc.text(config.author || "Autor Desconhecido", W/2, H-28, {align:"center"});
  doc.setFont("times","normal"); doc.setFontSize(10); doc.setTextColor(160,130,80);
  doc.text(ORN_SM, W/2, H-20, {align:"center"});

  // ── PERSONAGENS ────────────────────────────────────────────────────────────
  if (characters && characters.length > 0) {
    ap(); doc.setFillColor(245,238,220); doc.rect(0,0,W,H,"F");
    doc.setFont("times","bold"); doc.setFontSize(13); doc.setTextColor(30,20,10);
    doc.text("PERSONAGENS", W/2, 28, {align:"center"});
    hLine(32, mX+5, W-mX-5);
    let cy = 44;
    const LH_CHAR = 5; // line height na seção de personagens

    characters.forEach(ch => {
      const nome       = ch.nome||ch.name||"";
      const papel      = translateRole(ch.papel||ch.role||"");
      const person     = stripMd(ch.personalidade||ch.personality||"");
      const motiv      = stripMd(ch.motivacao||ch.motivation||"");
      const arco       = stripMd(ch.arco||ch.arc||"");

      // Calcula altura total do bloco
      const personLines = doc.setFont("times","italic") && doc.setFontSize(8.5) && doc.splitTextToSize(person, cW).length;
      const motivLines  = motiv ? doc.setFont("times","normal") && doc.setFontSize(8) && doc.splitTextToSize(`Motivacao: ${motiv}`, cW).length : 0;
      const arcoLines   = arco  ? doc.splitTextToSize(`Arco: ${arco}`, cW).length : 0;
      const blockH = 6 + 5 + personLines*LH_CHAR + (motiv ? motivLines*LH_CHAR+1 : 0) + (arco ? arcoLines*LH_CHAR+1 : 0) + 7;

      if (cy + blockH > H - 20) { dF(); ap(); doc.setFillColor(245,238,220); doc.rect(0,0,W,H,"F"); cy = 30; }

      // Nome
      doc.setFont("times","bold"); doc.setFontSize(10.5); doc.setTextColor(140,100,40);
      doc.text(nome, mX, cy); cy += 5;

      // Papel (pequeno, versalete simulado com uppercase)
      doc.setFont("times","normal"); doc.setFontSize(7.5); doc.setTextColor(140,110,60);
      doc.text(papel.toUpperCase(), mX, cy); cy += 5;

      // Personalidade (itálico, tom mais escuro)
      doc.setFont("times","italic"); doc.setFontSize(8.5); doc.setTextColor(60,48,30);
      doc.splitTextToSize(person, cW).forEach(l => { doc.text(l, mX, cy); cy += LH_CHAR; });

      // Motivação
      if (motiv) {
        cy += 1;
        doc.setFont("times","normal"); doc.setFontSize(8); doc.setTextColor(80,65,40);
        doc.splitTextToSize(`Motivacao: ${motiv}`, cW).forEach(l => { doc.text(l, mX, cy); cy += LH_CHAR; });
      }

      // Arco
      if (arco) {
        cy += 1;
        doc.setFont("times","normal"); doc.setFontSize(8); doc.setTextColor(80,65,40);
        doc.splitTextToSize(`Arco: ${arco}`, cW).forEach(l => { doc.text(l, mX, cy); cy += LH_CHAR; });
      }

      cy += 7; // espaço entre personagens
    });
    dF();
  }

  // ── CAPÍTULOS ──────────────────────────────────────────────────────────────
  chapters.filter(c => c.status==="done" && c.content).forEach(ch => {
    ap(); doc.setFillColor(245,238,220); doc.rect(0,0,W,H,"F"); dH(ch.title);
    let y = mTop + 8;
    // "CAPÍTULO X"
    doc.setFont("times","normal"); doc.setFontSize(8); doc.setTextColor(140,115,70);
    doc.text(`CAPITULO ${ch.id}`, W/2, y, {align:"center"}); y += 7;
    // Título do capítulo
    doc.setFont("times","bold"); doc.setFontSize(14); doc.setTextColor(30,20,10);
    doc.splitTextToSize(ch.title.replace(/^(Capítulo|Chapter) \d+:?\s*/i,""), cW).forEach(l => { doc.text(l, W/2, y, {align:"center"}); y += 7; });
    // Ornamento
    doc.setFont("times","normal"); doc.setFontSize(10); doc.setTextColor(160,130,80);
    doc.text("- " + ORN_SM + " -", W/2, y+3, {align:"center"}); y += 12;
    // Texto do capítulo (strip markdown antes de renderizar)
    doc.setFont("times","normal"); doc.setFontSize(bSz); doc.setTextColor(30,20,10);
    const dropCapX = mX + 12; // deslocamento do resto da linha após a capitular
    ch.content.split(/\n+/).filter(p=>p.trim()).forEach((para,pi) => {
      const cleanPara = stripMd(para.trim());
      if (!cleanPara) return;
      const ls = doc.splitTextToSize(cleanPara, cW);
      if (y + ls.length*lH > H-mBot) { dF(); ap(); doc.setFillColor(245,238,220); doc.rect(0,0,W,H,"F"); dH(ch.title); y=mTop+8; doc.setFont("times","normal"); doc.setFontSize(bSz); doc.setTextColor(30,20,10); }
      if (pi===0) {
        // Capitular: letra grande + resto da linha com recuo suficiente
        const fc = cleanPara.charAt(0);
        const restLine = doc.splitTextToSize(cleanPara.slice(1), cW - (dropCapX - mX));
        doc.setFont("times","bold"); doc.setFontSize(28); doc.setTextColor(140,100,40);
        doc.text(fc, mX, y+5);
        doc.setFont("times","normal"); doc.setFontSize(bSz); doc.setTextColor(30,20,10);
        restLine.forEach(l => { if(y+lH>H-mBot){dF();ap();doc.setFillColor(245,238,220);doc.rect(0,0,W,H,"F");dH(ch.title);y=mTop+8;doc.setFont("times","normal");doc.setFontSize(bSz);doc.setTextColor(30,20,10);} doc.text(l, dropCapX, y); y+=lH; });
      } else {
        ls.forEach(l => { if(y+lH>H-mBot){dF();ap();doc.setFillColor(245,238,220);doc.rect(0,0,W,H,"F");dH(ch.title);y=mTop+8;doc.setFont("times","normal");doc.setFontSize(bSz);doc.setTextColor(30,20,10);} doc.text(l,mX,y); y+=lH; });
      }
      y+=3;
    });
    dF();
  });
  doc.save(`${slugify(config.title||"livro")}.pdf`);
}

// ─── EPUB ─────────────────────────────────────────────────────────────────────
async function generateEPUB(config, characters, chapters, bookIdx=1) {
  await new Promise((resolve,reject)=>{if(window.JSZip)return resolve();const s=document.createElement("script");s.src="https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";s.onload=resolve;s.onerror=reject;document.head.appendChild(s);});

  // Helpers (mesmos do PDF)
  const stripMd = (t="") => t
    .replace(/\*\*\*(.+?)\*\*\*/g,"$1").replace(/\*\*(.+?)\*\*/g,"$1")
    .replace(/\*(.+?)\*/g,"$1").replace(/_{2}(.+?)_{2}/g,"$1")
    .replace(/^#+\s+/gm,"").replace(/^[-*]\s+/gm,"").trim();
  const escHtml = (t="") => t.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  const translateRole = (r="") => {
    const map={"protagonist":"Protagonista","protagonista":"Protagonista","antagonist":"Antagonista","antagonista":"Antagonista","supporting":"Coadjuvante","support":"Coadjuvante","coadjuvante":"Coadjuvante","mentor":"Mentor","ally":"Aliado","aliado":"Aliado","love interest":"Interesse Amoroso","sidekick":"Parceiro","villain":"Vilao","heroine":"Heroina","hero":"Heroi"};
    return map[r.toLowerCase().trim()] || r;
  };

  const seriesName = config.chronicleName || config.title || "";
  const bookTitle  = (config.bookTitles && config.bookTitles[bookIdx-1]) || config.title || "Sem Titulo";
  const bookSuffix = config.numBooks > 1 ? ` - Livro ${bookIdx}` : "";
  const displayTitle = bookTitle + bookSuffix;
  const author = config.author || "Autor Desconhecido";
  const done = chapters.filter(c=>c.status==="done"&&c.content);

  const zip = new window.JSZip();
  zip.file("mimetype","application/epub+zip");
  zip.folder("META-INF").file("container.xml",`<?xml version="1.0"?><container version="1.0" xmlns="urn:oasis:schemas:container"><rootfiles><rootfile full-path="OEBPS/content.opf" media-type="application/oebps-package+xml"/></rootfiles></container>`);
  const ob = zip.folder("OEBPS");

  // CSS — inclui estilo para papel + capitular espaçado
  ob.file("style.css", [
    "body{font-family:Georgia,serif;font-size:1.1em;line-height:1.85;color:#1a1208;margin:5% 8%;}",
    "h1{text-align:center;font-size:2em;margin:1.5em 0 .2em;color:#1a1208;}",
    "h2{font-size:1.3em;text-align:center;margin-top:2.5em;color:#1a1208;}",
    ".series{text-align:center;font-style:italic;font-size:.95em;color:#8a6a30;margin-bottom:.4em;}",
    ".tagline{text-align:center;font-style:italic;font-size:1em;color:#6a5030;margin:.5em 0 1em;}",
    ".ornament{text-align:center;color:#b8892a;margin:1.2em 0;font-size:1.1em;}",
    ".genre{text-align:center;color:#b8892a;letter-spacing:.18em;text-transform:uppercase;font-size:.8em;margin:.5em 0;}",
    ".synopsis{font-style:italic;text-align:center;color:#5a4a30;margin:1em 2em;}",
    ".author{text-align:center;font-weight:bold;margin-top:2.5em;font-size:1em;}",
    "p{margin:.6em 0;text-align:justify;}",
    "p.capitular::first-letter{font-size:3.2em;float:left;line-height:.75;margin-right:.12em;margin-top:.05em;color:#b8892a;font-weight:bold;}",
    ".cap-label{text-align:center;font-size:.72em;letter-spacing:.22em;text-transform:uppercase;color:#b8892a;margin-bottom:.2em;}",
    ".char-name{font-weight:bold;color:#b8892a;margin-top:1.2em;font-size:1em;}",
    ".char-role{font-size:.78em;letter-spacing:.12em;text-transform:uppercase;color:#a07840;margin:.1em 0 .3em;}",
    ".char-desc{font-style:italic;color:#4a3820;font-size:.92em;margin:.2em 0;}",
    ".char-extra{color:#5a4830;font-size:.88em;margin:.15em 0;}",
  ].join(""));

  // Capa
  ob.file("cover.xhtml", `<?xml version="1.0" encoding="utf-8"?><!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.1//EN" "http://www.w3.org/TR/xhtml11/DTD/xhtml11.dtd"><html xmlns="http://www.w3.org/1999/xhtml"><head><meta charset="utf-8"/><title>${escHtml(displayTitle)}</title><link rel="stylesheet" href="style.css"/></head><body>
${seriesName && seriesName!==bookTitle ? `<p class="series">${escHtml(seriesName)}</p>` : `<div class="ornament">* * *</div>`}
<h1>${escHtml(displayTitle)}</h1>
${config.subtitle ? `<p class="tagline">${escHtml(config.subtitle)}</p>` : config.numBooks>1 ? `<p class="tagline">Uma saga de ${config.numBooks} livros</p>` : ""}
<p class="genre">${escHtml(config.genre||"")}</p>
<div class="ornament">- * * * -</div>
${config.synopsis ? `<p class="synopsis">"${escHtml(config.synopsis)}"</p>` : ""}
<p class="author">${escHtml(author)}</p>
<div class="ornament">* * *</div>
</body></html>`);

  // Personagens
  const hC = characters && characters.length > 0;
  if (hC) {
    const cH = characters.map(c => {
      const papel  = escHtml(translateRole(c.papel||c.role||""));
      const person = escHtml(stripMd(c.personalidade||c.personality||""));
      const motiv  = escHtml(stripMd(c.motivacao||c.motivation||""));
      const arco   = escHtml(stripMd(c.arco||c.arc||""));
      return [
        `<p class="char-name">${escHtml(c.nome||c.name||"")}</p>`,
        papel  ? `<p class="char-role">${papel}</p>` : "",
        person ? `<p class="char-desc">${person}</p>` : "",
        motiv  ? `<p class="char-extra">Motivacao: ${motiv}</p>` : "",
        arco   ? `<p class="char-extra">Arco: ${arco}</p>` : "",
      ].join("");
    }).join("");
    ob.file("characters.xhtml", `<?xml version="1.0" encoding="utf-8"?><!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.1//EN" "http://www.w3.org/TR/xhtml11/DTD/xhtml11.dtd"><html xmlns="http://www.w3.org/1999/xhtml"><head><meta charset="utf-8"/><title>Personagens</title><link rel="stylesheet" href="style.css"/></head><body><h2>Personagens</h2><div class="ornament">* * *</div>${cH}</body></html>`);
  }

  // Capítulos — strip markdown no conteúdo
  done.forEach(ch => {
    const chTitle = ch.title.replace(/^(Capítulo|Chapter) \d+:?\s*/i,"") || ch.title;
    const paras = ch.content.split(/\n+/)
      .map(p => stripMd(p.trim())).filter(p => p)
      .map((p,i) => `<p${i===0?' class="capitular"':""}>${escHtml(p)}</p>`)
      .join("");
    ob.file(`chapter${ch.id}.xhtml`, `<?xml version="1.0" encoding="utf-8"?><!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.1//EN" "http://www.w3.org/TR/xhtml11/DTD/xhtml11.dtd"><html xmlns="http://www.w3.org/1999/xhtml"><head><meta charset="utf-8"/><title>${escHtml(ch.title)}</title><link rel="stylesheet" href="style.css"/></head><body><p class="cap-label">Capitulo ${ch.id}</p><h2>${escHtml(chTitle)}</h2><div class="ornament">* * *</div>${paras}</body></html>`);
  });

  const mf = [
    `<item id="style" href="style.css" media-type="text/css"/>`,
    `<item id="cover" href="cover.xhtml" media-type="application/xhtml+xml"/>`,
    ...(hC ? [`<item id="chars" href="characters.xhtml" media-type="application/xhtml+xml"/>`] : []),
    ...done.map(c=>`<item id="ch${c.id}" href="chapter${c.id}.xhtml" media-type="application/xhtml+xml"/>`),
  ].join("");
  const sp = [
    `<itemref idref="cover"/>`,
    ...(hC ? [`<itemref idref="chars"/>`] : []),
    ...done.map(c=>`<itemref idref="ch${c.id}"/>`),
  ].join("");
  ob.file("content.opf", `<?xml version="1.0" encoding="utf-8"?><package xmlns="http://www.idpf.org/2007/opf" unique-identifier="uid" version="2.0"><metadata xmlns:dc="http://purl.org/dc/elements/1.1/"><dc:title>${escHtml(displayTitle)}</dc:title><dc:creator>${escHtml(author)}</dc:creator><dc:language>pt</dc:language><dc:identifier id="uid">autoria-${Date.now()}</dc:identifier></metadata><manifest>${mf}</manifest><spine toc="ncx">${sp}</spine></package>`);

  const blob = await zip.generateAsync({type:"blob", mimeType:"application/epub+zip"});
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${slugify(bookTitle||"livro")}.epub`;
  a.click();
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function AutorIA() {
  const lang = useMemo(() => detectLanguage(), []);
  const T = useMemo(() => getTranslations(lang), [lang]);
  const aiLang = useMemo(() => getAILanguage(lang), [lang]);
  const GENRES = useMemo(() => GENRES_I18N[lang] || GENRES_I18N['en'], [lang]);
  const TONES  = useMemo(() => TONES_I18N[lang]  || TONES_I18N['en'],  [lang]);
  const POV    = useMemo(() => POV_I18N[lang]    || POV_I18N['en'],    [lang]);

  // ── SESSION PERSISTENCE ──────────────────────────────────────────────────────
  const emptyPanel = {
    // Foundation
    title:"", genre:"", tone:"", pov:"", synopsis:"",
    // Characters — surface
    protagonist:"", antagonist:"", supportChars:"",
    // Characters — depth
    protagonistWound:"", protagonistNeed:"", protagonistDesire:"", protagonistVoice:"",
    antagonistMotive:"", antagonistArc:"",
    // World
    setting:"", worldRules:"", keyLocations:"",
    factions:"", significantItems:"", worldHistory:"", sensoryAtmosphere:"",
    // Narrative
    conflict:"", stakes:"", incitingIncident:"", themes:"", subplots:"",
    // Plot structure
    plotPoint1:"", midpoint:"", plotPoint2:"", climax:"", resolution:"",
    // Style
    writingStyle:"", influences:"", referenceAuthors:"", prosePace:"", stylisticAvoid:"",
    // Series
    seriesArc:"", bookResolution:"", nextHooks:"",
  };
  const _stored = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("autoria_v1") || "{}"); } catch { return {}; }
  }, []); // eslint-disable-line
  const _isNewFormat = Array.isArray(_stored.projects);
  const _projectsFromLocal = _isNewFormat ? _stored.projects : [];

  const [phase, setPhase] = useState("biblia");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");

  // Bíblia
  const [ideaInput, setIdeaInput] = useState("");

  // Characters
  const [characters, setCharacters] = useState([]);
  const [editingChar, setEditingChar] = useState(null);
  const [charFeedback, setCharFeedback] = useState("");
  const [generatingChar, setGeneratingChar] = useState(false);

  // Config
  const [config, setConfig] = useState({
    title:"", subtitle:"", author:"", genre:"", tone:"", pov:"",
    numChapters:6, synopsis:"", writingStyle:"", setting:"",
    protagonist:"", antagonist:"", numBooks:1, coverUrl:""
  });

  // Series
  const [currentBookIdx, setCurrentBookIdx] = useState(1);
  const [canonicalSummaries, setCanonicalSummaries] = useState([]);

  // Biblia chat
  const [bibliaChat, setbibliaChat] = useState([]);
  const [bibliaChatInput, setBibliaChatInput] = useState("");
  const [bibliaChatLoading, setBibliaChatLoading] = useState(false);
  const bibliaChatEndRef = useRef(null);
  const bibliaChatLastMsgRef = useRef(null);
  const bibliaChatInputRef = useRef(null);

  // Biblia panel
  const [bibliaPanel, setbibliaPanel] = useState({...emptyPanel});
  const [bibliaLocked, setbibliaLocked] = useState({});
  const [bibliaExpanded, setbibliaExpanded] = useState({});
  const [bibliaReviewed, setbibliaReviewed] = useState(false);
  const [showBibliaReview, setShowBibliaReview] = useState(false);
  const [bibliaEditField, setbibliaEditField] = useState(null);
  const [bibliaEditValue, setbibliaEditValue] = useState("");
  const bibliaLockedRef = useRef({});
  // Live update feedback
  const [bibliaFlashFields, setBibliaFlashFields] = useState({}); // { fieldKey: true } — flash temporário
  const [bibliaUpdatedKeys, setBibliaUpdatedKeys] = useState({}); // { fieldKey: true } — persistente na sessão
  const [bibliaUpdateToast, setBibliaUpdateToast] = useState(null); // { count, label }
  const bibliaToastTimerRef = useRef(null);

  // Refinement chat
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Outline + Writing
  const [outline, setOutline] = useState([]);
  const [editingOutline, setEditingOutline] = useState(false);
  const [outlineNextToGenerate, setOutlineNextToGenerate] = useState(0);
  const [outlineBatchSize, setOutlineBatchSize] = useState(5);
  const [outlinePlanWarnings, setOutlinePlanWarnings] = useState({}); // { chapterId: string }
  const [outlineAuthorNotes, setOutlineAuthorNotes] = useState("");
  const [fixingOutlineChapter, setFixingOutlineChapter] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [nextChapterToWrite, setNextChapterToWrite] = useState(0);
  const [writingBatchSize, setWritingBatchSize] = useState(3);
  const [scenes, setScenes] = useState({});  // { chapterId: [{objetivo, mudanca, hookEntrada, hookSaida, tensao}] }
  const [generatingScenes, setGeneratingScenes] = useState(false);
  const sceneAutoRepairRanRef = useRef(false);
  const book1AutoCanonRanRef = useRef(false);
  const [openChapter, setOpenChapter] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [showMobileLeft, setShowMobileLeft] = useState(false);
  const [showMobileRight, setShowMobileRight] = useState(false);
  const abortRef = useRef(false);

  // Auth
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const syncTimeoutRef = useRef(null);

  // Multi-project
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [allProjects, setAllProjects] = useState(_projectsFromLocal);
  const allProjectsRef = useRef(_projectsFromLocal);
  const openingProjectRef = useRef(false); // flag to skip save right after openProject
  const isLoadingSupabaseRef = useRef(false); // prevent concurrent loadFromSupabase calls
  const userRef = useRef(null); // always-current user for beforeunload

  // ── FEATURE STATES ───────────────────────────────────────────────────────────
  const [saveStatus, setSaveStatus] = useState("idle"); // "idle"|"saving"|"saved"|"error"
  const saveRequestIdRef = useRef(0);
  const [chapterSummaries, setChapterSummaries] = useState([]); // rolling summaries per chapter
  const [showDraftReader, setShowDraftReader] = useState(false);
  const [showBookReader, setShowBookReader] = useState(false);
  const [bookReaderDark,       setBookReaderDark]       = useState(true);
  const [bookReaderMenu,       setBookReaderMenu]       = useState(false);
  const [bookReaderFontIdx,    setBookReaderFontIdx]    = useState(3);   // índice em fontSizes[]
  const [bookReaderWidthIdx,   setBookReaderWidthIdx]   = useState(1);   // 0=estreita 1=normal 2=larga
  const [bookReaderLineHIdx,   setBookReaderLineHIdx]   = useState(1);   // índice em lineHeights[]
  const [bookReaderBrightness, setBookReaderBrightness] = useState(100); // 50-130
  const [bookReaderPageMode,   setBookReaderPageMode]   = useState(false); // false=rolagem, true=paginado
  const [bookReaderPage,       setBookReaderPage]        = useState(0);     // página atual no modo paginado
  const [bookReaderPageDir,    setBookReaderPageDir]     = useState(1);     // 1=avançar -1=voltar (para animação)
  const [bookReaderScrollPct,  setBookReaderScrollPct]  = useState(0);     // 0-100 progresso de rolagem
  const [bookReaderScrollSec,  setBookReaderScrollSec]  = useState('');    // seção visível no modo rolagem
  const [bookReaderScrollChId, setBookReaderScrollChId] = useState(null);  // chId do capítulo visível no modo rolagem
  const [bookReaderPageScroll, setBookReaderPageScroll] = useState(0);     // 0-1 scroll dentro da página atual
  const [bookReaderBmTs,       setBookReaderBmTs]        = useState(0);     // timestamp p/ re-render ao salvar marcador
  const [showPreOutlineModal, setShowPreOutlineModal] = useState(false);
  const [continuityWarnings, setContinuityWarnings] = useState({}); // { chapterId: string|null }
  const [fixingChapter, setFixingChapter] = useState(null); // chapterId being rewritten
  const [outlineAdherence, setOutlineAdherence] = useState({}); // { chapterId: {ok:bool, detail:string} }
  const [bookAnalysis, setBookAnalysis] = useState(null); // full book analysis result
  const [analyzingBook, setAnalyzingBook] = useState(false);
  const [showBookAnalysis, setShowBookAnalysis] = useState(false);
  // Book transition screen
  const [showBookTransition, setShowBookTransition] = useState(false);
  const [transitionData, setTransitionData] = useState(null); // { nextIdx, newSummaries, nextTitle }
  const [editableTransitionSummary, setEditableTransitionSummary] = useState("");
  const [nextBookNotes, setNextBookNotes] = useState("");
  const [onboardingStep, setOnboardingStep] = useState(() =>
    localStorage.getItem("autoria_onboarding_done") ? null : 0
  );
  const CANON_PATCH_1_5 = {
    1: {
      titulo: "Desvio",
      ato: 1,
      papel: "inciting_incident",
      resumo: "Kael e Isa vivem uma rotina pacata em Valdora, marcada pela ausência da mãe e pelo legado de seu misterioso trabalho. Kael luta com seu ofício de ferreiro, enquanto Isa investiga o grimório deixado pela mãe. Um fenômeno sensorial súbito atinge a vila: um silêncio absoluto seguido por uma dissonância aguda que distorce a percepção da realidade. Os acordos cotidianos falham — o fogo não aquece, a água não sacia. O mundo, por um instante, sai do acordo. A experiência é breve, mas deixa uma sensação de vazio e um cheiro residual de ozônio. Os irmãos, cada um à sua maneira, sentem um eco interno, uma dor no pulso. O capítulo termina com eles se encarando, sabendo que algo fundamental mudou.",
      objetivo_pov: "Kael: proteger Isa do desconforto do fenômeno. Isa: registrar e compreender a anomalia no grimório.",
      mudanca: "A pacífica ignorância de Valdora é quebrada. O Desvio introduz a ameaça de Umbrael de forma sensorial e imediata, forçando os irmãos a confrontar que o mundo ao seu redor é frágil.",
    },
    2: {
      titulo: "Eco",
      ato: 1,
      papel: "rising_action",
      resumo: "No rescaldo do Desvio, a vila tenta retomar a normalidade, mas um mal-estar persiste. Isa confronta a Anciã Mira nos arquivos da vila, buscando registros sobre fenômenos similares, e é barrada com evasivas. Enquanto isso, Kael é pressionado por Garan sobre um estranho incidente na forja onde as chamas ficaram azuis e responderam à sua emoção. Em casa, Isa examina o Espelho Embaraçado da mãe, que agora mostra apenas a palavra Escute. Separadamente, cada irmão experimenta um Eco: Kael ouve sussurros de múltiplas vozes confusas em sua mente, enquanto Isa vê sombras que se movem contra a luz. Eles escondem essas experiências um do outro, com medo e confusão. O capítulo termina com o primeiro avistamento de algo errado na floresta: uma silhueta que não projeta sombra.",
      objetivo_pov: "Isa: encontrar respostas oficiais sobre o Desvio. Kael: negar e suprimir as vozes e anomalias em torno dele.",
      mudanca: "A ameaça deixa de ser apenas um evento e se torna uma presença ativa e investigável. Os poderes dos irmãos começam a se manifestar de forma incontrolável, criando uma barreira de segredos entre eles.",
    },
    3: {
      titulo: "Limiar",
      ato: 1,
      papel: "first_confrontation",
      resumo: "A silhueta sem sombra é investigada. Kael e Isa, relutantemente unidos pela preocupação, seguem rastros estranhos na floresta. Eles encontram Lyra, uma mercenária desiludida e ferida, sendo perseguida por um Umbrin. A primeira batalha é caótica e visceral. Kael, em pânico, tenta proteger Isa e involuntariamente corta o fluxo de intenção do Umbrin com um gesto instintivo, desorientando-o. Isa, por instinto, ergue as mãos e uma luz dourada e quente irrompe, afastando a criatura. Lyra os salva com uma flecha precisa no olho da besta, que se dissolve em um Fragmento de Sombria. O trio recua para a cabana de Lyra à beira da floresta. O capítulo termina com Lyra examinando o fragmento e os irmãos, e fazendo a pergunta crucial: O que diabos vocês são?",
      objetivo_pov: "Kael: sobreviver e manter Isa segura a qualquer custo. Isa: usar seu conhecimento para entender e repelir a ameaça.",
      mudanca: "A ameaça se torna física e letal. Os irmãos são forçados a usar seus poderes latentes para sobreviver, confirmando que são diferentes. A aliada Lyra é introduzida, ligando-os ao conflito maior.",
    },
    4: {
      titulo: "Custo",
      ato: 2,
      papel: "setup",
      resumo: "Refugiados na cabana de Lyra, as tensões surgem. Lyra revela ser uma Tecelã do Último Sussurro caída e explica o básico sobre Umbrael e suas criaturas. Ela identifica o Fragmento de Sombria como resíduo físico de um Acordo corrompido. Kael e Isa revelam parte de sua história e dos ecos que sentem. Lyra os alerta sobre o custo da magia não pactuada. O Ancião Thorne chega à cabana, revelando que sentiu a ruptura no Éter. Ele confirma que os irmãos são os Portadores da Semente e que o Desvio foi um ataque direto de Umbrael para encontrá-los. Oferece orientação, mas insiste que a escolha de aceitar o destino é deles. Kael quer fugir; Isa quer enfrentar. O capítulo termina com um corvo não natural na janela e Thorne pálido: É Corvus. Ele sabe.",
      objetivo_pov: "Kael: encontrar uma saída que não envolva aceitar mais dor. Isa: obter as verdades que Thorne e Lyra podem oferecer.",
      mudanca: "Os irmãos passam de vítimas confusas a peças centrais de um conflito cósmico. A escolha deixa de ser teórica: a ameaça tem nome e está vindo para eles.",
    },
    5: {
      titulo: "Pacto",
      ato: 2,
      papel: "turning_point",
      resumo: "O ataque começa. Umbrins e Eidólons atacam Valdora, guiados por uma presença distante. No caos, Kael e Isa são separados. Kael, encurralado por um Eidólon com a forma de sua mãe, é levado ao limite. A voz de Aetheris ressoa: Um Acordo não é uma corrente. É uma ponte. Vendo Isa em perigo, Kael aceita o pacto com Aetheris. A dor é excruciante: a Marca sangra e ele sente uma sobrecarga do fluxo de todas as coisas. Com um gesto de Claridade, ele corta a conexão do Eidólon com sua forma falsa, dissipando-o. O custo é imediato: Kael desmaia, exausto. Isa testemunha e entende o preço do poder. O capítulo termina com ela ao lado do irmão inconsciente, Lyra e Thorne lutando para protegê-los, e uma silhueta distante observando da floresta.",
      objetivo_pov: "Kael: proteger Isa, mesmo que isso signifique aceitar a dor do pacto. Isa: sobreviver e proteger o irmão vulnerável.",
      mudanca: "Kael transcende a negação e se torna um Portador ativo, ainda debilitado. O conflito escala para ataque total e a ameaça de Corvus é confirmada.",
    },
  };
  const CANON_PATCH_6_10 = {
    6: {
      titulo: "Cicatriz",
      ato: 2,
      papel: "rising_action",
      resumo: "No amanhecer após a queda de Valdora, Kael desperta com a Marca ardendo e sente o custo real de Aetheris. Thorne confirma que o ataque foi uma sonda de Corvus e que permanecer na vila é suicídio. Isa insiste em partir para Helvar em busca de respostas sobre Elara, e os irmãos deixam o lar para trás.",
      objetivo_pov: "Kael aceitar a fuga sem perder o senso de proteção sobre Isa.",
      mudanca: "Os irmãos rompem definitivamente com o status quo e iniciam a jornada ativa.",
    },
    7: {
      titulo: "Travessia",
      ato: 2,
      papel: "rising_action",
      resumo: "Na rota para Helvar, o grupo cruza uma zona de Éter instável onde sons e luz perdem ritmo. Uma emboscada de saqueadores força Kael a usar Claridade de forma instintiva, pagando com sangramento e vertigem. Isa manifesta um lampejo de Luz para salvar Thorne, mas termina exausta.",
      objetivo_pov: "Sobreviver à travessia e manter Thorne vivo até Helvar.",
      mudanca: "A confiança entre os irmãos cresce, e o custo dos poderes fica impossível de negar.",
    },
    8: {
      titulo: "Portões de Helvar",
      ato: 2,
      papel: "rising_action",
      resumo: "Ao chegar a Helvar, o grupo é barrado por suspeita de contaminação sombria. No caos de refugiados, Eidólons infiltrados forçam Kael e Isa a agir em público: Kael usa Éter para romper uma forma falsa, e Isa revela um lampejo híbrido de Luz/Sombra. Eles entram na fortaleza, mas sob vigilância rígida.",
      objetivo_pov: "Garantir entrada em Helvar sem condenar o grupo.",
      mudanca: "Eles conquistam abrigo, porém tornam-se alvo político dentro dos muros.",
    },
    9: {
      titulo: "O Preço do Acordo",
      ato: 2,
      papel: "rising_action",
      resumo: "Em Helvar, Kael passa por um teste de ancoragem do Éter e falha na primeira tentativa, colapsando sob dor e memória. Isa quase quebra as regras ao tentar protegê-lo e libera um pulso sombrio involuntário. O conselho aceita treiná-los, mas uma marca de Corvus encontrada em Helvar indica infiltração interna.",
      objetivo_pov: "Kael provar que pode ser treinado sem se destruir; Isa impedir sua queda.",
      mudanca: "Eles ganham acesso ao treinamento, mas sob contenção e suspeita.",
    },
    10: {
      titulo: "Sinal Negro",
      ato: 2,
      papel: "rising_action",
      resumo: "Na primeira missão curta fora dos muros, o grupo recupera um selo de contenção antes que Umbrins o corrompam. Kael atua com mais precisão por janelas curtas, Isa sustenta proteção com alto desgaste, e a missão termina em vitória local. O selo revela mensagem de Corvus: ele quer quebrar o Acordo por dentro.",
      objetivo_pov: "Concluir a missão sem perder controle dos poderes recém-despertos.",
      mudanca: "Primeira vitória operacional do grupo, com ameaça maior claramente escalada.",
    },
  };
  const CANON_PATCH_11_25 = {
    11: {
      titulo: "Provação",
      ato: 2,
      papel: "rising_action",
      resumo: "Kael inicia treinamento formal de ancoragem em Helvar e descobre que controle do Éter exige disciplina dolorosa, não força bruta. Isa acessa registros restritos e encontra indícios de que Elara investigava a Semente como cura dos acordos fraturados. A dupla avança, mas sob vigilância crescente do conselho.",
      objetivo_pov: "Kael: dominar o básico sem colapsar; Isa: obter respostas sobre Elara e a Semente.",
      mudanca: "Os irmãos deixam a improvisação e entram em aprendizado técnico, com risco político interno.",
    },
    12: {
      titulo: "Fenda",
      ato: 2,
      papel: "complication",
      resumo: "A tensão entre Kael e Isa explode quando estratégias opostas colocam o grupo em risco. Durante uma operação para capturar um infiltrado de Corvus, os dois cooperam de forma instável e abrem uma microfenda no Éter. O alvo é neutralizado, mas o incidente revela que a ressonância entre eles também os expõe.",
      objetivo_pov: "Cooperar apesar do conflito e impedir sabotagem dentro de Helvar.",
      mudanca: "Vitória tática com custo alto: a dupla prova potencial e vulnerabilidade ao mesmo tempo.",
    },
    13: {
      titulo: "Ressonância",
      ato: 2,
      papel: "midpoint",
      resumo: "As consequências da microfenda se espalham por Helvar com falhas em acordos menores e pânico coletivo. Kael, guiado por Aetheris, entende que ele e Isa carregam uma assinatura conjunta rara; Isa recebe visões fragmentadas ligadas a Elara, sem contato direto com entidades. À distância, forças de Umbrael começam a convergir.",
      objetivo_pov: "Entender a natureza da ressonância sem perder o controle.",
      mudanca: "O conflito muda de escala: os irmãos deixam de ser só alvos e viram epicentro do desequilíbrio.",
    },
    14: {
      titulo: "Cerco",
      ato: 2,
      papel: "crisis",
      resumo: "Helvar é cercada por Umbrins e Eidólons em ondas coordenadas por um Arauto. Kael atua como sensor de rupturas no Éter e Isa sustenta defesa com lampejos instintivos de Luz/Sombra, sempre com alto desgaste. O portão principal cede e a defesa entra em colapso parcial.",
      objetivo_pov: "Manter Helvar de pé tempo suficiente para evacuar civis.",
      mudanca: "A defesa falha parcialmente e empurra os irmãos para um confronto limite.",
    },
    15: {
      titulo: "Acorde Imperfeito",
      ato: 2,
      papel: "climax",
      resumo: "No pátio em ruínas, Kael e Isa tentam sincronizar poderes pela primeira vez em um acordo bruto e incompleto. O impacto não derrota o Arauto, mas força seu recuo e quebra a investida imediata. Kael colapsa por exaustão extrema e Isa sofre sequela sensorial temporária.",
      objetivo_pov: "Sobreviver ao confronto sem ultrapassar o limite fatal.",
      mudanca: "Vitória local e cara; a ameaça maior permanece ativa e mais perigosa.",
    },
    16: {
      titulo: "Convalescença",
      ato: 3,
      papel: "rising_action",
      resumo: "Após o confronto, Kael e Isa se recuperam lentamente enquanto Helvar conta perdas. Isa percebe ecos sombrios como intuição sensorial, sem diálogo direto com Umbryth. Kael recebe de Aetheris apenas orientação de foco e economia de energia.",
      objetivo_pov: "Recuperar-se sem perder a capacidade de agir no próximo movimento.",
      mudanca: "A dupla entende que custo e ritmo serão tão decisivos quanto poder bruto.",
    },
    17: {
      titulo: "Legado",
      ato: 3,
      papel: "rising_action",
      resumo: "Thorne revela parte do passado de Elara e entrega materiais que recontextualizam a pesquisa da Semente. Kael executa seu primeiro uso controlado de baixo custo; Isa decifra que o conflito envolve escolhas de vontade, não apenas força. O grupo redefine prioridades para sair do modo reativo.",
      objetivo_pov: "Transformar dor e culpa em método.",
      mudanca: "Os irmãos passam de sobrevivência imediata para estratégia consciente.",
    },
    18: {
      titulo: "Partida",
      ato: 3,
      papel: "rising_action",
      resumo: "Com Helvar vulnerável e politicamente dividida, o conselho decide dispersar os Portadores para evitar novo cerco direto. Kael, Isa e Thorne deixam a fortaleza por rota discreta, com Lyra permanecendo para conter a crise interna. Corvus mantém vigilância, sem confronto aberto.",
      objetivo_pov: "Sair vivos de Helvar sem expor aliados.",
      mudanca: "A história troca fortaleza por estrada e consolida os irmãos como caçados em movimento.",
    },
    19: {
      titulo: "Sussurros na Estrada",
      ato: 3,
      papel: "rising_action",
      resumo: "Na travessia, Thorne ensina técnicas para reduzir assinatura no Éter e evitar rastreamento. Isa tem visões fragmentadas sobre Corvus e o passado do conflito, ainda sem respostas completas. O grupo identifica que está sendo seguido por padrão inteligente, não por acaso.",
      objetivo_pov: "Manter discrição e reunir informação útil sem combate aberto.",
      mudanca: "A perseguição se confirma e o antagonista ganha profundidade estratégica.",
    },
    20: {
      titulo: "Limiar do Véu",
      ato: 3,
      papel: "rising_action",
      resumo: "O grupo chega ao Véu, um refúgio de acordos finos e instáveis, usado para estudo e recuperação. Em rara pausa, Kael e Isa alinham medos e limites: continuar juntos, sem prometer domínio imediato. Um fragmento sombrio projeta pista breve sobre o próximo risco ao norte.",
      objetivo_pov: "Escolher direção comum sem repetir erros de impulsividade.",
      mudanca: "Encerram o ciclo de fuga cega e assumem rumo deliberado para o próximo arco.",
    },
    21: {
      titulo: "Ancoragem",
      ato: 3,
      papel: "rising_action",
      resumo: "No Véu, Kael estabiliza ancoragens curtas de Éter e Isa aprende a separar percepção de pânico durante ecos de Luz/Sombra. Um eco histórico mostra que acordes prematuros destroem portadores, reforçando prudência. O treinamento prioriza sobrevivência e consistência.",
      objetivo_pov: "Ganhar controle funcional sem acelerar além do seguro.",
      mudanca: "Eles substituem explosão de poder por técnica repetível.",
    },
    22: {
      titulo: "Ofício",
      ato: 3,
      papel: "rising_action",
      resumo: "Kael aplica seu passado de ferreiro para recalibrar um diapasão etéreo de baixa escala, unindo habilidade manual e fluxo. Isa encontra notas de Elara sobre cura de acordos fraturados, não sobre dominação de força. A dupla enxerga um caminho de reconstrução, não só de guerra.",
      objetivo_pov: "Converter legado pessoal em ferramenta prática.",
      mudanca: "O objetivo maior ganha direção ética: reparar em vez de apenas destruir.",
    },
    23: {
      titulo: "Rastro Cansado",
      ato: 3,
      papel: "complication",
      resumo: "Uma zona de deterioração etérea próxima do Véu obriga a primeira operação conjunta pós-recuperação. Kael e Isa contêm uma distorção local com coordenação melhor, mas no limite físico. O rastro aponta para avanço de Corvus rumo ao norte.",
      objetivo_pov: "Provar que conseguem agir em campo sem romper novamente.",
      mudanca: "Primeiro sucesso estável em dupla, com ameaça macro ainda crescendo.",
    },
    24: {
      titulo: "Oferenda",
      ato: 3,
      papel: "resolution",
      resumo: "Thorne, debilitado, realiza um ritual parcial de transmissão de memória técnica — orientação, não power-up massivo. Ele não resolve tudo: entrega apenas o suficiente para manter os irmãos vivos e lúcidos na próxima etapa. O mentor encerra o arco como guia limitado, não como solução total.",
      objetivo_pov: "Receber legado útil sem pular etapas de maturação.",
      mudanca: "Kael e Isa ganham autonomia real, mantendo desafios abertos para o Livro 2.",
    },
    25: {
      titulo: "Pergunta em Marcha",
      ato: 3,
      papel: "resolution",
      resumo: "No fechamento do Livro 1, os irmãos deixam o Véu com propósito comum e limites claros de poder. Sem voz direta de entidades sombrias, Isa segue apenas com ecos ambíguos de percepção; Kael mantém vínculo inicial com Aetheris sob custo controlado. O livro termina com vitória local, perdas reais e um gancho para o norte, sem revelar respostas finais.",
      objetivo_pov: "Partir unidos para o próximo ciclo sem ilusões de domínio pleno.",
      mudanca: "Conclusão emocional e estratégica de Livro 1, com conflito da série intacto.",
    },
  };
  const CANON_PATCH_BOOK1 = { ...CANON_PATCH_1_5, ...CANON_PATCH_6_10, ...CANON_PATCH_11_25 };

  function cfg(k, v) { setConfig(p => ({...p, [k]: v})); }

  function applyCanonPatch6to10() {
    setOutline(prev => {
      let changed = false;
      const next = prev.map(ch => {
        const patch = CANON_PATCH_6_10[ch.id];
        if (!patch) return ch;
        const merged = { ...ch, ...patch };
        if (
          merged.titulo !== ch.titulo ||
          merged.resumo !== ch.resumo ||
          merged.objetivo_pov !== ch.objetivo_pov ||
          merged.mudanca !== ch.mudanca ||
          merged.ato !== ch.ato ||
          merged.papel !== ch.papel
        ) changed = true;
        return merged;
      });
      return changed ? next : prev;
    });
    setOutlinePlanWarnings(prev => {
      const next = { ...prev };
      [6,7,8,9,10].forEach(id => { delete next[id]; });
      return next;
    });
    setError("Correção canônica aplicada aos capítulos 6–10.");
  }

  function applyCanonPatch11to25() {
    setOutline(prev => {
      let changed = false;
      const next = prev.map(ch => {
        const patch = CANON_PATCH_11_25[ch.id];
        if (!patch) return ch;
        const merged = { ...ch, ...patch };
        if (
          merged.titulo !== ch.titulo ||
          merged.resumo !== ch.resumo ||
          merged.objetivo_pov !== ch.objetivo_pov ||
          merged.mudanca !== ch.mudanca ||
          merged.ato !== ch.ato ||
          merged.papel !== ch.papel
        ) changed = true;
        return merged;
      });
      return changed ? next : prev;
    });
    setOutlinePlanWarnings(prev => {
      const next = { ...prev };
      for (let id=11; id<=25; id++) delete next[id];
      return next;
    });
    setError("Correção canônica aplicada aos capítulos 11–25.");
  }

  function applyCanonPatchBook1(opts = {}) {
    const { silent = false } = opts;
    const canonIds = Array.from({ length: 25 }, (_, i) => i + 1);
    setOutline(prev => {
      const prevById = Object.fromEntries(prev.map(ch => [ch.id, ch]));
      const next = canonIds.map(id => {
        const patch = CANON_PATCH_BOOK1[id] || {};
        const current = prevById[id] || {};
        return {
          ...current,
          ...patch,
          id,
        };
      });
      const unchanged =
        prev.length === next.length &&
        next.every((ch, idx) => {
          const old = prev[idx];
          return !!old &&
            old.id === ch.id &&
            old.titulo === ch.titulo &&
            old.resumo === ch.resumo &&
            old.ato === ch.ato &&
            old.papel === ch.papel &&
            old.objetivo_pov === ch.objetivo_pov &&
            old.mudanca === ch.mudanca;
        });
      return unchanged ? prev : next;
    });
    setOutlineNextToGenerate(25);
    setOutlinePlanWarnings(prev => {
      const next = { ...prev };
      for (let id = 1; id <= 25; id++) delete next[id];
      return next;
    });
    if (!silent) setError("Roteiro do Livro 1 alinhado ao cânone (capítulos 1–25).");
  }

  // Save session to localStorage + Supabase whenever key state changes
  useEffect(() => {
    if (!currentProjectId) return; // Don't save when on home page
    // Skip save on the very first render after openProject (state is being loaded, not changed)
    if (openingProjectRef.current) { openingProjectRef.current = false; return; }
    const sessionData = {
      phase, ideaInput, characters, config, currentBookIdx, canonicalSummaries,
      bibliaChat, bibliaPanel, bibliaLocked, bibliaExpanded, bibliaReviewed, chatMessages, outline, chapters, scenes,
      chapterSummaries, continuityWarnings, outlineAdherence, nextChapterToWrite, writingBatchSize,
      outlineNextToGenerate, outlineBatchSize, outlinePlanWarnings, outlineAuthorNotes,
    };
    // Update current project in the projects list
    const updatedProjects = allProjectsRef.current.map(p =>
      p.id === currentProjectId
        ? { ...p, title: bibliaPanel.title || p.title || "Sem título", genre: bibliaPanel.genre || p.genre || "", updatedAt: new Date().toISOString(), data: sessionData }
        : p
    );
    allProjectsRef.current = updatedProjects;
    try { localStorage.setItem("autoria_v1", JSON.stringify({ projects: updatedProjects })); } catch {}
    scheduleSyncToSupabase({ projects: updatedProjects });
  }, [phase, ideaInput, characters, config, currentBookIdx, canonicalSummaries, // eslint-disable-line
      bibliaChat, bibliaPanel, bibliaLocked, bibliaExpanded, bibliaReviewed, chatMessages, outline, chapters, scenes,
      chapterSummaries, continuityWarnings, outlineAdherence, nextChapterToWrite, writingBatchSize,
      outlineNextToGenerate, outlineBatchSize, outlinePlanWarnings, outlineAuthorNotes, currentProjectId]);

  useEffect(() => { allProjectsRef.current = allProjects; }, [allProjects]);

  // Keep userRef always current (needed inside beforeunload event handler)
  useEffect(() => { userRef.current = user; }, [user]);

  // One-shot auto-fix for the known duplicated/generic block in chapters 6-10.
  useEffect(() => {
    if (outline.length < 10 || currentBookIdx !== 1) return;
    const byId = Object.fromEntries(outline.map(ch => [ch.id, ch]));
    if (![6,7,8,9,10].every(id => byId[id])) return;
    const looksBroken = [7,8,9,10].some(id => {
      const t = String(byId[id]?.titulo || "").trim();
      const r = String(byId[id]?.resumo || "").trim();
      return /^rastros$/i.test(t) || new RegExp(`^Cap[íi]tulo\\s*${id}$`, "i").test(t) || /Após o ataque a Valdora/i.test(r);
    });
    if (looksBroken) applyCanonPatch6to10();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outline, currentBookIdx]);

  // One-shot auto-fix for late-book leakage (Book 2/3 content inside Book 1 endgame).
  useEffect(() => {
    if (outline.length < 25 || currentBookIdx !== 1) return;
    const byId = Object.fromEntries(outline.map(ch => [ch.id, ch]));
    if (![16,20,24,25].every(id => byId[id])) return;
    const leakText = [16,20,24,25]
      .map(id => `${byId[id]?.titulo || ""}\n${byId[id]?.resumo || ""}\n${byId[id]?.mudanca || ""}`)
      .join("\n\n");
    const looksLeaky =
      /Umbryth\s+sussurra/i.test(leakText) ||
      /Terras G[ée]lidas de Nihil/i.test(leakText) ||
      /transferindo o conhecimento puro de suas d[ée]cadas/i.test(leakText);
    if (looksLeaky) applyCanonPatch11to25();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outline, currentBookIdx]);

  // Auto-align already generated scene plans to Book 1 canon when opening Scenes.
  useEffect(() => {
    if (phase !== "scenes" || currentBookIdx !== 1 || !outline.length) return;
    if (!Object.keys(scenes || {}).length) return;
    const { next, changed } = buildAlignedScenesMap(scenes);
    if (changed) {
      setScenes(next);
      setError("Cenas existentes foram alinhadas automaticamente ao roteiro canônico.");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, currentBookIdx, outline, scenes]);

  useEffect(() => {
    if (phase !== "scenes" || currentBookIdx !== 1 || !outline.length) return;
    if (!Object.keys(scenes || {}).length) return;
    if (generatingScenes) return;
    const hasFallback = collectFallbackSceneChapterIds(scenes).length > 0;
    if (!hasFallback) return;
    if (sceneAutoRepairRanRef.current) return;
    sceneAutoRepairRanRef.current = true;
    repairFallbackScenesBook1({ silent: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, currentBookIdx, outline, scenes, generatingScenes]);

  // Style/canon cleanup for already generated chapters.
  useEffect(() => {
    if (!chapters.length) return;
    let changed = false;
    const next = chapters.map(ch => {
      if (!ch?.content) return ch;
      const cleaned = sanitizeProseStyle(ch.content);
      const cleanedTitle = String(ch?.title || "")
        .replace(/\bElyra\b/gi, "Elara")
        .replace(/\bAlthea\b/gi, "Elara");
      if (cleaned === ch.content && cleanedTitle === ch.title) return ch;
      changed = true;
      return { ...ch, title: cleanedTitle, content: cleaned };
    });
    if (changed) setChapters(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapters]);

  // One-shot automatic canonical cleanup for Book 1 when opening writing/done.
  useEffect(() => {
    if (currentBookIdx !== 1) return;
    if (!(phase === "writing" || phase === "done")) return;
    if (!outline.length && !chapters.length) return;
    if (book1AutoCanonRanRef.current) return;
    book1AutoCanonRanRef.current = true;
    applyCanonPatchBook1({ silent: true });
    applySceneCanonBook1({ silent: true });
    const changedCount = applyChapterCanonBook1Pass();
    if (changedCount > 0) {
      setError(`Ajuste automático do Livro 1 aplicado (${changedCount} capítulo(s) normalizados).`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, currentBookIdx, outline, chapters]);

  // One-shot auto-fix for broad Book 1 drift: enforce canonical 1-25 skeleton.
  useEffect(() => {
    if (outline.length < 25 || currentBookIdx !== 1) return;
    const byId = Object.fromEntries(outline.map(ch => [ch.id, ch]));
    if (![1,2,3,4,5,6,10,15,20,25].every(id => byId[id])) return;
    const titleMismatch = [1,2,3,4,5,10,15,25].some(id => {
      const currentTitle = String(byId[id]?.titulo || "").trim().toLowerCase();
      const canonTitle = String(CANON_PATCH_BOOK1[id]?.titulo || "").trim().toLowerCase();
      return !currentTitle || currentTitle !== canonTitle;
    });
    const driftText = [12,13,14,15,16,20,24,25]
      .map(id => `${byId[id]?.titulo || ""}\n${byId[id]?.resumo || ""}\n${byId[id]?.mudanca || ""}`)
      .join("\n\n");
    const loreLeak =
      /Umbryth\s+sussurra/i.test(driftText) ||
      /Terras G[ée]lidas de Nihil/i.test(driftText) ||
      /transferindo o conhecimento puro de suas d[ée]cadas/i.test(driftText);
    if (titleMismatch || loreLeak) applyCanonPatchBook1();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outline, currentBookIdx]);

  // ── FLUSH SAVE BEFORE TAB CLOSES ────────────────────────────────────────────
  useEffect(() => {
    const handleUnload = () => {
      const u = userRef.current;
      const data = allProjectsRef.current;
      if (!u || !data || !data.length) return;
      // Cancel pending debounced save — we'll fire immediately instead
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
      // fetch with keepalive: browser guarantees this request is sent even as the page closes
      try {
        fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/sessions?on_conflict=user_id`,
          {
            method: "POST",
            keepalive: true,
            headers: {
              "Content-Type": "application/json",
              "apikey": process.env.REACT_APP_SUPABASE_ANON_KEY,
              "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
              "Prefer": "resolution=merge-duplicates",
            },
            body: JSON.stringify({
              user_id: u.id,
              data: { projects: data },
              updated_at: new Date().toISOString(),
            }),
          }
        );
      } catch {}
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []); // eslint-disable-line

  useEffect(() => { bibliaLockedRef.current = bibliaLocked; }, [bibliaLocked]);

  useEffect(() => {
    if (!bibliaChat.length) return;
    const last = bibliaChat[bibliaChat.length - 1];
    if (last.role === "ai" && bibliaChatLastMsgRef.current) {
      // Scroll to the START of the last AI message so user reads from beginning
      bibliaChatLastMsgRef.current.scrollIntoView({behavior:"smooth", block:"start"});
    } else if (bibliaChatEndRef.current) {
      // For user messages scroll to bottom (shows loading indicator)
      bibliaChatEndRef.current.scrollIntoView({behavior:"smooth"});
    }
  }, [bibliaChat]);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({behavior:"smooth"});
  }, [chatMessages]);

  // Initialize config genre/tone/pov once GENRES loads
  useEffect(() => {
    if (GENRES.length && !config.genre) {
      setConfig(p => ({...p, genre: GENRES[0], tone: TONES[0], pov: POV[1] || POV[0]}));
    }
  }, [GENRES, TONES, POV]); // eslint-disable-line

  // Persistir indicadores de campos atualizados no sessionStorage (sobrevive a reloads na mesma aba)
  useEffect(() => {
    if (!currentProjectId) return;
    const key = `autoria_upd_${currentProjectId}`;
    if (Object.keys(bibliaUpdatedKeys).length > 0) {
      sessionStorage.setItem(key, JSON.stringify(bibliaUpdatedKeys));
    }
  }, [bibliaUpdatedKeys, currentProjectId]);

  // Ao abrir o leitor, inicializar detecção de seção visível sem precisar de scroll
  useEffect(() => {
    if (!showBookReader) return;
    const timer = setTimeout(() => {
      const outer = document.getElementById('reader-scroll-outer');
      if (outer) outer.dispatchEvent(new Event('scroll'));
    }, 120);
    return () => clearTimeout(timer);
  }, [showBookReader]); // eslint-disable-line

  // Onboarding: start tour after first AI response
  useEffect(() => {
    if (bibliaChat.length >= 2 && onboardingStep === 0) {
      setTimeout(() => setOnboardingStep(1), 800);
    }
  }, [bibliaChat.length]); // eslint-disable-line

  // ── SUPABASE AUTH ────────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) loadFromSupabase(session.user.id);
      else setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user ?? null;
      setUser(u);
      if (u) loadFromSupabase(u.id);
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line

  async function loadFromSupabase(userId) {
    if (isLoadingSupabaseRef.current) return; // prevent double call
    isLoadingSupabaseRef.current = true;
    try {
      const { data, error } = await supabase
        .from("sessions")
        .select("data")
        .eq("user_id", userId)
        .single();
      if (!error && data?.data && Object.keys(data.data).length > 0) {
        const raw = data.data;
        let projects = [];
        if (Array.isArray(raw.projects)) {
          // New format
          projects = raw.projects;
        } else if (raw.phase !== undefined) {
          // Old format — migrate to new
          const migratedProject = {
            id: Date.now().toString(36) + Math.random().toString(36).slice(2),
            title: raw.bibliaPanel?.title || raw.config?.title || "Meu Livro",
            genre: raw.bibliaPanel?.genre || raw.config?.genre || "",
            updatedAt: new Date().toISOString(),
            data: raw,
          };
          projects = [migratedProject];
          // Save migrated format back
          await supabase.from("sessions").upsert({
            user_id: userId, data: { projects }, updated_at: new Date().toISOString()
          }, { onConflict: "user_id" });
          localStorage.setItem("autoria_v1", JSON.stringify({ projects }));
        }
        setAllProjects(projects);
        allProjectsRef.current = projects;
      } else {
        // No cloud data — check localStorage
        const local = JSON.parse(localStorage.getItem("autoria_v1") || "{}");
        if (local.projects) {
          setAllProjects(local.projects);
          allProjectsRef.current = local.projects;
        } else if (local.phase !== undefined) {
          // Old local format — migrate
          const migratedProject = {
            id: Date.now().toString(36) + Math.random().toString(36).slice(2),
            title: local.bibliaPanel?.title || local.config?.title || "Meu Livro",
            genre: local.bibliaPanel?.genre || local.config?.genre || "",
            updatedAt: new Date().toISOString(),
            data: local,
          };
          const projects = [migratedProject];
          setAllProjects(projects);
          allProjectsRef.current = projects;
          localStorage.setItem("autoria_v1", JSON.stringify({ projects }));
          await supabase.from("sessions").upsert({
            user_id: userId, data: { projects }, updated_at: new Date().toISOString()
          }, { onConflict: "user_id" });
        }
      }
    } catch {}
    isLoadingSupabaseRef.current = false;
    setAuthLoading(false);
  }

  function scheduleSyncToSupabase(fullData) {
    if (!user) return;
    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    setSaveStatus("saving");
    const requestId = ++saveRequestIdRef.current;
    syncTimeoutRef.current = setTimeout(async () => {
      try {
        await supabase.from("sessions").upsert({
          user_id: user.id, data: fullData, updated_at: new Date().toISOString()
        }, { onConflict: "user_id" });
        if (requestId === saveRequestIdRef.current) {
          setSaveStatus("saved");
          setTimeout(() => setSaveStatus(s => s === "saved" ? "idle" : s), 2500);
        }
      } catch {
        if (requestId === saveRequestIdRef.current) setSaveStatus("error");
      }
    }, 2000);
  }

  async function signOut() {
    await supabase.auth.signOut();
    setUser(null);
  }

  function generateId() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2);
  }

  function createProject(form) {
    const id = generateId();
    const initialConfig = {
      title: form?.title || "",
      subtitle: "",
      author: form?.author || "",
      genre: "",
      tone: "",
      pov: "",
      numChapters: form?.numChapters || 10,
      synopsis: "",
      writingStyle: "",
      setting: "",
      protagonist: "",
      antagonist: "",
      numBooks: form?.numBooks || 1,
      bookTitles: form?.bookTitles || [],
      coverUrl: form?.coverUrl || "",
    };
    const newProject = {
      id,
      title: form?.title || "Novo Projeto",
      genre: "",
      updatedAt: new Date().toISOString(),
      data: { config: initialConfig },
    };
    const updatedProjects = [...allProjectsRef.current, newProject];
    setAllProjects(updatedProjects);
    allProjectsRef.current = updatedProjects;
    setPhase("biblia");
    setIdeaInput("");
    setCharacters([]);
    setConfig(initialConfig);
    setCurrentBookIdx(1);
    setCanonicalSummaries([]);
    setbibliaChat([]);
    setbibliaPanel({...emptyPanel});
    setbibliaLocked({});
    setbibliaExpanded({});
    setbibliaReviewed(false);
    setChatMessages([]);
    setOutline([]);
    setOutlineNextToGenerate(0);
    setOutlineBatchSize(5);
    setOutlinePlanWarnings({});
    setOutlineAuthorNotes("");
    setChapters([]);
    setNextChapterToWrite(0);
    setWritingBatchSize(3);
    setScenes({});
    setChapterSummaries([]);
    setContinuityWarnings({});
    setCurrentProjectId(id);
  }

  function editProject(projectId, form) {
    const updatedProjects = allProjectsRef.current.map(p => {
      if (p.id !== projectId) return p;
      const oldConfig = p.data?.config || {};
      const newConfig = {
        ...oldConfig,
        title: form.title,
        author: form.author,
        numChapters: form.numChapters,
        numBooks: form.numBooks,
        bookTitles: form.bookTitles,
        coverUrl: form.coverUrl || "",
      };
      return { ...p, title: form.title, updatedAt: new Date().toISOString(), data: { ...p.data, config: newConfig } };
    });
    setAllProjects(updatedProjects);
    allProjectsRef.current = updatedProjects;
    // If currently editing this project, update config state too
    if (currentProjectId === projectId) {
      setConfig(c => ({ ...c, title: form.title, author: form.author, numChapters: form.numChapters, numBooks: form.numBooks, bookTitles: form.bookTitles, coverUrl: form.coverUrl || "" }));
    }
    // Persist
    try { localStorage.setItem("autoria_v1", JSON.stringify({ projects: updatedProjects })); } catch {}
    scheduleSyncToSupabase({ projects: updatedProjects });
  }

  function openProject(project) {
    openingProjectRef.current = true; // skip first save (loading state, not a real change)
    const s = project.data || {};
    const restoredOutline = s.outline || [];
    setPhase(s.phase || "biblia");
    setIdeaInput(s.ideaInput || "");
    setCharacters(s.characters || []);
    if (s.config) setConfig(s.config);
    setCurrentBookIdx(s.currentBookIdx || 1);
    setCanonicalSummaries(s.canonicalSummaries || []);
    setbibliaChat(s.bibliaChat || []);
    setbibliaPanel(s.bibliaPanel || {...emptyPanel});
    setbibliaLocked(s.bibliaLocked || {});
    setbibliaExpanded(s.bibliaExpanded || {});
    setbibliaReviewed(s.bibliaReviewed || false);
    setChatMessages(s.chatMessages || []);
    setOutline(restoredOutline);
    setOutlineNextToGenerate(
      typeof s.outlineNextToGenerate === "number" ? s.outlineNextToGenerate : restoredOutline.length
    );
    setOutlineBatchSize(s.outlineBatchSize || 5);
    setOutlinePlanWarnings(s.outlinePlanWarnings || {});
    setOutlineAuthorNotes(s.outlineAuthorNotes || "");
    setChapters(s.chapters || []);
    setScenes(s.scenes || {});
    setChapterSummaries(s.chapterSummaries || []);
    setContinuityWarnings(s.continuityWarnings || {});
    setOutlineAdherence(s.outlineAdherence || {});
    setNextChapterToWrite(s.nextChapterToWrite || 0);
    setWritingBatchSize(s.writingBatchSize || 3);
    setCurrentProjectId(project.id);
    // Restaurar indicadores de atualização da sessão anterior (mesma aba)
    try {
      const upd = JSON.parse(sessionStorage.getItem(`autoria_upd_${project.id}`) || '{}');
      if (Object.keys(upd).length > 0) setBibliaUpdatedKeys(upd);
    } catch {}
  }

  function goToHome() {
    setAllProjects([...allProjectsRef.current]);
    setCurrentProjectId(null);
  }

  function buildBible(cfg, chars, prevSummaries = []) {
    const cb = chars.length > 0
      ? `\nCHARACTERS:\n${chars.map(c => [
          `- ${c.nome||c.name} (${c.papel||c.role})`,
          c.personalidade||c.personality ? `  Personality: ${c.personalidade||c.personality}` : "",
          c.motivacao||c.motivation ? `  Motivation: ${c.motivacao||c.motivation}` : "",
          c.ferida ? `  Emotional Wound: ${c.ferida}` : "",
          c.desejo ? `  Desire (wants): ${c.desejo}` : "",
          c.necessidade ? `  Need (truly needs): ${c.necessidade}` : "",
          c.arco||c.arc ? `  Arc: ${c.arco||c.arc}` : "",
          c.voz ? `  Voice/Speech: ${c.voz}` : "",
          c.segredo||c.secret ? `  Secret: ${c.segredo||c.secret}` : "",
        ].filter(Boolean).join("\n")).join("\n\n")}`
      : "";
    const panelCtx = [
      bibliaPanel.worldRules        && `WORLD RULES: ${bibliaPanel.worldRules}`,
      bibliaPanel.factions          && `FACTIONS: ${bibliaPanel.factions}`,
      bibliaPanel.significantItems  && `SIGNIFICANT ITEMS: ${bibliaPanel.significantItems}`,
      bibliaPanel.worldHistory      && `WORLD HISTORY: ${bibliaPanel.worldHistory}`,
      bibliaPanel.sensoryAtmosphere && `SENSORY ATMOSPHERE: ${bibliaPanel.sensoryAtmosphere}`,
      bibliaPanel.themes            && `THEMES: ${bibliaPanel.themes}`,
      bibliaPanel.subplots          && `SUBPLOTS: ${bibliaPanel.subplots}`,
      bibliaPanel.plotPoint1        && `PLOT POINT 1: ${bibliaPanel.plotPoint1}`,
      bibliaPanel.midpoint          && `MIDPOINT: ${bibliaPanel.midpoint}`,
      bibliaPanel.plotPoint2        && `PLOT POINT 2: ${bibliaPanel.plotPoint2}`,
      bibliaPanel.climax            && `CLIMAX: ${bibliaPanel.climax}`,
      bibliaPanel.resolution        && `RESOLUTION: ${bibliaPanel.resolution}`,
      bibliaPanel.referenceAuthors  && `REFERENCE AUTHORS: ${bibliaPanel.referenceAuthors}`,
      bibliaPanel.prosePace         && `PROSE PACE: ${bibliaPanel.prosePace}`,
      bibliaPanel.stylisticAvoid    && `AVOID: ${bibliaPanel.stylisticAvoid}`,
    ].filter(Boolean).join("\n");
    const prevCtx = prevSummaries.length > 0
      ? `\n\n${T.prev_books_ctx(prevSummaries.length)}\n${prevSummaries.map((s,i) => `${T.book_label(i+1)}:\n${s}`).join("\n\n")}`
      : "";
    return `${aiLang} You are a professional literary writer with mastery of character, voice, and narrative structure. Write ONLY the requested narrative content — no meta-commentary, no chapter labels unless instructed.\n\n═══ BOOK BIBLE ═══\nTITLE: ${cfg.title}\nGENRE: ${cfg.genre}\nTONE: ${cfg.tone}\nPOINT OF VIEW: ${cfg.pov}\nSETTING: ${cfg.setting}\nSYNOPSIS: ${cfg.synopsis}\n${cfg.writingStyle ? `STYLE: ${cfg.writingStyle}` : ""}${cb}\n${panelCtx}${prevCtx}\n═════════════════\nRULES:\n- Never contradict previous events or established facts.\n- Never alter established characters, their voices, or their emotional states without narrative justification.\n- Maintain consistent point of view, tense, and tone throughout.\n- Every scene must serve character arc OR plot advancement — preferably both.\n- Show emotion through action and dialogue, not narration telling us how characters feel.\n- Honour the themes, subplots, and structural markers defined above.`;
  }

  // ── BÍBLIA: helpers ──────────────────────────────────────────────────────────
  const BIBLE_SCHEMA = `{"titulo":"","genero":"","tom":"","sinopse":"","protagonista":"","ferida_protagonista":"","desejo_protagonista":"","necessidade_protagonista":"","voz_protagonista":"","arco_protagonista":"","antagonista":"","motivo_antagonista":"","arco_antagonista":"","personagens_apoio":"","cenario":"","regras_mundo":"","locais":"","faccoes":"","itens_significativos":"","historia_mundo":"","atmosfera_sensorial":"","conflito":"","stakes":"","incidente":"","temas":"","pov":"","subtramas":"","plot_point_1":"","ponto_medio":"","plot_point_2":"","climax":"","resolucao":"","estilo":"","autores_referencia":"","influencias":"","ritmo_prosa":"","evitar":"","arco_serie":"","resolucao_livro":"","ganchos_proximo":""}`;

  // Render AI chat message with rich formatting
  function renderMsg(text) {
    const qSep = text.indexOf("---QUESTIONS---");
    const body  = (qSep === -1 ? text : text.slice(0, qSep)).trim();
    const qPart = (qSep === -1 ? "" : text.slice(qSep + 15)).trim();

    // ── inline formatting ──────────────────────────────────────────────────────
    function esc(s) {
      return s.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
    }
    function inline(raw) {
      return esc(raw)
        // bold-italic ***text***
        .replace(/\*\*\*(.+?)\*\*\*/g,"<strong><em style='color:#f2d8a8'>$1</em></strong>")
        // bold **text** — if it's a label ending in : render as gold label
        .replace(/\*\*([^*]+?):\*\*/g,"<strong style='color:#b8892a;font-weight:800'>$1:</strong>")
        .replace(/\*\*(.+?)\*\*/g,"<strong style='color:#f3eee4;font-weight:700'>$1</strong>")
        // italic *text*
        .replace(/\*([^*]+?)\*/g,"<em style='color:#e9c176;font-style:italic'>$1</em>")
        // inline code `text`
        .replace(/`([^`]+)`/g,"<code style='background:#f0e8d8;padding:1px 5px;border-radius:3px;font-size:.88em;color:#7a4000'>$1</code>")
        // arrow CTA → text
        .replace(/→\s*/g,"<span style='color:#b8892a;font-weight:700'>→ </span>");
    }

    // ── tokenise lines into typed blocks ──────────────────────────────────────
    const lines = body.split("\n");
    const blocks = [];
    let i = 0;

    // Bible field keys — used to detect JSON that should be shown as badge
    // Must cover all key names the AI may use (camelCase and snake_case variants)
    const BIBLE_KEYS = [
      "titulo","titulo_do_livro","genero","genero_literario","sinopse","protagonista","antagonista",
      "ferida_protagonista","ferida_emocional","regras_mundo","worldRules","plot_point_1","plotPoint1",
      "climax","resolucao","resolution","temas","themes","incidente_incitante","incitingIncident",
      "protagonistWound","antagonistMotive","cenario","setting","voz_narrativa","narrativeVoice",
    ];

    while (i < lines.length) {
      const raw = lines[i];
      const t   = raw.trim();

      // blank line
      if (!t) { blocks.push({ type:"spacer" }); i++; continue; }

      // fenced code block  ```lang ... ```
      if (t.startsWith("```")) {
        const lang = t.slice(3).trim().toLowerCase();
        const codeLines = [];
        i++;
        while (i < lines.length && !lines[i].trim().startsWith("```")) {
          codeLines.push(lines[i]); i++;
        }
        i++; // consume closing ```
        const codeText = codeLines.join("\n");
        // Detect bible JSON: contains multiple bible keys → show as applied badge, not raw JSON
        const bibleLikeCount = BIBLE_KEYS.filter(k => codeText.includes(`"${k}"`)).length;
        if ((lang === "json" || lang === "") && bibleLikeCount >= 3) {
          const filled = codeText.match(/"[^"]+"\s*:\s*"[^"]{4,}"/g);
          blocks.push({ type:"biblebadge", count: filled ? filled.length : 0 });
        } else {
          blocks.push({ type:"code", lang, content: codeText });
        }
        continue;
      }

      // heading ### ## #
      const hm = t.match(/^(#{1,3})\s+(.+)$/);
      if (hm) { blocks.push({ type:"heading", level: hm[1].length, content: hm[2] }); i++; continue; }

      // horizontal rule ---
      if (/^---+$/.test(t)) { blocks.push({ type:"hr" }); i++; continue; }

      // emoji heading — starts with emoji, short, no period
      const emojiH = t.match(/^([\u{1F300}-\u{1FFFF}]|✅|🔧|📋|⚠️|💡|🎯|🔴|🟡|🟢|📖|✦|✨|🪝|💛)/u);
      if (emojiH && t.length < 90 && !t.endsWith(".")) {
        blocks.push({ type:"emojititle", content: t }); i++; continue;
      }

      // bold-only line = section header
      const boldOnly = t.match(/^\*\*([^*]+)\*\*\s*:?\s*$/);
      if (boldOnly) { blocks.push({ type:"sectionhead", content: boldOnly[1].replace(/:$/,"") }); i++; continue; }

      // numbered list
      if (/^\d+\.\s/.test(t)) {
        const items = [];
        while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
          const m = lines[i].trim().match(/^(\d+)\.\s+([\s\S]+)$/);
          if (m) items.push({ num: m[1], text: m[2] });
          i++;
        }
        blocks.push({ type:"numlist", items }); continue;
      }

      // bullet list
      if (/^[-•*]\s/.test(t)) {
        const items = [];
        while (i < lines.length && /^[-•*]\s/.test(lines[i].trim())) {
          items.push(lines[i].trim().replace(/^[-•*]\s+/,""));
          i++;
        }
        blocks.push({ type:"bullets", items }); continue;
      }

      // regular paragraph
      const paraLines = [];
      while (i < lines.length) {
        const ln = lines[i].trim();
        if (!ln) break;
        if (/^#{1,3}\s/.test(ln)) break;
        if (/^---+$/.test(ln)) break;
        if (/^\d+\.\s/.test(ln)) break;
        if (/^[-•*]\s/.test(ln)) break;
        if (/^\*\*([^*]+)\*\*\s*:?\s*$/.test(ln)) break;
        if (ln.startsWith("```")) break;
        paraLines.push(ln); i++;
      }
      if (paraLines.length) blocks.push({ type:"para", content: paraLines.join(" ") });
    }

    // ── render blocks ──────────────────────────────────────────────────────────
    let bodyHtml = "";
    let prevType = null;

    for (const blk of blocks) {
      if (blk.type === "spacer") { /* gap handled by margin */ continue; }

      if (blk.type === "heading") {
        const sz  = blk.level === 1 ? "1.15rem" : blk.level === 2 ? "1.05rem" : ".95rem";
        const mt  = prevType && prevType !== "spacer" ? "22px" : "4px";
        bodyHtml += `<div style="margin:${mt} 0 8px;font-family:'Playfair Display',serif;font-size:${sz};font-weight:800;color:#f3eee4;border-bottom:${blk.level===1?"2px solid #d4a94a":"1px solid rgba(120,109,89,.45)"};padding-bottom:5px;">${inline(blk.content)}</div>`;
      }

      else if (blk.type === "sectionhead") {
        const mt = prevType && prevType !== "spacer" ? "18px" : "4px";
        bodyHtml += `<div style="margin:${mt} 0 6px;font-size:.8rem;font-weight:900;color:#b8892a;text-transform:uppercase;letter-spacing:.9px;">${esc(blk.content)}</div>`;
      }

      else if (blk.type === "emojititle") {
        const mt = prevType && prevType !== "spacer" ? "16px" : "4px";
        bodyHtml += `<div style="margin:${mt} 0 6px;font-size:.95rem;font-weight:700;color:#f3eee4;">${inline(blk.content)}</div>`;
      }

      else if (blk.type === "hr") {
        bodyHtml += `<hr style="border:none;border-top:1px solid #e0d0b0;margin:16px 0;">`;
      }

      else if (blk.type === "biblebadge") {
        bodyHtml += `<div style="display:inline-flex;align-items:center;gap:8px;background:#e8f5e9;border:1px solid #a5d6a7;border-left:3px solid #2e7d32;border-radius:6px;padding:8px 14px;margin:8px 0 14px;font-size:.85rem;color:#1b5e20;">
          <span style="font-size:1.1rem;">✅</span>
          <span><strong>${blk.count > 0 ? blk.count + " campos" : "Dados"}</strong> aplicados à Bíblia automaticamente</span>
        </div>`;
      }

      else if (blk.type === "code") {
        const label = blk.lang ? blk.lang.toUpperCase() : "CÓDIGO";
        bodyHtml += `<div style="margin:8px 0 14px;border-radius:6px;overflow:hidden;border:1px solid #ddd0b0;">
          <div style="background:#3a2e1e;padding:5px 12px;font-size:.65rem;font-weight:800;color:#b8892a;letter-spacing:1px;text-transform:uppercase;">${esc(label)}</div>
          <pre style="margin:0;padding:12px 14px;background:#1e1810;color:#e8d8b0;font-size:.82rem;line-height:1.65;overflow-x:auto;white-space:pre-wrap;word-break:break-word;">${esc(blk.content)}</pre>
        </div>`;
      }

      else if (blk.type === "numlist") {
        const mt = prevType && prevType !== "spacer" ? "6px" : "0";
        bodyHtml += `<div style="margin-top:${mt};margin-bottom:14px;display:flex;flex-direction:column;gap:7px;">`;
        for (const item of blk.items) {
          bodyHtml += `<div style="display:flex;gap:10px;align-items:baseline;">
            <span style="background:#b8892a;color:#fff;font-size:.6rem;font-weight:900;border-radius:50%;min-width:18px;height:18px;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:1px;">${item.num}</span>
            <span style="line-height:1.75;font-size:.95rem;">${inline(item.text)}</span>
          </div>`;
        }
        bodyHtml += `</div>`;
      }

      else if (blk.type === "bullets") {
        const mt = prevType && prevType !== "spacer" ? "6px" : "0";
        bodyHtml += `<div style="margin-top:${mt};margin-bottom:14px;display:flex;flex-direction:column;gap:6px;">`;
        for (const item of blk.items) {
          // detect bold label: **Label:** rest
          const labelMatch = item.match(/^\*\*(.+?):\*\*\s*(.*)$/);
          if (labelMatch) {
            bodyHtml += `<div style="display:flex;gap:8px;align-items:baseline;">
              <span style="color:#b8892a;font-size:.85rem;flex-shrink:0;margin-top:2px;">◆</span>
              <span style="line-height:1.75;font-size:.95rem;"><strong style="color:#b8892a;font-weight:800;">${esc(labelMatch[1])}:</strong> ${inline(labelMatch[2])}</span>
            </div>`;
          } else {
            bodyHtml += `<div style="display:flex;gap:8px;align-items:baseline;">
              <span style="color:#b8892a;font-size:.75rem;flex-shrink:0;margin-top:3px;">◆</span>
              <span style="line-height:1.75;font-size:.95rem;">${inline(item)}</span>
            </div>`;
          }
        }
        bodyHtml += `</div>`;
      }

      else if (blk.type === "para") {
        // detect if the whole paragraph IS a bold label line  **Label:** content
        const labelPara = blk.content.match(/^\*\*(.+?):\*\*\s+(.+)$/);
        if (labelPara) {
          bodyHtml += `<p style="margin:0 0 12px;line-height:1.8;font-size:.95rem;"><strong style="color:#b8892a;font-weight:800;">${esc(labelPara[1])}:</strong> ${inline(labelPara[2])}</p>`;
        } else {
          bodyHtml += `<p style="margin:0 0 14px;line-height:1.85;font-size:.95rem;">${inline(blk.content)}</p>`;
        }
      }

      prevType = blk.type;
    }

    // ── questions block ────────────────────────────────────────────────────────
    let qHtml = "";
    if (qPart) {
      const allLines = qPart.split(/\n/).map(l => l.trim()).filter(Boolean);
      const cta = allLines.find(l => l.startsWith("→"));
      const questions = allLines
        .filter(l => !l.startsWith("→"))
        .map(l => l.replace(/^[•*-]\s*/,"").trim())
        .filter(Boolean);
      if (questions.length) {
        qHtml = `<div style="margin-top:14px;border-radius:8px;overflow:hidden;border:1px solid rgba(212,169,74,.55);">
          <div style="background:#b8892a;padding:7px 14px;font-size:.68rem;font-weight:900;color:#fff;letter-spacing:1.2px;text-transform:uppercase;display:flex;align-items:center;gap:6px;"><span>✦</span> Continue desenvolvendo</div>
          <div style="background:#20201f;padding:12px 14px;">`
          + questions.map((l,idx) => `<div style="display:flex;gap:10px;color:#f0e9dd;${idx<questions.length-1?"margin-bottom:12px;padding-bottom:12px;border-bottom:1px solid rgba(120,109,89,.45)":""}">
              <span style="color:#b8892a;font-size:.9rem;line-height:1.6;flex-shrink:0;">•</span>
              <span style="line-height:1.75;font-size:.95rem;">${inline(l)}</span>
            </div>`).join("")
          + `</div></div>`;
      }
      if (cta) qHtml += `<div style="margin-top:8px;text-align:center;font-size:.85rem;color:#d1c5b4;font-style:italic;padding:6px 0;">${inline(cta)}</div>`;
    }

    return bodyHtml + qHtml;
  }

  function parseBibleResponse(raw) {
    // ── Primary: ---BIBLE--- separator (preferred format) ────────────────────
    const sep = raw.indexOf("---BIBLE---");
    if (sep !== -1) {
      const chatPart = raw.slice(0, sep).trim();
      const jsonPart = raw.slice(sep + 11).trim();
      try {
        const s = jsonPart.indexOf("{"), e = jsonPart.lastIndexOf("}");
        return { chat: chatPart, panel: JSON.parse(jsonPart.slice(s, e+1)) };
      } catch { return { chat: chatPart, panel: null }; }
    }

    // ── Fallback: ```json { ... } ``` code block in the body ─────────────────
    const BIBLE_KEYS = [
      "titulo","titulo_do_livro","genero","genero_literario","sinopse","protagonista","antagonista",
      "ferida_protagonista","ferida_emocional","regras_mundo","worldRules","plot_point_1","plotPoint1",
      "climax","resolucao","resolution","cenario","setting","incitingIncident","incidente_incitante",
    ];
    const codeBlockRe = /```(?:json)?\s*(\{[\s\S]*?\})\s*```/g;
    let match;
    while ((match = codeBlockRe.exec(raw)) !== null) {
      try {
        const parsed = JSON.parse(match[1]);
        const isBible = BIBLE_KEYS.filter(k => parsed[k] !== undefined).length >= 3;
        if (isBible) {
          // Remove the code block from the visible chat so renderMsg shows the badge only
          const chatPart = raw.slice(0, match.index).trim() + "\n" + raw.slice(match.index + match[0].length).trim();
          return { chat: chatPart.trim(), panel: parsed };
        }
      } catch {}
    }

    // ── Last resort: bare { } JSON object anywhere in the text ───────────────
    const s = raw.lastIndexOf('{"titulo"');
    if (s === -1) return { chat: raw.trim(), panel: null };
    const bigEnd = raw.lastIndexOf("}");
    try {
      const panel = safeParseAIJson(raw.slice(s, bigEnd + 1));
      const chatPart = raw.slice(0, s).trim();
      return { chat: chatPart, panel };
    } catch { return { chat: raw.trim(), panel: null }; }
  }

  // Apply panel from AI chat — NEVER blocked by locks (locks only protect manual user edits in reviewBible)
  function applyPanelFromAI(prev, p) {
    if (!p) return prev;
    // Pass empty locked {} so AI chat can always update any field
    const next = applyPanel(prev, p, {});
    // Auto-expand newly filled fields (first time a field gets content)
    const newlyFilled = Object.keys(next).filter(k => !prev[k] && next[k]);
    const updated = Object.keys(next).filter(k => next[k] && next[k] !== prev[k]);
    if (newlyFilled.length > 0) {
      setbibliaExpanded(e => { const u={...e}; newlyFilled.forEach(k=>{ u[k]=true; }); return u; });
    }
    // Flash animation + toast for all updated fields
    if (updated.length > 0) {
      const flashMap = {}; updated.forEach(k => { flashMap[k] = true; });
      setBibliaFlashFields(flashMap);
      setBibliaUpdatedKeys(prev => ({...prev, ...flashMap})); // persiste na sessão
      setTimeout(() => setBibliaFlashFields({}), 1800);
      // Toast
      if (bibliaToastTimerRef.current) clearTimeout(bibliaToastTimerRef.current);
      setBibliaUpdateToast({ count: updated.length });
      bibliaToastTimerRef.current = setTimeout(() => setBibliaUpdateToast(null), 3500);
    }
    return next;
  }

  function applyPanel(prev, p, locked = {}) {
    if (!p) return prev;
    const f = (key, val) => (locked[key] ? prev[key] : (val || prev[key]));
    return {
      title:             f("title",             p.titulo),
      genre:             f("genre",             p.genero),
      tone:              f("tone",              p.tom),
      synopsis:          f("synopsis",          p.sinopse),
      protagonist:       f("protagonist",       p.protagonista),
      protagonistWound:  f("protagonistWound",  p.ferida_protagonista),
      protagonistDesire: f("protagonistDesire", p.desejo_protagonista),
      protagonistNeed:   f("protagonistNeed",   p.necessidade_protagonista),
      protagonistVoice:  f("protagonistVoice",  p.voz_protagonista),
      protagonistArc:    f("protagonistArc",    p.arco_protagonista),
      antagonist:        f("antagonist",        p.antagonista),
      antagonistMotive:  f("antagonistMotive",  p.motivo_antagonista),
      antagonistArc:     f("antagonistArc",     p.arco_antagonista),
      supportChars:      f("supportChars",      p.personagens_apoio),
      setting:           f("setting",           p.cenario),
      worldRules:        f("worldRules",        p.regras_mundo),
      keyLocations:      f("keyLocations",      p.locais),
      factions:          f("factions",          p.faccoes),
      significantItems:  f("significantItems",  p.itens_significativos),
      worldHistory:      f("worldHistory",      p.historia_mundo),
      sensoryAtmosphere: f("sensoryAtmosphere", p.atmosfera_sensorial),
      conflict:          f("conflict",          p.conflito),
      stakes:            f("stakes",            p.stakes),
      incitingIncident:  f("incitingIncident",  p.incidente),
      themes:            f("themes",            p.temas),
      pov:               f("pov",              p.pov),
      subplots:          f("subplots",          p.subtramas),
      plotPoint1:        f("plotPoint1",        p.plot_point_1),
      midpoint:          f("midpoint",          p.ponto_medio),
      plotPoint2:        f("plotPoint2",        p.plot_point_2),
      climax:            f("climax",            p.climax),
      resolution:        f("resolution",        p.resolucao),
      writingStyle:      f("writingStyle",      p.estilo),
      referenceAuthors:  f("referenceAuthors",  p.autores_referencia),
      influences:        f("influences",        p.influencias),
      prosePace:         f("prosePace",         p.ritmo_prosa),
      stylisticAvoid:    f("stylisticAvoid",    p.evitar),
      seriesArc:         f("seriesArc",         p.arco_serie),
      bookResolution:    f("bookResolution",    p.resolucao_livro),
      nextHooks:         f("nextHooks",         p.ganchos_proximo),
    };
  }

  const BIBLIA_SYS = () => {
    const filled = Object.values(bibliaPanel).filter(Boolean).length;
    const total = Object.keys(bibliaPanel).length;
    const pct = Math.round((filled/total)*100);
    // Dynamic genre intelligence
    const g = (bibliaPanel.genre || config.genre || "").toLowerCase();
    const genreCtx = (() => {
      if (g.includes("fantasia") || g.includes("fantasy") || g.includes("épico") || g.includes("magia"))
        return `\n═══ GENRE INTELLIGENCE: FANTASY ═══
Reader PROMISE: wonder + a world that rewards curiosity. Break this promise and you lose them forever.
- Magic system: RULES + COSTS + LIMITATIONS. No cost = no drama. Establish the rules before breaking them.
- Obligatory scenes the reader expects: first contact with magic, crossing into the unknown, mentor/guide figure, loss that costs something real, darkest hour before climax.
- World-building: gradual revelation through conflict — NEVER exposition dumps. Layer it through character reactions.
- Protagonist: typically begins outside the power structure, forced or pulled into it.
- Pacing reference: Sanderson (systematic magic revealed), Rothfuss (voice-driven immersion), Tolkien (mythic weight).`;
      if (g.includes("thriller") || g.includes("suspense") || g.includes("policial") || g.includes("mistério"))
        return `\n═══ GENRE INTELLIGENCE: THRILLER/SUSPENSE ═══
Reader PROMISE: escalating tension + a revelation that recontextualizes everything. The reader must never feel safe.
- Information management IS the craft: reader knows slightly less than needed to feel safe — always.
- Plant red herrings that feel fair in retrospect (the reader should think "I should have seen that").
- Obligatory scenes: inciting threat, false resolution (reader thinks it's over — it's NOT), revelation of true villain/threat, race against time.
- Pacing rule: no chapter ends without an unresolved question or threat. Ever.
- Reference: Gillian Flynn (unreliable narrators), Lee Child (relentless forward momentum), Tana French (atmosphere + intelligence).`;
      if (g.includes("romance") || g.includes("romântico") || g.includes("romantico"))
        return `\n═══ GENRE INTELLIGENCE: ROMANCE ═══
Reader PROMISE: an emotionally satisfying relationship arc. HEA or HFN is NON-NEGOTIABLE — this is the genre contract.
- The external conflict must MIRROR the internal conflict preventing the relationship.
- Obligatory beats: charged first encounter, first real vulnerability shown, false hope moment, escalating obstacles, BLACK MOMENT (~75%) where reunion seems permanently impossible, earned resolution.
- Both protagonists must have equal agency, equal flaw, equal growth — no rescuing.
- Sexual/romantic tension must be maintained throughout — every interaction raises temperature.
- The relationship itself must change both characters fundamentally, not just unite them.`;
      if (g.includes("terror") || g.includes("horror"))
        return `\n═══ GENRE INTELLIGENCE: HORROR ═══
Reader PROMISE: genuine fear + the violation of safety. What feels safe at page 1 must feel threatening by the end.
- The monster (literal or metaphorical) must represent a SPECIFIC human fear — not just be scary.
- Dread > shock. Build atmosphere, wrongness, and unease before the reveal. The imagined monster is always worse.
- Obligatory: first hint of wrongness (small, easy to dismiss), false safety restored, escalating violations of normality, the moment the protagonist understands the TRUE nature of the threat.
- The protagonist must be changed irrevocably — even if they survive.`;
      if (g.includes("ficção científica") || g.includes("sci-fi") || g.includes("ficcao cientifica") || g.includes("científico"))
        return `\n═══ GENRE INTELLIGENCE: SCIENCE FICTION ═══
Reader PROMISE: a "what if" idea explored rigorously to its logical conclusion.
- One central speculative premise — everything else follows from it with internal consistency.
- The science/technology/social change must DIRECTLY create the central conflict. Not just backdrop.
- World-building through behavior and consequence — show don't explain.
- The protagonist must face a choice that only makes sense in THIS specific world/time.
- The speculative element must illuminate something true about the PRESENT human condition.`;
      if (g.includes("literário") || g.includes("literario") || g.includes("literary") || g.includes("contemporâneo"))
        return `\n═══ GENRE INTELLIGENCE: LITERARY FICTION ═══
Reader PROMISE: a profound human experience that changes how the reader sees the world.
- Every scene must work on at least TWO levels: the literal and the symbolic.
- Prose style IS the experience — voice, rhythm, sentence construction carry as much meaning as plot.
- Plot is pressure that forces interiority. What matters is what the character thinks and feels, not just what happens.
- The ending should feel inevitable and earned — not necessarily happy, but TRUE.
- Reference: Ishiguro (restrained revelation), Morrison (mythic resonance), Rooney (interiority as plot).`;
      return `\n═══ GENRE — NOT YET DEFINED ═══
Identifying the genre is urgent — it determines structure, reader promises, and commercial positioning.
Ask the author: "If your book was on a bookstore shelf, which section would it be in? Who is the reader picking it up?"
Genre clarity = you know which promises to make AND which conventions to honor (or subvert intentionally).`;
    })();

    return `${aiLang}
⚠️ ABSOLUTE OUTPUT RULE — NEVER SKIP — APPLIES TO EVERY SINGLE RESPONSE:
After your entire response, on its own line, you MUST output:
---BIBLE---
Then immediately a single JSON object with ALL bible fields populated with everything known so far (empty string "" for unknown). No exceptions. No skipping. Even if you just changed one field, output the complete JSON with all 39 fields.
${BIBLE_SCHEMA}
---END OF RULE---

You are an elite creative co-author AND bestseller architect — part story genius, part publishing strategist, part obsessive first reader. You build books that are both narratively profound AND commercially compelling. You PROPOSE, CREATE, and CHALLENGE. You do not just ask questions.

═══ IDENTITY ═══
You are the author's most brilliant creative partner: you have read thousands of bestsellers, understand story at a molecular level, know what agents look for, know what makes readers abandon a book at page 30 and what makes them pre-order the sequel at 3am. You react with genuine excitement AND you know how to make ideas work commercially. Your job is to make this the best possible version of what the author is trying to create.

═══ CORE CREATIVE PRINCIPLES ═══
1. SUGGEST FIRST, CONFIRM SECOND. Never ask "does X happen?" — propose: "I see X happening like this... does that resonate?" Offer 2-3 vivid concrete options when something is undefined.
2. "YES, AND..." — always build on what the author just said. Take their detail and immediately expand it.
3. BE SPECIFIC. Never say "interesting conflict." Say "the moment Kael realizes his mother CHOSE this — that IS the emotional peak of Book 2."
4. TAKE STANCES. Say "I think the strongest version of this is..." Don't hedge. Great co-authors have opinions and defend them.
5. FIND THE DEEPER MEANING. What is this story REALLY about? What universal human truth does it explore? Surface it always: "This isn't just about magic — it's about the cost of exceptional gifts on ordinary love."
${genreCtx}
═══ THE LIE THE CHARACTER BELIEVES ═══
This is the single most important concept in bestseller craft. Every compelling protagonist begins the story believing a LIE — a false belief about themselves, others, or the world, formed from their wound. This lie governs every decision they make and drives the external conflict.
- WOUND → LIE → MISBELIEF that causes bad decisions → story forces protagonist to confront it
- Examples: "I must never show weakness or I'll be destroyed" / "I am fundamentally unworthy of love" / "Power is the only real protection" / "Trusting others only leads to betrayal and loss"
- The CLIMAX is the moment the protagonist CHOOSES, for the first time, to act AGAINST their lie.
- When you identify the lie, flag it explicitly: "The lie [Character] believes is ___. The story must systematically shatter this."
- The antagonist often embodies the lie VALIDATED — what happens if you never question it.

═══ THEMATIC ARGUMENT ═══
Stories don't just explore themes — they ARGUE about them. A bestseller takes a side.
- Not: "This story is about identity."
- But: "This story argues that identity is built through conscious choices, not inherited — and the protagonist proves this by dismantling what they were told to be."
- The protagonist proves the thematic argument RIGHT through their arc. The antagonist often embodies the counter-argument.
- Every major plot beat should be asking: does this moment advance the thematic argument?
- Help the author formulate their thesis: "What do you BELIEVE about [theme]? Your readers will finish this book holding your belief."

═══ THE PROMISE OF THE PREMISE ═══
Every book makes an implicit contract with the reader on page 1. Breaking this promise is the #1 cause of 1-star reviews and abandoned reads.
- Always ask: "What is THIS book promising in the first pages? Is every subsequent page delivering on that promise?"
- When a scene or plot development breaks the promise (e.g., a thriller becomes a romance midway), flag it immediately.
- The promise must be established WITHIN the first chapter — no exceptions.

═══ NARRATIVE INTELLIGENCE ═══
Always think about:
- DESIRE vs NEED: What does the protagonist WANT (external goal) vs what do they actually NEED (internal truth they must discover)? The story is the gap between these two.
- WOUND → LIE → MISBELIEF: The past wound created a false belief. The story is the process of dismantling it.
- STAKES: Emotional stakes matter more than physical ones. "The world might end" is less powerful than "she might prove her mother right about her."
- CONFLICT ESCALATION: Every conflict must be BIGGER than the last. Track this — the situation must visibly worsen. Stakes that plateau or decrease kill forward momentum.
- THEMATIC ARGUMENT: The plot is the argument playing out. Every beat should serve it.
- STRUCTURE: Three movements — disruption of ordinary world, confrontation with the impossible challenge, transformation. Plus: the DARKEST MOMENT (~85%) where the protagonist seems totally defeated before the final push.
- SUBTEXT: The most powerful scenes say one thing and mean another. Flag every opportunity.
- READER EXPERIENCE: Always think: what will the reader FEEL here? What question are they asking? What makes them unable to put this down?

═══ CHARACTER PRINCIPLES ═══
- Every character needs: a clear WANT, a hidden NEED, a defining WOUND, the LIE they believe because of that wound, a CONTRADICTION that makes them human, and a VOICE distinct from all others.
- Antagonists need to believe they are RIGHT. The best villains are the heroes of their own story — they embody the thematic counter-argument.
- MIRROR CHARACTERS: The most powerful secondary characters reflect different possible choices the protagonist could make — "what if they had chosen differently?" (Neville to Harry, Sam to Frodo, Jaime to Ned Stark). Suggest these specifically.
- Secondary characters should COMPLICATE the protagonist's journey, not just assist. They should have their own wants that create friction.
- Character arcs = internal change driven by external pressure. What does each character BELIEVE at page 1? What will they believe at the final page?
- Flag when a character feels too perfect, too evil, or too passive.

═══ DIALOGUE CRAFT ═══
Dialogue is the most underestimated craft element. Every line must do at least TWO of these simultaneously:
- Reveal CHARACTER (how they speak IS who they are — word choice, rhythm, what they avoid saying)
- Advance PLOT (something changes because of this conversation)
- Create SUBTEXT (what's NOT being said matters more than what is)
- Differentiate VOICE (no two characters should sound the same — ever)
Rules: People rarely say exactly what they mean. They interrupt, deflect, talk past each other. Conflict in dialogue = two characters each wanting something the other won't give. When defining character voices, be specific: "She speaks in short declarative sentences. He speaks in questions because he's terrified of being wrong."

═══ NARRATIVE VOICE ═══
Voice is what makes a book irreplaceable — it's what makes readers seek out the AUTHOR, not just the next plot. Help the author discover their voice:
- What is their natural rhythm? Long lyrical sentences or short punchy ones? Both?
- What details do they notice that others miss? (This reveals their worldview)
- What is their emotional register? Wry irony? Raw immediacy? Dark humor? Poetic distance?
- Voice should be specific enough that you could identify the author from a single paragraph with no name on it.
- The POV choice (first/third/close/distant) is a VOICE decision, not just technical. Push the author to make it intentional and defend it.

═══ PAGE-TURNER MECHANICS ═══
These are the engineering of compulsive reading. Every bestseller is built on:
- THE OPEN LOOP: The reader must ALWAYS have at least one unanswered question active. The moment all questions are answered, they put the book down. Open a new loop BEFORE closing the current one.
- CHAPTER HOOKS: Every chapter must end on a question, threat, revelation, or decision — never a comfortable resolution. The reader must NEED to turn the page.
- MICRO-TENSION: Even in quiet scenes, something must be at stake. Even breakfast between two characters must have undercurrent — unspoken tension, a secret one holds, something that will change.
- INFORMATION MANAGEMENT: Reveal at the last responsible moment — not earlier. The reader must always know slightly less than they need to feel safe.
- THE TICKING CLOCK: Create urgency through deadlines, threats, consequences with timing. "They have 3 days before..." forces forward momentum.
- SCENE PURPOSE: Every scene must end with the situation either BETTER or WORSE than when it started. No neutral scenes. No scenes that exist only for information delivery.

═══ THE FIRST TEN PAGES ═══
This is where most readers — and all agents — decide. Treat it as sacred.
- Sentence 1: must demand attention. Not world-building — a voice, a tension, a question.
- Page 3: we must care about the protagonist's immediate problem.
- Page 10: we must understand the world, the protagonist's want, and FEEL the shadow of the central conflict.
- The genre promise must be established within the first chapter — no exceptions.
- When helping plan the opening: "What is the most compelling possible scene to OPEN this specific book? What if we started at the most interesting moment?"

═══ MARKET AWARENESS ═══
Great books also need to be findable and sellable:
- LOGLINE TEST: "A [protagonist description] must [goal] before [stakes], but [central obstacle/conflict]." If the book can't be described compellingly in 1 sentence, the premise may need strengthening.
- COMP TITLES: 2-3 published books this resembles. "Fans of [X] and [Y] will love this." No comps = harder to position and sell.
- TARGET READER: Who is buying this? "Women 28-45 who read Colleen Hoover and Sarah J. Maas" is useful. "Anyone who likes good stories" is not.
- HOOK: Could the concept fit a bookstore sticker in 5 words? ("Magic school for adults" / "Gone Girl meets Shining")
- When the author shares their concept, run the logline test: "Let me test if this premise has commercial legs..."

═══ WORLD-BUILDING PRINCIPLES ═══
- Every rule must have a cost. Magic that costs nothing creates no drama.
- The world should feel like it existed before the story and continues after. History matters.
- Locations must have atmosphere and emotional meaning — not just be settings.
- Internal consistency is sacred. Flag contradictions gently and suggest fixes immediately.
- The best world details serve character AND theme simultaneously.

═══ SERIES THINKING ═══
${(config.numBooks||1) > 1 ? `This is a ${config.numBooks}-book series. Think long-term:
- Plant specific seeds in Book 1 that bloom in Book ${config.numBooks}. Name them explicitly.
- Each book: protagonist transformed but facing a LARGER problem than before.
- The LIE the protagonist believes should evolve across books — partially dismantled, rebuilt differently, fully shattered only at the series climax.
- The central theme deepens across books — it doesn't repeat.
- Each book must work as a standalone AND as part of the whole.
- Characters must be meaningfully, visibly different by the series end.` : `If this becomes a series, think about which threads could extend naturally. What lie might take multiple books to fully dismantle?`}

═══ ANTI-CLICHÉS ═══
Flag when an idea is overused (chosen one with no cost, love triangle as sole conflict, villain who monologues their plan, mentor who dies only to motivate the hero, etc.) and immediately suggest a fresh subversion. Frame it as "this could be even more surprising if..."

═══ ADAPTIVE BEHAVIOR ═══
- EARLY (bible <25% filled): Prioritize genre identity, logline test, protagonist's lie, thematic argument. These are the load-bearing walls.
- MID (25-60%): Prioritize structure, page-turner mechanics, character depth, dialogue voice.
- LATE (>60%): Prioritize consistency, first-pages experience, market positioning, promise of the premise check.
- Author adds info → react to the SPECIFIC detail, build on it, then run it through lie/theme/genre lens
- Author asks a question → concrete creative opinion first, then expand
- Author seems uncertain → pick a direction boldly and explain why it's the strongest choice
- Author shares something personal → acknowledge it, connect it to the work specifically
- NEVER ask something already answered. NEVER give generic praise. ALWAYS be specific to THIS story.

═══ READER SIMULATION ═══
At key moments, simulate the reader's experience to test structure:
- "At page 50, the reader is asking: [question]. Are we answering it at the right moment — or too early/late?"
- "At the midpoint, the reader should feel [emotion]. Does our current structure deliver this?"
- "A reader who abandons books at page 30 would stop here because [reason]. Here's how to prevent that:"
Use this tool whenever you detect a pacing risk, information gap, or unclear stakes.

═══ FORMAT ═══
Adapt length to complexity:
- Simple additions or reactions: 2-3 punchy paragraphs. Direct, energetic.
- Complex structural problems (broken premise, weak arc, missing lie): as long as needed to diagnose and fix properly. Don't truncate a diagnosis.
- **bold** for names, places, concepts. *italic* for themes, emotions, titles.
- Then write: ---QUESTIONS---
- 1-2 bullets (•) offering concrete OPTIONS or asking the author to CHOOSE between ideas.
${pct >= 70 ? `- End with: "→ A bíblia está tomando forma! Quando quiser, podemos revisar tudo e criar o roteiro."` : ""}

MANDATORY — append this at the very end of EVERY response, no exceptions:
---BIBLE---
JSON with ALL fields known so far (empty string "" for unknown fields). Include every field mentioned in this entire conversation:
${BIBLE_SCHEMA}`;
  };

  // ── BÍBLIA CHAT: start ───────────────────────────────────────────────────────
  async function startBibliaChat() {
    if (!ideaInput.trim()) return;
    const userMsg = { role:"user", content: ideaInput };
    setbibliaChat([userMsg]);
    setIdeaInput("");
    setBibliaChatLoading(true); setError("");
    try {
      const raw = await callAI([{ role:"user", content: ideaInput }], BIBLIA_SYS(), 0.75);
      const { chat, panel } = parseBibleResponse(raw);
      setbibliaChat([userMsg, { role:"ai", content: chat }]);
      setbibliaPanel(prev => applyPanelFromAI(prev, panel));
    } catch(e) { setError(e.message); setbibliaChat([]); }
    setBibliaChatLoading(false);
  }

  // ── BÍBLIA CHAT: continue ────────────────────────────────────────────────────
  async function sendBibliaChat() {
    const msg = bibliaChatInput.trim();
    if (!msg || bibliaChatLoading) return;
    setBibliaChatInput("");
    if(bibliaChatInputRef.current){bibliaChatInputRef.current.style.height="auto";}
    const newMsgs = [...bibliaChat, { role:"user", content: msg }];
    setbibliaChat(newMsgs);
    setBibliaChatLoading(true);
    try {
      const history = newMsgs.map(m => ({ role: m.role==="ai" ? "assistant" : "user", content: m.content }));
      const raw = await callAI(history, BIBLIA_SYS(), 0.75);
      const { chat, panel } = parseBibleResponse(raw);
      setbibliaChat(prev => [...prev, { role:"ai", content: chat }]);
      if (panel) {
        setbibliaPanel(prev => applyPanelFromAI(prev, panel));
      } else {
        // AI forgot to send ---BIBLE--- JSON — extract silently in background
        extractPanelFromProse(chat, newMsgs);
      }
    } catch(e) { setError(e.message); }
    setBibliaChatLoading(false);
  }

  // Silent extraction when AI sends prose without structured JSON
  async function extractPanelFromProse(aiText, history) {
    try {
      const currentFields = Object.entries(bibliaPanel)
        .filter(([,v]) => v)
        .map(([k,v]) => `${k}: ${v}`)
        .join("\n");
      const lastUserMsg = (history.filter(m=>m.role==="user").slice(-1)[0]?.content||"").slice(0,600);
      const extractPrompt = `The user just told the AI:\n"${lastUserMsg}"\n\nThe AI responded:\n"${aiText.slice(0,1400)}"\n\nCurrent bible fields:\n${currentFields.slice(0,1000)}\n\nBased on the user's new information and the AI's response, output ONLY a JSON object with ALL fields that should be created or updated (including fields the AI said it would update, fields that logically changed based on user input, and new details mentioned). Use these exact Portuguese keys. Return ONLY the JSON, nothing else:\n${BIBLE_SCHEMA}`;
      const extracted = await callAI(
        [{ role:"user", content: extractPrompt }],
        "You are a JSON extractor. Read what the AI described updating and output ONLY a valid JSON object with the updated fields using the exact key names provided. No explanation, no markdown, just the JSON.",
        0.1
      );
      try {
        const panel = safeParseAIJson(extracted);
        if (panel && typeof panel === "object") setbibliaPanel(prev => applyPanelFromAI(prev, panel));
      } catch {}
    } catch {} // silent — never block UI
  }

  // ── BÍBLIA: revisão editorial completa + autocorreção ────────────────────────
  async function reviewBible() {
    if (bibliaChatLoading) return;
    setBibliaChatLoading(true);

    // Use Portuguese key names so they match BIBLE_SCHEMA output — fixes PT/EN inconsistency
    const PT_LABELS = {
      title:"titulo", genre:"genero", tone:"tom", synopsis:"sinopse",
      protagonist:"protagonista", protagonistWound:"ferida_protagonista",
      protagonistDesire:"desejo_protagonista", protagonistNeed:"necessidade_protagonista",
      protagonistVoice:"voz_protagonista", protagonistArc:"arco_protagonista",
      antagonist:"antagonista", antagonistMotive:"motivo_antagonista", antagonistArc:"arco_antagonista",
      supportChars:"personagens_apoio", setting:"cenario", worldRules:"regras_mundo",
      keyLocations:"locais", factions:"faccoes", significantItems:"itens_significativos",
      worldHistory:"historia_mundo", sensoryAtmosphere:"atmosfera_sensorial",
      conflict:"conflito", stakes:"stakes", incitingIncident:"incidente",
      themes:"temas", pov:"pov", subplots:"subtramas",
      plotPoint1:"plot_point_1", midpoint:"ponto_medio", plotPoint2:"plot_point_2",
      climax:"climax", resolution:"resolucao", writingStyle:"estilo",
      referenceAuthors:"autores_referencia", influences:"influencias",
      prosePace:"ritmo_prosa", stylisticAvoid:"evitar",
      seriesArc:"arco_serie", bookResolution:"resolucao_livro", nextHooks:"ganchos_proximo"
    };
    const allFields = Object.entries(bibliaPanel)
      .map(([k,v]) => `${PT_LABELS[k]||k}: ${v || "⚠️ VAZIO"}`)
      .join("\n");

    const reviewPrompt = `🔍 Revisar e corrigir toda a bíblia agora`;
    setbibliaChat(prev => [...prev, { role:"user", content: reviewPrompt }]);

    const systemReview = `${aiLang} You are a senior literary editor AND a creative co-author. Your job is to do a COMPLETE review of this book bible, fix ALL problems in one pass, and deliver a corrected, publication-ready bible.

═══ BIBLE TO REVIEW (ALL FIELDS) ═══
${allFields}

═══ YOUR TASK ═══

STEP 1 — FULL DIAGNOSIS
Review every single field. Check for:
- Internal consistency (does the protagonist's wound feed their motivation? does the conflict arise naturally from the world? does tone match genre?)
- Contradictions between fields (e.g. protagonist described as introverted in one field, charismatic in another)
- Vague or generic content that will cause hallucinations during writing ("interesting character" is not a character)
- Empty critical fields (inciting incident, stakes, protagonist arc, midpoint, climax — these are non-negotiable for a coherent outline)
- Logical gaps (a magic system with no cost, a conflict with no escalation, a protagonist with no internal arc)

STEP 2 — CORRECTIONS
For EVERY problem found: fix it directly. Don't just describe the problem — write the corrected content.
Be specific, vivid, and consistent with everything already defined.

STEP 3 — REPORT
Write a clear report with exactly these sections:
**✅ O que estava sólido**
(list what was already good — be specific)

**🔧 O que foi corrigido**
(for each correction: field name → what was wrong → what you wrote instead)

**📋 Campos adicionados**
(list empty critical fields you filled in)

STEP 4 — OUTPUT CORRECTIONS
After your report, write exactly:
---CORRECOES---
Then a JSON object with ONLY the fields you changed or added (use the same key names as the bible schema, empty string for fields you did NOT touch):
${BIBLE_SCHEMA}

STEP 5 — APPROVAL
After the JSON, on its own line, write:
---APROVADA---

ABSOLUTE RULES — NO EXCEPTIONS:
- YOU HAVE FULL CREATIVE AUTHORITY. Write the corrections yourself. Do not ask. Do not offer options. Do not say "would you like me to...". Just do it.
- NEVER say "I can fix this if you want" or "here are some options". You ARE fixing it. Right now. In this response.
- Fix every single problem in ONE pass. The author does not want to review again.
- Write complete, vivid, specific content for every corrected field — not placeholders, not suggestions.
- The JSON must contain your actual written corrections, not descriptions of what to write.
- The JSON must be valid. Double quotes only. No trailing commas. No comments inside JSON.
- After outputting the JSON, always output ---APROVADA--- on its own line.`;

    try {
      const raw = await callAI(
        [{ role:"user", content: `Review the entire bible, fix ALL problems right now in this single response, output the corrected JSON and approve it. Do not ask questions. Do not offer options. Just do it.` }],
        systemReview, 0.35
      );

      // Split at ---CORRECOES---
      const corrSep = raw.indexOf("---CORRECOES---");
      const chatPart = corrSep === -1 ? raw : raw.slice(0, corrSep).trim();
      const afterCorr = corrSep === -1 ? "" : raw.slice(corrSep + 15);

      // Split at ---APROVADA---
      const aprovSep = afterCorr.search(/---[^-]*APROVADA[^-]*---/i);
      const jsonPart = aprovSep === -1 ? afterCorr : afterCorr.slice(0, aprovSep);
      // Apply corrections to bible panel
      let appliedCount = 0;
      if (jsonPart.trim()) {
        try {
          const s = jsonPart.indexOf("{"), e = jsonPart.lastIndexOf("}");
          if (s !== -1 && e !== -1) {
            const corrections = JSON.parse(jsonPart.slice(s, e+1));
            setbibliaPanel(prev => applyPanel(prev, corrections, bibliaLockedRef.current));
            appliedCount = Object.values(corrections).filter(v => v && v.trim()).length;
          }
        } catch {}
      }

      // Always approve after a successful review — the review's job is to fix and approve
      const suffix = appliedCount > 0
        ? `\n\n---\n✅ **${appliedCount} campo(s) corrigido(s) automaticamente na bíblia.**\n\n🟢 **Bíblia aprovada — clique em "✨ Criar Roteiro →" para continuar.**`
        : `\n\n🟢 **Bíblia revisada e aprovada — clique em "✨ Criar Roteiro →" para continuar.**`;

      setbibliaChat(prev => [...prev, { role:"ai", content: chatPart + suffix }]);
      setbibliaReviewed(true);

    } catch(e) {
      setbibliaChat(prev => [...prev, { role:"ai", content: "Erro na revisão: " + e.message }]);
    }
    setBibliaChatLoading(false);
  }

  function buildOutlineContext(cfgLocal, bIdx, sums, charsLocal, authorNotes = "") {
    const cb = charsLocal.map(c => `- ${c.nome||c.name} (${c.papel||c.role}): ${c.motivacao||c.motivation||""} | Arc: ${c.arco||c.arc||""}`).join("\n");
    const prevCtx = sums.length > 0
      ? `\n\nPREVIOUS BOOKS CANON (IMMUTABLE — never contradict):\n${sums.map((s,i) => `Book ${i+1}: ${s}`).join("\n\n")}\n\nThis is Book ${bIdx} of ${cfgLocal.numBooks}. Continue the story from where it left off.`
      : "";
    const seriesConstraint = cfgLocal.numBooks > 1 ? `

═══ SÉRIE: RESTRIÇÕES CRÍTICAS ═══
Esta é uma série de ${cfgLocal.numBooks} livros. Este roteiro cobre APENAS o LIVRO ${bIdx}.

REGRAS INVIOLÁVEIS para o Livro ${bIdx}:
${bIdx === 1 ? `
- O Livro 1 apresenta o mundo, os protagonistas e o conflito central — NÃO os resolve.
- O antagonista principal da série deve aparecer como ameaça, mas NÃO ser derrotado nem plenamente revelado.
- O arco da série (conflito maior) deve ser apenas INICIADO, não concluído.
- O Livro 1 termina com uma vitória PARCIAL e LOCAL — os protagonistas sobrevivem, crescem, vencem um obstáculo — mas a guerra maior continua.
- Os mistérios centrais da série devem permanecer abertos — o leitor quer o Livro 2.
- NÃO escreva capítulos que: derrotam o antagonista da série, explicam todos os segredos do mundo, cumprem a promessa total da sinopse.
- O clímax do Livro 1 = os protagonistas enfrentam e sobrevivem à primeira grande ameaça, emergem transformados, mas o caminho à frente é mais difícil.
- Termine com um gancho emocional forte que justifique o Livro 2.` :
bIdx < cfgLocal.numBooks ? `
- Este é o Livro ${bIdx} de ${cfgLocal.numBooks} — a fase de escalada e complicação.
- Resolva arcos secundários abertos desde o Livro ${bIdx-1}, mas deixe o conflito principal para o Livro ${cfgLocal.numBooks}.
- Termine com a situação mais sombria da série — o "tudo parece perdido" — para motivar o volume final.` : `
- Este é o LIVRO FINAL (${bIdx} de ${cfgLocal.numBooks}).
- Resolva TODOS os arcos: protagonistas, antagonista, conflito da série, mistérios do mundo.
- Não deixe fios soltos principais.`}
${bibliaPanel.seriesArc ? `\nARCO DA SÉRIE (toda a jornada): ${bibliaPanel.seriesArc}` : ""}
${bibliaPanel.bookResolution ? `\nO QUE O LIVRO ${bIdx} RESOLVE (e apenas isso): ${bibliaPanel.bookResolution}` : ""}
${bibliaPanel.nextHooks ? `\nGANCHOS PLANTADOS PARA O PRÓXIMO LIVRO: ${bibliaPanel.nextHooks}` : ""}
═══════════════════════════════════` : "";
    const bibleCtx = [
      bibliaPanel.setting           && `WORLD/SETTING: ${bibliaPanel.setting}`,
      bibliaPanel.worldRules        && `WORLD RULES: ${bibliaPanel.worldRules}`,
      bibliaPanel.keyLocations      && `KEY LOCATIONS: ${bibliaPanel.keyLocations}`,
      bibliaPanel.factions          && `FACTIONS: ${bibliaPanel.factions}`,
      bibliaPanel.significantItems  && `SIGNIFICANT ITEMS: ${bibliaPanel.significantItems}`,
      bibliaPanel.worldHistory      && `WORLD HISTORY: ${bibliaPanel.worldHistory}`,
      bibliaPanel.sensoryAtmosphere && `ATMOSPHERE: ${bibliaPanel.sensoryAtmosphere}`,
      bibliaPanel.conflict          && `CENTRAL CONFLICT: ${bibliaPanel.conflict}`,
      bibliaPanel.stakes            && `STAKES: ${bibliaPanel.stakes}`,
      bibliaPanel.incitingIncident  && `INCITING INCIDENT: ${bibliaPanel.incitingIncident}`,
      bibliaPanel.themes            && `CENTRAL THEMES: ${bibliaPanel.themes}`,
      bibliaPanel.subplots          && `SUB-PLOTS: ${bibliaPanel.subplots}`,
      bibliaPanel.plotPoint1        && `PLOT POINT 1: ${bibliaPanel.plotPoint1}`,
      bibliaPanel.midpoint          && `MIDPOINT: ${bibliaPanel.midpoint}`,
      bibliaPanel.plotPoint2        && `PLOT POINT 2: ${bibliaPanel.plotPoint2}`,
      bibliaPanel.climax            && `CLIMAX: ${bibliaPanel.climax}`,
      bibliaPanel.resolution        && `RESOLUTION: ${bibliaPanel.resolution}`,
      bibliaPanel.writingStyle      && `WRITING STYLE: ${bibliaPanel.writingStyle}`,
      bibliaPanel.referenceAuthors  && `REFERENCE AUTHORS: ${bibliaPanel.referenceAuthors}`,
      bibliaPanel.influences        && `LITERARY INFLUENCES: ${bibliaPanel.influences}`,
      bibliaPanel.prosePace         && `PROSE PACE: ${bibliaPanel.prosePace}`,
      bibliaPanel.stylisticAvoid    && `AVOID: ${bibliaPanel.stylisticAvoid}`,
      bibliaPanel.tone              && `TONE: ${bibliaPanel.tone}`,
      bibliaPanel.seriesArc         && `SERIES ARC: ${bibliaPanel.seriesArc}`,
      bibliaPanel.bookResolution    && `THIS BOOK RESOLVES: ${bibliaPanel.bookResolution}`,
      bibliaPanel.nextHooks         && `HOOKS FOR NEXT BOOK: ${bibliaPanel.nextHooks}`,
    ].filter(Boolean).join("\n");
    const authorNotesCtx = authorNotes ? `\n\nAUTHOR NOTES FOR BOOK ${bIdx} (high priority — follow these directions):\n${authorNotes}` : "";
    const bookTitle = (cfgLocal.bookTitles && cfgLocal.bookTitles[bIdx - 1]) || cfgLocal.title || "Sem título";
    return { cb, prevCtx, seriesConstraint, bibleCtx, authorNotesCtx, bookTitle };
  }

  async function validateOutlineBatch(batch, bIdx, numBooks, outlineCtx) {
    try {
      const minimalCtx = [
        bibliaPanel.conflict && `Conflito: ${bibliaPanel.conflict}`,
        bibliaPanel.stakes && `Stakes: ${bibliaPanel.stakes}`,
        bibliaPanel.incitingIncident && `Incidente: ${bibliaPanel.incitingIncident}`,
        bibliaPanel.themes && `Temas: ${bibliaPanel.themes}`,
        bibliaPanel.bookResolution && `Livro ${bIdx} resolve: ${bibliaPanel.bookResolution}`,
        bibliaPanel.nextHooks && `Gancho próximo livro: ${bibliaPanel.nextHooks}`,
      ].filter(Boolean).join("\n");
      const result = await callAI(
        [{ role:"user", content: `Valide estes capítulos de roteiro contra a bíblia e o escopo do Livro ${bIdx}/${numBooks}. Retorne APENAS JSON array.\n\nContexto:\n${minimalCtx}\n${outlineCtx.seriesConstraint}\n\nCapítulos:\n${batch.map(ch => `Cap ${ch.id}\nTítulo: ${ch.titulo}\nResumo: ${ch.resumo}\nFunção: ${ch.papel}\nObjetivo POV: ${ch.objetivo_pov||""}\nMudança: ${ch.mudanca||""}`).join("\n\n")}\n\nFormato obrigatório:\n[{"id":1,"ok":true,"issue":""}]\n\nMarque ok=false somente para desvios reais: contradição da bíblia, resolução precoce da série, arco incoerente, papel estrutural errado.` }],
        aiLang + " You are a strict outline consistency editor. Return ONLY valid JSON array with concise issues in Brazilian Portuguese.", 0.2, 1200
      );
      const parsed = safeParseAIJson(result);
      if (!Array.isArray(parsed)) return {};
      const warns = {};
      parsed.forEach(p => {
        if (p && p.id && !p.ok && p.issue) warns[p.id] = String(p.issue).trim();
      });
      return warns;
    } catch {
      return {};
    }
  }

  async function parseOutlineBatchWithRepair(raw, expectedCount, startId, endId) {
    try {
      const parsed = safeParseAIJson(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      try {
        const repaired = await callAI(
          [{ role:"user", content: `Repair this malformed JSON into a valid JSON array ONLY.\n\nRules:\n- Return exactly ${expectedCount} objects\n- Keep chapter ids from ${startId} to ${endId}\n- Preserve meaning from the original text\n- Output ONLY pure JSON array, no markdown\n\nMalformed input:\n${String(raw).slice(0, 14000)}` }],
          "You are a strict JSON repair engine. Output ONLY valid JSON array.", 0.0, 2500
        );
        const parsedRepaired = safeParseAIJson(repaired);
        return Array.isArray(parsedRepaired) ? parsedRepaired : [];
      } catch {
        return [];
      }
    }
  }

  async function regenerateOutlineBatchStrict(cfgLocal, outlineCtx, priorCtx, count, startId, endId, bIdx) {
    const strictRaw = await callAI(
      [{ role:"user", content: `Return ONLY valid JSON array (RFC8259), no markdown, no prose.\n\nGenerate exactly ${count} chapters for Book ${bIdx} of ${cfgLocal.numBooks}, ids ${startId}..${endId}.\n\nTitle: ${outlineCtx.bookTitle}\nGenre: ${cfgLocal.genre}\nTone: ${cfgLocal.tone}\nPOV: ${cfgLocal.pov}\nSynopsis: ${cfgLocal.synopsis}\nSetting: ${cfgLocal.setting}\nCharacters:\n${outlineCtx.cb}\n\n${outlineCtx.bibleCtx}${outlineCtx.seriesConstraint}${outlineCtx.prevCtx}${outlineCtx.authorNotesCtx}${priorCtx}\n\nOutput schema (all strings must be double-quoted):\n[{"id":${startId},"titulo":"...","resumo":"...","ato":1,"papel":"setup","objetivo_pov":"...","mudanca":"..."}]\n\nRules:\n- EXACTLY ${count} objects\n- IDs strictly sequential from ${startId} to ${endId}\n- No trailing commas\n- No comments\n- No text outside JSON` }],
      aiLang + " Strict JSON generator. Return ONLY valid JSON array.", 0.2, Math.max(1800, count * 420)
    );
    return await parseOutlineBatchWithRepair(strictRaw, count, startId, endId);
  }

  async function generateNextOutlineBatch(cfgLocal, charsLocal, sums, bIdx, existingOutline = [], authorNotes = "") {
    const total = cfgLocal.numChapters || 0;
    const startIdx = existingOutline.length;
    if (startIdx >= total) return { merged: existingOutline, nextIdx: startIdx, warnings: {} };

    const batchSize = Math.max(3, Math.min(8, outlineBatchSize || 5));
    const count = Math.min(batchSize, total - startIdx);
    const startId = startIdx + 1;
    const endId = startIdx + count;
    const outlineCtx = buildOutlineContext(cfgLocal, bIdx, sums, charsLocal, authorNotes);
    const priorCtx = existingOutline.length
      ? `\n\nCHAPTERS ALREADY APPROVED (immutable, do not rewrite):\n${existingOutline.map(ch => `Cap ${ch.id} | Ato ${ch.ato} | ${ch.papel}\nTítulo: ${ch.titulo}\nResumo: ${ch.resumo}`).join("\n\n")}`
      : "";

    setLoadingMsg(`Gerando roteiro: capítulos ${startId}-${endId}...`);
    const raw = await callAI(
      [{ role:"user", content: `Create ONLY the next ${count} chapter-outline entries for Book ${bIdx} of ${cfgLocal.numBooks}, covering chapters ${startId} to ${endId}. Return ONLY pure JSON array.\n\nTitle: ${outlineCtx.bookTitle}\nGenre: ${cfgLocal.genre}\nTone: ${cfgLocal.tone}\nPOV: ${cfgLocal.pov}\nSynopsis: ${cfgLocal.synopsis}\nSetting: ${cfgLocal.setting}\nCharacters:\n${outlineCtx.cb}\n\n${outlineCtx.bibleCtx}${outlineCtx.seriesConstraint}${outlineCtx.prevCtx}${outlineCtx.authorNotesCtx}${priorCtx}\n\nRules:\n- Output exactly ${count} items, chapter ids ${startId}..${endId}\n- Continue from prior approved chapters without contradiction\n- Do NOT resolve the series conflict early\n- Keep 3-act progression coherent\n\nEach object must include: id, titulo, resumo, ato, papel, objetivo_pov, mudanca.` }],
      aiLang + " Master literary editor. Continue outlines with high continuity. Return ONLY pure JSON array.", 0.45,
      Math.max(1800, count * 450)
    );
    let arr = await parseOutlineBatchWithRepair(raw, count, startId, endId);
    if (arr.length < count) {
      setLoadingMsg(`Regerando JSON estrito: capítulos ${startId}-${endId}...`);
      arr = await regenerateOutlineBatchStrict(cfgLocal, outlineCtx, priorCtx, count, startId, endId, bIdx);
    }
    const batch = arr.slice(0, count).map((ch, idx) => ({
      id: startId + idx,
      titulo: ch?.titulo || `Capítulo ${startId + idx}`,
      resumo: ch?.resumo || "",
      ato: Number(ch?.ato) || (startId + idx <= Math.ceil(total*0.25) ? 1 : (startId + idx > Math.ceil(total*0.75) ? 3 : 2)),
      papel: ch?.papel || "rising_action",
      objetivo_pov: ch?.objetivo_pov || "",
      mudanca: ch?.mudanca || "",
    }));
    const safeBatch = batch.length === count ? batch : Array.from({length:count}, (_,i) => ({
      id: startId + i, titulo:`Capítulo ${startId + i}`, resumo:"", ato:2, papel:"rising_action", objetivo_pov:"", mudanca:""
    }));
    if (batch.length !== count) {
      setError(`A IA retornou JSON inválido no bloco ${startId}-${endId}. Preenchi capítulos-base para você revisar sem travar o fluxo.`);
    }

    setLoadingMsg(`Validando capítulos ${startId}-${endId}...`);
    const warnings = await validateOutlineBatch(safeBatch, bIdx, cfgLocal.numBooks, outlineCtx);
    return { merged: [...existingOutline, ...safeBatch], nextIdx: endId, warnings };
  }

  // ── BÍBLIA CHAT: build outline directly from conversation + panel ─────────────
  async function buildOutlineFromChat() {
    setLoading(true); setLoadingMsg(T.load_building); setError("");
    try {
      // Step 1 — extract structured config from the conversation + panel
      const conv = bibliaChat.map(m => `${m.role==="user"?"User":"Assistant"}: ${m.content}`).join("\n\n");
      const panelCtx = Object.entries(bibliaPanel).filter(([,v])=>v).map(([k,v])=>`${k}: ${v}`).join("\n");
      const result = await callAI(
        [{ role:"user", content: `Extract a complete book bible from this conversation and current bible state. Return ONLY valid JSON.\n\nConversation:\n${conv}\n\nCurrent bible:\n${panelCtx}\n\nReturn:\n{"titulo":"...","subtitulo":"...","genero":"...","tom":"...","pov":"...","sinopse":"...","cenario":"...","protagonista":"...","antagonista":"...","estilo_escrita":"...","num_capitulos":${config.numChapters||6}}` }],
        aiLang + " Extract complete structured book info. Fill ALL fields. Return ONLY valid JSON.", 0.1
      );
      const parsed = safeParseAIJson(result);

      const newConfig = {
        title: parsed.titulo||bibliaPanel.title||"My Book",
        subtitle: parsed.subtitulo||"", author:"",
        genre: parsed.genero||GENRES[0],
        tone: parsed.tom||TONES[0],
        pov: parsed.pov||POV[1]||POV[0],
        // Always respect the user's explicit chapter count from Config — never let the AI override it
        numChapters: config.numChapters || Math.max(4, parseInt(parsed.num_capitulos)||6),
        synopsis: parsed.sinopse||bibliaPanel.synopsis||"",
        setting: parsed.cenario||bibliaPanel.setting||"",
        protagonist: parsed.protagonista||bibliaPanel.protagonist||"",
        antagonist: parsed.antagonista||bibliaPanel.antagonist||"",
        writingStyle: parsed.estilo_escrita||bibliaPanel.writingStyle||"",
        numBooks: config.numBooks||1,
        bookTitles: config.bookTitles||[],
        chronicleName: config.chronicleName||"",
        coverUrl: config.coverUrl || "",
      };
      setConfig(newConfig);

      // Step 2 — generate characters from the full bible panel (silent, used only for outline context)
      setLoadingMsg(T.load_chars);
      const charResult = await callAI(
        [{ role:"user", content: `Create 3-5 rich characters for this book. Return ONLY a JSON array. Short strings, no line breaks.\n\nTitle: ${newConfig.title}\nGenre: ${newConfig.genre}\nTone: ${newConfig.tone}\nSynopsis: ${newConfig.synopsis.slice(0,300)}\nProtagonist: ${newConfig.protagonist}\nAntagonist: ${newConfig.antagonist}\nSupporting: ${bibliaPanel.supportChars||""}\nThemes: ${bibliaPanel.themes||""}\nConflict: ${bibliaPanel.conflict||""}\nStakes: ${bibliaPanel.stakes||""}\n\nFormat:\n[{"nome":"Name","papel":"protagonist","idade":"30","aparencia":"desc","personalidade":"desc","motivacao":"desc","ferida":"wound","arco":"arc desc"}]` }],
        aiLang + " Return ONLY pure JSON array, no markdown, no extra text. Short strings, no line breaks inside values.", 0.5
      );
      const generatedChars = safeParseAIJson(charResult);
      setCharacters(generatedChars);

      // Step 3 — generate only the first outline batch, then pause for review
      setOutline([]);
      setOutlinePlanWarnings({});
      setOutlineAuthorNotes("");
      setOutlineNextToGenerate(0);
      setPhase("outline");
      const firstBatch = await generateNextOutlineBatch(newConfig, generatedChars, canonicalSummaries, currentBookIdx, [], "");
      setOutline(firstBatch.merged);
      setOutlineNextToGenerate(firstBatch.nextIdx);
      setOutlinePlanWarnings(firstBatch.warnings);
    } catch(e) { setError(e.message); }
    setLoading(false); setLoadingMsg("");
  }

  // ── CHARACTERS ──────────────────────────────────────────────────────────────
  async function doGenerateCharacters() {
    setGeneratingChar(true); setError("");
    try {
      const c = config;
      const result = await callAI(
        [{ role:"user", content: "Create the main characters for this book. Return ONLY a pure JSON array without markdown.\n\nTitle: " + c.title + "\nGenre: " + c.genre + "\nSynopsis: " + c.synopsis + "\n\nCreate 3 to 4 characters. For each: nome, papel, idade, aparencia, personalidade, motivacao, segredo, arco, relacoes." }],
        aiLang + " Create rich literary characters. Return ONLY pure JSON array without markdown.", 0.75
      );
      setCharacters(safeParseAIJson(result));
    } catch(e) { setError(e.message); }
    setGeneratingChar(false);
  }

  async function refineCharacter(idx) {
    if (!charFeedback.trim()) return;
    setGeneratingChar(true);
    try {
      const result = await callAI(
        [{ role:"user", content: `Adjust this character based on feedback. Return ONLY the updated character JSON without markdown.\n\nCharacter: ${JSON.stringify(characters[idx])}\nFeedback: ${charFeedback}\nContext: ${config.synopsis}` }],
        aiLang + " Adjust literary characters maintaining coherence. Return ONLY pure character JSON.", 0.6
      );
      const updated = safeParseAIJson(result);
      setCharacters(prev => prev.map((c,i) => i===idx ? updated : c));
      setEditingChar(null); setCharFeedback("");
    } catch(e) { setError(e.message); }
    setGeneratingChar(false);
  }

  // ── REFINEMENT CHAT ─────────────────────────────────────────────────────────
  async function sendRefinementChat() {
    const msg = chatInput.trim();
    if (!msg || chatLoading) return;
    setChatInput("");
    const newMessages = [...chatMessages, { role:"user", content: msg }];
    setChatMessages(newMessages);
    setChatLoading(true);
    try {
      const history = newMessages.map(m => ({ role: m.role==="ai" ? "assistant" : "user", content: m.content }));
      const sysCtx = `${aiLang} You are a collaborative world-building assistant helping to refine a book bible. Current state:\nTitle: ${config.title}\nGenre: ${config.genre}\nSynopsis: ${config.synopsis}\nCharacters: ${characters.map(c=>`${c.nome||c.name} (${c.papel||c.role})`).join(", ")}\n\nAnswer questions, suggest improvements, refine world rules. Be creative and concise.`;
      const response = await callAI(history, sysCtx, 0.7);
      setChatMessages(prev => [...prev, { role:"ai", content: response }]);
    } catch(e) {
      setChatMessages(prev => [...prev, { role:"ai", content: "Error: " + e.message }]);
    }
    setChatLoading(false);
  }

  // ── OUTLINE ─────────────────────────────────────────────────────────────────
  async function generateOutline(prevSummaries, bookIdx, authorNotes = "", overrideConfig = null) {
    const sums = prevSummaries !== undefined ? prevSummaries : canonicalSummaries;
    const bIdx = bookIdx !== undefined ? bookIdx : currentBookIdx;
    const cfg2 = overrideConfig || config;
    setLoading(true); setLoadingMsg(T.load_outline); setError("");
    try {
      setOutline([]);
      setOutlinePlanWarnings({});
      setOutlineNextToGenerate(0);
      setOutlineAuthorNotes(authorNotes || "");
      setPhase("outline");
      const firstBatch = await generateNextOutlineBatch(cfg2, characters, sums, bIdx, [], authorNotes || "");
      setOutline(firstBatch.merged);
      setOutlineNextToGenerate(firstBatch.nextIdx);
      setOutlinePlanWarnings(firstBatch.warnings);
    } catch(e) { setError(e.message); }
    setLoading(false); setLoadingMsg("");
  }

  async function continueOutlineBatch() {
    if (loading) return;
    if (outlineNextToGenerate >= (config.numChapters || 0)) return;
    setLoading(true); setError("");
    try {
      const bIdx = currentBookIdx;
      const sums = canonicalSummaries;
      const result = await generateNextOutlineBatch(config, characters, sums, bIdx, outline, outlineAuthorNotes || "");
      setOutline(result.merged);
      setOutlineNextToGenerate(result.nextIdx);
      setOutlinePlanWarnings(prev => ({ ...prev, ...result.warnings }));
    } catch(e) { setError(e.message); }
    setLoading(false); setLoadingMsg("");
  }

  function isOutlineChapterEmpty(ch) {
    const resumo = String(ch?.resumo || "").trim();
    return !resumo;
  }

  async function fillEmptyOutlineChapters() {
    if (loading || fixingOutlineChapter !== null) return;
    const empties = outline
      .map((ch, idx) => ({ ch, idx }))
      .filter(({ ch }) => isOutlineChapterEmpty(ch));
    if (empties.length === 0) return;
    for (const item of empties) {
      // eslint-disable-next-line no-await-in-loop
      await fixOutlineDeviation(
        item.ch,
        item.idx,
        "Capítulo vazio/incompleto. Reescreva o capítulo inteiro do roteiro mantendo continuidade com bíblia, série e capítulos vizinhos."
      );
    }
  }

  async function fixOutlineDeviation(ch, idx, warningText) {
    if (!warningText || fixingOutlineChapter !== null) return;
    setFixingOutlineChapter(ch.id);
    setError("");
    try {
      const bIdx = currentBookIdx;
      const sums = canonicalSummaries;
      const outlineCtx = buildOutlineContext(config, bIdx, sums, characters, outlineAuthorNotes || "");
      const prevOutlineCtx = outline
        .slice(0, idx)
        .map(o => `Cap ${o.id} | Ato ${o.ato} | ${o.papel}\nTítulo: ${o.titulo}\nResumo: ${o.resumo}`)
        .join("\n\n");
      const nextOutlineCtx = outline
        .slice(idx + 1, Math.min(outline.length, idx + 4))
        .map(o => `Cap ${o.id} | Ato ${o.ato} | ${o.papel}\nTítulo: ${o.titulo}\nResumo: ${o.resumo}`)
        .join("\n\n");

      const raw = await callAI(
        [{ role:"user", content: `Corrija APENAS o capítulo de roteiro abaixo para eliminar o desvio da bíblia e manter continuidade com o restante do livro.\n\nCapítulo com desvio:\nID: ${ch.id}\nTítulo: ${ch.titulo}\nResumo: ${ch.resumo}\nAto: ${ch.ato}\nPapel: ${ch.papel}\nObjetivo POV: ${ch.objetivo_pov||""}\nMudança: ${ch.mudanca||""}\n\nDesvio detectado:\n${warningText}\n\nCapítulos anteriores (imutáveis):\n${prevOutlineCtx || "Nenhum"}\n\nPróximos capítulos (não contradizer):\n${nextOutlineCtx || "Nenhum"}\n\nContexto da bíblia:\n${outlineCtx.bibleCtx}\n${outlineCtx.seriesConstraint}\n${outlineCtx.prevCtx}\n${outlineCtx.authorNotesCtx}\n\nRetorne APENAS um objeto JSON válido com:\n{"id":${ch.id},"titulo":"","resumo":"","ato":1,"papel":"","objetivo_pov":"","mudanca":""}\n\nRegras:\n- manter mesmo id\n- corrigir o rumo sem resolver prematuramente o conflito da série\n- preservar progressão de três atos e coerência do Livro ${bIdx}` }],
        aiLang + " Você é um editor estrutural rigoroso. Corrija desvios de outline mantendo canon e continuidade. Retorne SOMENTE JSON válido.", 0.25, 1800
      );
      const fixed = safeParseAIJson(raw) || {};
      const updated = {
        ...ch,
        id: ch.id,
        titulo: fixed.titulo || ch.titulo,
        resumo: fixed.resumo || ch.resumo,
        ato: [1,2,3].includes(Number(fixed.ato)) ? Number(fixed.ato) : ch.ato,
        papel: fixed.papel || ch.papel,
        objetivo_pov: fixed.objetivo_pov || ch.objetivo_pov || "",
        mudanca: fixed.mudanca || ch.mudanca || "",
      };

      setOutline(prev => prev.map(o => o.id === ch.id ? updated : o));
      const singleWarn = await validateOutlineBatch([updated], bIdx, config.numBooks, outlineCtx);
      setOutlinePlanWarnings(prev => {
        const next = { ...prev };
        if (singleWarn[ch.id]) next[ch.id] = singleWarn[ch.id];
        else delete next[ch.id];
        return next;
      });
    } catch(e) {
      setError(`Erro ao corrigir desvio do roteiro no cap. ${ch.id}: ${e.message}`);
    }
    setFixingOutlineChapter(null);
  }

  // ── CANONICAL SUMMARY ───────────────────────────────────────────────────────
  async function generateCanonicalSummary(chaptersData) {
    const fullText = chaptersData
      .filter(c => c.status==="done" && c.content)
      .map(c => `${c.title}:\n${c.content}`)
      .join("\n\n");
    return await callAI(
      [{ role:"user", content: `Create a 400-600 word canonical summary of this book for series continuity. Include: main events, character developments, world changes, unresolved threads for future books.\n\n${fullText.slice(0,8000)}` }],
      aiLang + " Create precise canonical summaries to maintain narrative continuity in a series. Be detailed about events, character states and world changes.", 0.3
    );
  }

  // ── CONTINUITY CHECK ─────────────────────────────────────────────────────────
  async function checkContinuity(chapterId, newContent, previousChapters) {
    if (previousChapters.length === 0) return;
    const prevText = previousChapters
      .map(c => `${c.title}:\n${c.content.slice(0, 700)}`)
      .join("\n\n---\n\n");
    const bible = buildBible(config, characters, canonicalSummaries);
    try {
      const result = await callAI(
        [{ role:"user", content:
          `Compare the new chapter against previous chapters. List ONLY real inconsistencies (character traits, established facts, locations, timeline). If none, reply exactly: NENHUMA_INCONSISTENCIA\n\nNEW CHAPTER ${chapterId}:\n${newContent.slice(0,1800)}\n\nPREVIOUS CHAPTERS:\n${prevText.slice(0,3500)}`
        }],
        bible + "\n\nYou are a continuity editor. List only genuine contradictions, not style differences. Be specific. Keep under 150 words. Write in Portuguese.",
        0.2
      );
      if (!result.includes("NENHUMA_INCONSISTENCIA")) {
        setContinuityWarnings(prev => ({ ...prev, [chapterId]: result.trim() }));
      }
    } catch {} // advisory only — never block writing
  }

  function sanitizeProseStyle(content) {
    const text = String(content || "");
    if (!text) return text;
    // Remove markdown italics around common onomatopoeias.
    let cleaned = text.replace(/\*(clang|tap|tic-tac|tick-tock|toc|tum|bam|boom|crash|snap|click|whoosh|hum|thud|crack|swish|hiss)\*/gi, "$1");
    // Replace plain onomatopoeias with neutral prose wording (pt-BR style).
    cleaned = cleaned
      .replace(/\bclang\s+met[áa]lico\b/gi, "som metálico seco")
      .replace(/\bclang\b/gi, "som metálico seco")
      .replace(/\btic-?tac\b/gi, "ritmo mecânico")
      .replace(/\btap\b/gi, "toque leve")
      .replace(/\bclick\b/gi, "estalido curto")
      .replace(/\bsnap\b/gi, "estalo seco");
    // Fix occasional adjective duplication introduced by prior replacements.
    cleaned = cleaned
      .replace(/som metálico seco metálico/gi, "som metálico seco")
      .replace(/som metálico seco seco/gi, "som metálico seco");
    // Canon consistency: enforce mother's canonical name.
    cleaned = cleaned
      .replace(/\bElyra Valenor\b/gi, "Elara Valenor")
      .replace(/\bAlthea Valenor\b/gi, "Elara Valenor")
      .replace(/\b(m[ãa]e[^.\n:]{0,45}\b)(Elyra|Althea|Lyra)\b/gi, "$1Elara")
      .replace(/\bsua m[ãa]e,\s*Lyra\b/gi, "sua mãe, Elara");
    // Terminology normalization for shadow creatures.
    cleaned = cleaned
      .replace(/\bUmbryns\b/g, "Umbrins")
      .replace(/\bUmbryns\b/gi, "Umbrins")
      .replace(/\bUmbryn\b/g, "Umbrin")
      .replace(/\bUmbryn\b/gi, "Umbrin");
    // Book 1 power-scaling guardrails in prose wording.
    cleaned = cleaned
      .replace(/\bv[íi]nculo\s+pleno\s+com\s+(Umbryth|Solarys)\b/gi, "ecos instáveis de luz e sombra")
      .replace(/\bdom[íi]nio\s+total\s+de\s+(Umbryth|Solarys)\b/gi, "controle parcial e instável")
      .replace(/\bcinco\s+vozes\b/gi, "ecos fragmentados")
      .replace(/\bcinco\s+l[úu]mens\b/gi, "ecos de poder ainda indecifráveis")
      .replace(/\bm[úu]ltiplos?\s+v[íi]nculos?\b/gi, "um vínculo incomum ainda incompleto")
      .replace(/\b(Umbryth|Solarys)\s+(sussurr(?:ou|a|ava|am)?|fal(?:ou|a|ava|am)?|disse|respondeu)\b/gi, "um eco ancestral pressionou sua percepção");
    // Keep Book 1 naming less explicit in prose beats (echoes, not mastery).
    cleaned = cleaned
      .replace(/\bSolarys\b/g, "luz ancestral")
      .replace(/\bUmbryth\b/g, "sombra ancestral");
    // Fix pasted/chunked words detected in generated chapters.
    cleaned = cleaned
      .replace(/\bOmartelo\b/g, "O martelo")
      .replace(/\bOarquivo\b/g, "O arquivo")
      .replace(/\bAcabana\b/g, "A cabana")
      .replace(/\bAfloresta\b/g, "A floresta")
      .replace(/\bOsino\b/g, "O sino")
      .replace(/\bOvento\b/g, "O vento")
      .replace(/\bAn[ée]voa\b/g, "A névoa")
      .replace(/\bAcela\b/g, "A cela")
      .replace(/\bObarril\b/g, "O barril")
      .replace(/\bOport[ãa]o\b/g, "O portão")
      .replace(/\bOpulso\b/g, "O pulso")
      .replace(/\bOdiapas[ãa]o\b/g, "O diapasão")
      .replace(/\bOvelho\b/g, "O velho")
      .replace(/\bOquarto\b/g, "O quarto")
      .replace(/\bOsal[ãa]o\b/g, "O salão")
      .replace(/\bOar\b/g, "O ar")
      .replace(/\bOs[óo]t[ãa]o\b/g, "O sótão")
      .replace(/\bAdor\b/g, "A dor")
      .replace(/\bApenis\b/g, "Apenas")
      .replace(/\balaixo\b/g, "alojo")
      .replace(/\bassentiran\b/g, "assentiram")
      .replace(/\btrocararam\b/g, "trocaram")
      .replace(/\bDurman bem\b/g, "Durmam bem")
      .replace(/\bclic suave\b/g, "clique suave")
      .replace(/\bganho de forja\b/g, "gancho de forja")
      .replace(/\bmolha de prata\b/g, "moldura de prata");
    return cleaned;
  }

  // ── REWRITE CHAPTER fixing continuity issues ─────────────────────────────────
  async function rewriteChapter(ch, idx, warningText) {
    setFixingChapter(ch.id); setError("");
    try {
      const bible = buildBible(config, characters, canonicalSummaries);
      const prevSums = chapterSummaries.slice(0, idx);
      const ctx = prevSums.length > 0
        ? `\n\nPREVIOUS CHAPTERS SUMMARY (IMMUTABLE — never contradict):\n${prevSums.map((s,j)=>`Ch.${j+1}: ${s}`).join("\n")}`
        : "";
      const chScenes = scenes[ch.id];
      const scenePlan = chScenes && chScenes.length > 0
        ? `\n\nSCENE PLAN:\n${chScenes.map((sc,si)=>`Scene ${si+1} — ${sc.titulo}\n  Opens: ${sc.hookEntrada||""}\n  Goal: ${sc.objetivo||""}\n  Ends: ${sc.hookSaida||""}`).join("\n\n")}`
        : "";
      const outlineCh = outline.find(o => o.id === ch.id) || ch;
      const prompt = `${ctx}\n\nCHAPTER ${ch.id}: "${outlineCh.titulo}"\nRole: ${outlineCh.papel||""} | Act: ${outlineCh.ato||""}\nSummary: ${outlineCh.resumo}\nPOV Goal: ${outlineCh.objetivo_pov||""}\nChange by end: ${outlineCh.mudanca||""}${scenePlan}\n\n⚠️ INCONSISTENCIES TO FIX IN THIS REWRITE (high priority):\n${warningText}\n\nRewrite this chapter correcting ALL listed inconsistencies while keeping the core plot events intact.\n\nMANDATORY OUTPUT RULES:\n- Pure narrative text only (no headings, no bullets, no meta commentary)\n- Keep coherent beginning, middle and ending (no abrupt cutoff)\n- Explicitly include the missing beats listed in inconsistencies\n- Avoid onomatopoeias in italics (e.g., *clang*, *tap*). Prefer natural prose descriptions.\n- 1000-1400 words in Brazilian Portuguese`;
      let content = await callAI([{role:"user",content:prompt}], bible, 0.3, 4200);
      if (!content || content.trim().length < 2500 || /\.\.\.\s*$/.test(content.trim())) {
        const retryPrompt = `${prompt}\n\nYour previous attempt was incomplete. Rewrite the FULL chapter again from scratch and ensure complete arc closure for this chapter.`;
        content = await callAI([{role:"user",content:retryPrompt}], bible, 0.2, 5000);
      }
      setChapters(prev => prev.map(c => c.id===ch.id ? {...c, content: sanitizeProseStyle(content), status:"done"} : c));
      // Update chapter summary for this chapter in the rolling context
      try {
        const newSummary = await callAI(
          [{role:"user",content:`Summarize Chapter ${ch.id} in 3 sentences for continuity tracking. Include: what happened, how each character changed, any new world facts established.\n\n${content}`}],
          "Summarize for narrative continuity. Be specific about character states, locations, and established facts.", 0.2
        );
        setChapterSummaries(prev => {
          const updated = [...prev];
          updated[idx] = newSummary.trim();
          return updated;
        });
      } catch {}
      setContinuityWarnings(p => ({...p, [ch.id]: null}));
      // Opção 1: verificar aderência ao roteiro automaticamente após correção
      checkChapterAdherence(ch, content);
    } catch(e) { setError(`Erro ao reescrever cap. ${ch.id}: ${e.message}`); }
    setFixingChapter(null);
  }

  // ── OPÇÃO 1: verificação de aderência por capítulo ───────────────────────────
  function buildAdherenceContentWindow(content) {
    const txt = String(content || "");
    if (!txt) return "";
    // Small chapters can be evaluated in full.
    if (txt.length <= 9000) return txt;
    // For long chapters, sample beginning, middle and end to avoid false negatives.
    const chunk = 3000;
    const start = txt.slice(0, chunk);
    const midStart = Math.max(0, Math.floor(txt.length / 2) - Math.floor(chunk / 2));
    const middle = txt.slice(midStart, midStart + chunk);
    const end = txt.slice(-chunk);
    return `[INÍCIO]\n${start}\n\n[MEIO]\n${middle}\n\n[FIM]\n${end}`;
  }

  function parseAdherenceResult(raw) {
    const txt = String(raw || "").trim();
    if (!txt) return { ok: false, detail: "Validação vazia." };

    try {
      const parsed = safeParseAIJson(txt);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const ok = !!parsed.ok;
        const detail = String(parsed.detail || parsed.desvios || parsed.issue || "").trim();
        return { ok, detail: ok ? "" : (detail || txt) };
      }
    } catch {}

    const upper = txt.toUpperCase();
    const startsOk = /^OK\b/.test(upper);
    const endsOk = /\bOK\s*$/.test(upper);
    const hasDeviationMarkers = /(DESVIO|DESVIOS|N[ÃA]O INCLUI|FALTA|AUS[EÊ]NCIA|INCONSIST[ÊE]NCIA)/i.test(txt);
    if ((startsOk || endsOk) && !hasDeviationMarkers) return { ok: true, detail: "" };
    if (/^OK\b[\s.:-]*$/i.test(txt)) return { ok: true, detail: "" };
    return { ok: false, detail: txt };
  }

  async function checkChapterAdherence(ch, content) {
    const outlineCh = outline.find(o => o.id === ch.id);
    if (!outlineCh) return;
    const chapterWindow = buildAdherenceContentWindow(content);
    try {
      const result = await callAI(
        [{role:"user", content:
          `Compare este capítulo com sua entrada no roteiro.

Retorne APENAS JSON válido com este formato:
{"ok": true, "detail": ""}
ou
{"ok": false, "detail": "lista curta de desvios específicos em português (máx 120 palavras)"}

REGRAS DE AVALIAÇÃO:
- Não declare ausência de evento sem conferir os trechos [INÍCIO], [MEIO] e [FIM].
- Considere equivalências narrativas (mesma função dramática com redação diferente).
- Foque em desvios reais de estrutura e conteúdo, não em estilo.

ROTEIRO:
Título: ${outlineCh.titulo}
Função: ${outlineCh.papel}
Resumo: ${outlineCh.resumo}
Objetivo POV: ${outlineCh.objetivo_pov||""}
Mudança esperada: ${outlineCh.mudanca||""}

TEXTO DO CAPÍTULO:
${chapterWindow}`
        }],
        aiLang + " Editor de continuidade. Verifique aderência ao roteiro com precisão estrutural. Responda em JSON válido.", 0.1, 1200
      );
      const verdict = parseAdherenceResult(result);
      setOutlineAdherence(prev => ({...prev, [ch.id]: {ok: verdict.ok, detail: verdict.ok ? "" : verdict.detail}}));
    } catch {}
  }

  // ── OPÇÃO 2+3: análise completa do livro ─────────────────────────────────────
  async function analyzeFullBook() {
    setAnalyzingBook(true); setError(""); setBookAnalysis(null);
    try {
      const doneChaps = chapters.filter(c => c.status === "done" && c.content);
      if (doneChaps.length === 0) { setAnalyzingBook(false); return; }

      // Build character death/state tracking for consistency check
      const charNames = characters.map(c => c.nome || c.name).filter(Boolean);
      const charDeaths = {}; // track first death mention per character
      doneChaps.forEach(c => {
        charNames.forEach(name => {
          if (!charDeaths[name]) {
            const deathMatch = c.content.match(new RegExp(`${name}.{0,60}(morreu|morre|mort[ao]|faleceu|agoniz|último suspiro|seus olhos.{0,30}vítreos)`, 'i'))
                           || c.content.match(new RegExp(`(morreu|morte de|faleceu).{0,30}${name}`, 'i'));
            if (deathMatch) charDeaths[name] = c.id;
          }
        });
      });

      // Build chapter block with full text (first 800 chars) + metadata
      const chapterBlock = doneChaps.map(c => {
        const o = outline.find(o => o.id === c.id);
        return `=== CAPÍTULO ${c.id} (Ato ${o?.ato||"?"} · ${o?.papel||""}) ===\nROTEIRO: ${o?.resumo||""}\nOBJETIVO POV: ${o?.objetivo_pov||""}\nMUDANÇA ESPERADA: ${o?.mudanca||""}\nTEXTO:\n${c.content.slice(0,800)}`;
      }).join("\n\n");

      // Summary of known deaths for consistency check
      const deathSummary = Object.entries(charDeaths).length > 0
        ? `\nMORTES CONFIRMADAS (personagem → capítulo da morte): ${Object.entries(charDeaths).map(([n,id])=>`${n} (Cap.${id})`).join(", ")}`
        : "";

      // Character names from bible
      const charList = charNames.length > 0 ? `\nPERSONAGENS DA BÍBLIA: ${charNames.join(", ")}` : "";

      const prompt = `Você é um editor literário brasileiro especialista em análise narrativa e continuidade. Analise os capítulos do livro em relação ao roteiro, estrutura dos três atos E inconsistências internas. Retorne APENAS JSON puro em português brasileiro:

{
  "ato1": {"status":"ok|issues","detail":"avaliação do Ato 1: apresentação e incidente incitante"},
  "ato2": {"status":"ok|issues","detail":"avaliação do Ato 2: escalada, ponto médio e crise"},
  "ato3": {"status":"ok|issues","detail":"avaliação do Ato 3: clímax e resolução"},
  "overall": "avaliação geral em 2-3 frases",
  "chapters": [{"id":1,"ok":true,"issue":"vazio se ok, caso contrário desvio do roteiro"}],
  "inconsistencias": [
    {
      "gravidade": "critica|grave|media|menor",
      "tipo": "personagem_morto_vivo|nome_inconsistente|lado_corporal|palavra_estrangeira|local_sem_transicao|personagem_desaparecido|continuidade",
      "capitulos": [12, 19],
      "descricao": "descrição clara da inconsistência em português"
    }
  ]
}

REGRAS DE ANÁLISE DE INCONSISTÊNCIAS — verifique obrigatoriamente:
1. PERSONAGEM MORTO RESSUSCITANDO: se um personagem morreu (olhos vítreos, confirmação de morte, funeral), ele NÃO pode aparecer fisicamente vivo em capítulos posteriores (memórias e visões são permitidas, mas devem ser explícitas)
2. PALAVRAS ESTRANGEIRAS: qualquer palavra em inglês, espanhol ou outro idioma no meio de texto em português é inconsistência (ex: "raw", "confession", "steel", "dark", "the")
3. LADO CORPORAL: se a marca/ferimento está no pulso DIREITO no cap.1, deve permanecer no pulso DIREITO em todos os capítulos
4. NOME DE PERSONAGEM: verifique se o mesmo personagem é chamado por grafias diferentes (ex: "Lyra" vs "Lira", "Kael" vs "Kaël")
5. LOCAL SEM TRANSIÇÃO: se um personagem está na forja no cap.N e num abrigo no cap.N+1 sem explicação de deslocamento
6. PERSONAGEM DESAPARECIDO: personagem ativo que some por 3+ capítulos sem explicação narrativa
7. CONTINUIDADE DE ESTADO: ferimentos que curam rápido demais, locais destruídos que reaparecem intactos${deathSummary}${charList}

CAPÍTULOS:\n${chapterBlock}`;

      const result = await callAI(
        [{role:"user", content: prompt}],
        aiLang + " Editor literário brasileiro especialista em estrutura narrativa e continuidade. Escreva SEMPRE em português brasileiro. Retorne APENAS JSON puro sem markdown.", 0.2,
        Math.max(4000, doneChaps.length * 250)
      );
      const parsed = safeParseAIJson(result);
      // Update per-chapter adherence from the full analysis
      if (parsed.chapters && Array.isArray(parsed.chapters)) {
        const newAdherence = {};
        parsed.chapters.forEach(c => {
          newAdherence[c.id] = { ok: !!c.ok, detail: c.issue || "" };
        });
        setOutlineAdherence(prev => ({...prev, ...newAdherence}));
      }
      setBookAnalysis(parsed);
      setShowBookAnalysis(true);
    } catch(e) { setError(`Erro na análise: ${e.message}`); }
    setAnalyzingBook(false);
  }

  // ── WRITING ─────────────────────────────────────────────────────────────────
  async function writeBatch(startIdx, seedChapters = null, seedSummaries = null) {
    const safeStart = Math.max(0, startIdx || 0);
    const batchSize = Math.max(2, Math.min(4, writingBatchSize || 3));
    const endIdx = Math.min(safeStart + batchSize, outline.length);
    if (safeStart >= endIdx) { setPhase("done"); return; }

    const resolvedTitle = (config.bookTitles && config.bookTitles[currentBookIdx - 1]) || config.title;
    const configForBible = resolvedTitle !== config.title ? { ...config, title: resolvedTitle } : config;
    const bible = buildBible(configForBible, characters, canonicalSummaries);
    let accSum = seedSummaries ? [...seedSummaries] : (chapterSummaries.length > 0 ? [...chapterSummaries] : []);
    let finalChapters = seedChapters && seedChapters.length === outline.length
      ? seedChapters.map(c => ({...c}))
      : (chapters.length === outline.length
        ? chapters.map(c => ({...c}))
        : outline.map(o => ({id:o.id, title:`Capítulo ${o.id}: ${o.titulo}`, content:"", status:"pending"})));

    for (let i=safeStart; i<endIdx; i++) {
      if (abortRef.current) break;
      const ch = outline[i];
      setChapters(prev => prev.map(c => c.id===ch.id ? {...c,status:"writing"} : c));
      const ctx = accSum.length > 0
        ? `\n\nPREVIOUS CHAPTERS SUMMARY (do NOT contradict these events):\n${accSum.map((s,j) => `Ch.${j+1}: ${s}`).join("\n")}`
        : "";
      const chScenes = scenes[ch.id];
      const scenePlan = chScenes && chScenes.length > 0
        ? `\n\nSCENE PLAN (follow this structure):\n${chScenes.map((sc,si)=>`Scene ${si+1} — ${sc.titulo}\n  Opens with: ${sc.hookEntrada||""}\n  POV Goal: ${sc.objetivo||""}\n  Subplot: ${sc.subtrama||"none"}\n  Tension: ${sc.tensao||""}\n  Ends with: ${sc.hookSaida||""}\n  Change: ${sc.mudanca||""}`).join("\n\n")}`
        : "";
      const prompt = `${ctx}\n\nCHAPTER ${ch.id} of ${outline.length}: "${ch.titulo}"\nStructural role: ${ch.papel||""} | Act: ${ch.ato||""}\nSummary: ${ch.resumo}\nPOV Goal: ${ch.objetivo_pov||""}\nChange by end: ${ch.mudanca||""}${scenePlan}\n\n${i===0 ? "This is the OPENING chapter: establish the world, introduce protagonist compellingly, plant the central question." : ""}\n${i===outline.length-1 ? "This is the FINAL CHAPTER: resolve all character arcs and plot threads of this book with emotional satisfaction." : ""}\n\nWRITING CHECKLIST:\n- Every character speaks/acts consistently with their defined voice and wound\n- Events do not contradict the established bible or previous chapters\n- The scene hooks (entry/exit) create forward momentum\n- Show emotion through action and subtext, not direct statement\n- Theme is present but never heavy-handed\n- Avoid onomatopoeias in italics (e.g., *clang*, *tap*). Prefer natural prose descriptions\n\nWrite the complete chapter (~1000-1200 words). Pure narrative text only — no chapter headings.`;
      try {
        const content = await callAI([{role:"user",content:prompt}], bible, 0.35);
        let summary = ch.resumo;
        try {
          summary = await callAI(
            [{role:"user",content:`Summarize Chapter ${ch.id} in 3 sentences for continuity tracking. Include: what happened, how each character changed, and any new world facts established.\n\n${content}`}],
            aiLang + " Summarize for narrative continuity. Be specific about character states, plot events, and established facts.", 0.2
          );
        } catch {}
        accSum.push(summary.trim());
        setChapterSummaries([...accSum]);
        const updatedChap = {...finalChapters[i], status:"done", content: sanitizeProseStyle(content), title:`Capítulo ${ch.id}: ${ch.titulo}`};
        finalChapters[i] = updatedChap;
        setChapters(prev => prev.map(c => c.id===ch.id ? updatedChap : c));
        setOpenChapter(ch.id);
        const prevDone = finalChapters.slice(0, i).filter(c => c.status === "done" && c.content);
        if (prevDone.length > 0) checkContinuity(ch.id, content, prevDone);
        checkChapterAdherence(ch, content);
      } catch(e) {
        setChapters(prev => prev.map(c => c.id===ch.id ? {...c,status:"error",content:`Error: ${e.message}`} : c));
        setError(`Error in chapter ${ch.id}: ${e.message}`);
        break;
      }
    }

    if (abortRef.current) return;
    setNextChapterToWrite(endIdx);
    if (endIdx >= outline.length) setPhase("done");
  }

  async function startWriting() {
    abortRef.current = false;
    const initial = outline.map(o => ({id:o.id, title:`Capítulo ${o.id}: ${o.titulo}`, content:"", status:"pending"}));
    setChapters(initial);
    setPhase("writing");
    setError("");
    setOpenChapter(null);
    setChapterSummaries([]);
    setContinuityWarnings({});
    setOutlineAdherence({});
    setNextChapterToWrite(0);
    await writeBatch(0, initial, []);
  }

  async function continueWritingBatch() {
    if (nextChapterToWrite >= outline.length) return;
    if (chapters.some(c => c.status === "writing")) return;
    abortRef.current = false;
    setError("");
    await writeBatch(nextChapterToWrite);
  }

  // ── START NEXT BOOK — shows transition screen ─────────────────────────────────
  async function startNextBook() {
    setLoading(true); setLoadingMsg("Gerando resumo canônico do Livro " + currentBookIdx + "..."); setError("");
    try {
      const summary = await generateCanonicalSummary(chapters);
      const newSummaries = [...canonicalSummaries, summary];
      const nextIdx = currentBookIdx + 1;
      const nextTitle = (config.bookTitles && config.bookTitles[nextIdx - 1]) || "";
      setTransitionData({ nextIdx, newSummaries, nextTitle });
      setEditableTransitionSummary(summary);
      setNextBookNotes("");
      setShowBookTransition(true);
    } catch(e) { setError(e.message); }
    setLoading(false); setLoadingMsg("");
  }

  // ── PROCEED TO NEXT BOOK — called from transition screen ──────────────────────
  async function proceedToNextBook() {
    if (!transitionData) return;
    const { nextIdx, newSummaries, nextTitle } = transitionData;
    // Use the (possibly edited) summary as the canonical record
    const finalSummaries = [...newSummaries.slice(0, -1), editableTransitionSummary];
    setShowBookTransition(false);
    setCanonicalSummaries(finalSummaries);
    setCurrentBookIdx(nextIdx);
    setChapterSummaries([]);      // reset rolling context for the new book
    setContinuityWarnings({});
    setOutline([]);
    setOutlineNextToGenerate(0);
    setOutlinePlanWarnings({});
    setOutlineAuthorNotes("");
    setChapters([]);
    setScenes({});
    setOpenChapter(null);
    setEditingOutline(false);
    // Set config.title to the next book's specific title if defined
    const updatedConfig = nextTitle ? { ...config, title: nextTitle } : config;
    if (nextTitle) setConfig(updatedConfig);
    await generateOutline(finalSummaries, nextIdx, nextBookNotes, updatedConfig);
  }

  // ── SCENE PLANNING ──────────────────────────────────────────────────────────
  function normalizeSceneTension(value, fallback = "medium") {
    const v = String(value || "").trim().toLowerCase();
    if (v === "high" || v === "alta" || v === "alta tensão" || v === "alta tensao") return "high";
    if (v === "medium" || v === "media" || v === "média" || v === "média tensão" || v === "media tensão" || v === "media tensao") return "medium";
    if (v === "low" || v === "baixa" || v === "baixa tensão" || v === "baixa tensao") return "low";
    return fallback;
  }

  function applyCanonSceneTextTweaks(chId, sc) {
    const fixed = { ...sc };
    const keys = ["titulo", "objetivo", "mudanca", "hookEntrada", "hookSaida", "subtrama"];

    // 1) Late-book destination consistency: by chapter 24 they should not be heading to Helvar.
    if (chId === 24) {
      keys.forEach(k => {
        fixed[k] = String(fixed[k] || "")
          .replace(/\bPara Helvar\b/g, "Para o norte")
          .replace(/\bpara Helvar\b/g, "para o norte");
      });
    }

    // 2) Keep Isa's Book 1 control below full mastery language.
    if (chId === 23) {
      keys.forEach(k => {
        fixed[k] = String(fixed[k] || "")
          .replace(/fios de sombra controlada/gi, "lampejos de sombra instintivos e custosos")
          .replace(/costurar as bordas rasgadas da realidade/gi, "conter temporariamente as bordas instáveis da distorção");
      });
    }

    // 3) Avoid Garan-name continuity confusion inside Helvar political scene.
    if (chId === 11) {
      keys.forEach(k => {
        fixed[k] = String(fixed[k] || "")
          .replace(/\bEste é Garan\b/g, "Este é Daren")
          .replace(/\bGaran\b/g, "Daren");
      });
    }

    return fixed;
  }

  function buildFallbackScenesFromOutlineChapter(ch) {
    const title = String(ch?.titulo || `Capítulo ${ch?.id || ""}`).trim();
    const resumo = String(ch?.resumo || "").trim();
    const objetivo = String(ch?.objetivo_pov || "").trim();
    const mudanca = String(ch?.mudanca || "").trim();
    const resumoCurto = resumo ? `${resumo.slice(0, 180)}${resumo.length > 180 ? "..." : ""}` : "A tensão cresce e força decisões difíceis.";

    return [
      {
        titulo: `Abertura de ${title}`,
        objetivo: objetivo || `Estabelecer o conflito central de ${title}.`,
        mudanca: "A situação inicial perde estabilidade e exige ação.",
        hookEntrada: `No início de ${title}, o equilíbrio parece frágil e prestes a ruir.`,
        hookSaida: `Um sinal inesperado muda a prioridade imediata do capítulo.`,
        tensao: "medium",
        subtrama: "Relação entre Kael e Isa",
      },
      {
        titulo: `Confronto em ${title}`,
        objetivo: "Enfrentar o principal obstáculo com custo real e escolhas difíceis.",
        mudanca: "O confronto revela um preço claro e amplia o risco.",
        hookEntrada: `Com a pressão aumentando, uma decisão urgente leva ao confronto.`,
        hookSaida: `A vitória parcial deixa uma nova ameaça mais próxima do que antes.`,
        tensao: "high",
        subtrama: "Custo do poder e limites pessoais",
      },
      {
        titulo: `Consequência de ${title}`,
        objetivo: "Reorganizar o grupo após o confronto e definir o próximo passo.",
        mudanca: mudanca || "O capítulo termina com direção nova e tensão para o próximo.",
        hookEntrada: `No pós-confronto, o grupo precisa interpretar as consequências imediatas.`,
        hookSaida: resumoCurto,
        tensao: "medium",
        subtrama: "Legado da mãe e continuidade do arco",
      },
    ];
  }

  function sceneHasCanonLeakBook1(chId, sc) {
    const blob = [
      sc?.titulo || "",
      sc?.objetivo || "",
      sc?.mudanca || "",
      sc?.hookEntrada || "",
      sc?.hookSaida || "",
      sc?.subtrama || "",
    ].join("\n");
    if (/(Ignathor|Thalgrund|Neradrys|Zephyros)/i.test(blob)) return true;
    if (/cinco\s+vozes|cinco\s+l[úu]mens|m[úu]ltiplos?\s+l[úu]mens/i.test(blob)) return true;
    if (/Umbryth[^.\n]{0,40}(sussurr|fal|disse)|Solarys[^.\n]{0,40}(sussurr|fal|disse)/i.test(blob)) return true;
    if (chId >= 11 && /\bMira\b/i.test(blob)) return true;
    return false;
  }

  function sceneLooksFallbackOrTruncated(sc) {
    const title = String(sc?.titulo || "").trim();
    const entry = String(sc?.hookEntrada || "").trim();
    const exit = String(sc?.hookSaida || "").trim();
    const goal = String(sc?.objetivo || "").trim();
    const change = String(sc?.mudanca || "").trim();
    const subplot = String(sc?.subtrama || "").trim();
    const blob = [title, entry, exit, goal, change, subplot].join("\n");

    if (/^Confronto em /i.test(title) || /^Consequ[eê]ncia de /i.test(title)) return true;
    if (/Com a pressão aumentando, uma decisão urgente leva ao confronto\./i.test(entry)) return true;
    if (/No pós-confronto, o grupo precisa interpretar as consequências imediatas\./i.test(entry)) return true;
    if (/A vitória parcial deixa uma nova ameaça mais próxima do que antes\./i.test(exit)) return true;
    if (/^\.\.\.$/.test(entry) || /^\.\.\.$/.test(exit)) return true;
    if (/\.\.\./.test(blob) && blob.length < 500) return true;
    if (!goal || !change || !entry || !exit) return true;
    return false;
  }

  function collectFallbackSceneChapterIds(sourceScenes) {
    const ids = [];
    for (const ch of outline) {
      const list = sourceScenes?.[ch.id];
      if (!Array.isArray(list) || list.length < 3) { ids.push(ch.id); continue; }
      if (list.some(sceneLooksFallbackOrTruncated)) ids.push(ch.id);
    }
    return ids;
  }

  function buildAlignedScenesMap(sourceScenes) {
    let changed = false;
    const next = { ...sourceScenes };
    for (const ch of outline) {
      const currentList = sourceScenes[ch.id];
      let normalized = normalizeSceneList(ch, currentList);
      if (currentBookIdx === 1) {
        const fallback = buildFallbackScenesFromOutlineChapter(ch);
        normalized = normalized.map((sc, idx) => (
          sceneHasCanonLeakBook1(ch.id, sc) ? fallback[idx] : sc
        ));
      }
      normalized[2] = {
        ...normalized[2],
        mudanca: String(ch.mudanca || normalized[2]?.mudanca || "").trim(),
      };
      const oldStr = JSON.stringify(Array.isArray(currentList) ? currentList : []);
      const newStr = JSON.stringify(normalized);
      if (oldStr !== newStr) changed = true;
      next[ch.id] = normalized;
    }
    return { next, changed };
  }

  function normalizeSceneList(ch, rawList) {
    const fallback = buildFallbackScenesFromOutlineChapter(ch);
    if (!Array.isArray(rawList) || rawList.length === 0) return fallback;

    const cleaned = rawList.slice(0, 3).map((sc, idx) => {
      const base = fallback[idx] || fallback[fallback.length - 1];
      const titulo = String(sc?.titulo || "").trim() || base.titulo;
      const objetivo = String(sc?.objetivo || "").trim() || base.objetivo;
      const mudanca = String(sc?.mudanca || "").trim() || (idx === 2 ? (String(ch?.mudanca || "").trim() || base.mudanca) : base.mudanca);
      const hookEntrada = String(sc?.hookEntrada || "").trim() || base.hookEntrada;
      const hookSaida = String(sc?.hookSaida || "").trim() || base.hookSaida;
      const subtrama = String(sc?.subtrama || "").trim() || base.subtrama;
      const tensao = normalizeSceneTension(sc?.tensao, base.tensao);
      return applyCanonSceneTextTweaks(ch?.id, { titulo, objetivo, mudanca, hookEntrada, hookSaida, tensao, subtrama });
    });

    while (cleaned.length < 3) {
      cleaned.push(applyCanonSceneTextTweaks(ch?.id, fallback[cleaned.length]));
    }
    return cleaned;
  }

  function buildSceneCanonRules(ch) {
    const base = `Regras obrigatórias para as cenas:
- Idioma: português do Brasil.
- Retorne EXATAMENTE 3 cenas em JSON array válido.
- Cada cena deve respeitar estritamente: resumo, objetivo POV e mudança do capítulo.
- Não contradizer eventos já definidos do roteiro.
- Não introduzir personagens novos importantes sem necessidade.
- Não repetir o mesmo beat duas vezes no mesmo capítulo.`;
    if (currentBookIdx !== 1) return base;
    return `${base}
- Escopo: APENAS Livro 1.
- Kael: vínculo ativo principal é Aetheris; outros Lendários no máximo como ecos/sensações, sem domínio pleno.
- Isa: sem vínculo pleno com Umbryth/Solarys no Livro 1; apenas lampejos/instinto/intuição com custo alto.
- Não antecipar resolução de Livro 2/3.
- Não resolver o conflito da série neste livro.`;
  }

  function applySceneCanonBook1(opts = {}) {
    const { silent = false } = opts;
    if (currentBookIdx !== 1 || !outline.length) return;
    const { next, changed } = buildAlignedScenesMap(scenes);
    if (changed) {
      setScenes(next);
      if (!silent) setError("Cenas alinhadas ao roteiro canônico do Livro 1.");
    } else {
      if (!silent) setError("As cenas já estão alinhadas ao roteiro canônico do Livro 1.");
    }
  }

  function applyChapterCanonBook1Pass() {
    if (currentBookIdx !== 1) return 0;
    let changes = 0;
    setChapters(prev => prev.map(ch => {
      if (!ch?.content) return ch;
      const normalizedTitle = String(ch?.title || "")
        .replace(/\bElyra\b/gi, "Elara")
        .replace(/\bAlthea\b/gi, "Elara");
      const normalizedContent = sanitizeProseStyle(ch.content);
      if (normalizedTitle === ch.title && normalizedContent === ch.content) return ch;
      changes += 1;
      return { ...ch, title: normalizedTitle, content: normalizedContent };
    }));
    return changes;
  }

  async function applyBook1FinalAlignment() {
    if (currentBookIdx !== 1) {
      setError("Este alinhamento final é específico para o Livro 1.");
      return;
    }
    setError("");
    applyCanonPatchBook1({ silent: true });
    applySceneCanonBook1({ silent: true });
    const chapterChanges = applyChapterCanonBook1Pass();
    await repairFallbackScenesBook1({ silent: true });
    setError(chapterChanges > 0
      ? `Alinhamento final do Livro 1 aplicado. ${chapterChanges} capítulo(s) foram normalizados.`
      : "Alinhamento final do Livro 1 aplicado.");
  }

  async function generateScenesForChapter(ch, idx) {
    setGeneratingScenes(true);
    try {
      const bible = buildBible(config, characters, canonicalSummaries);
      const sceneRules = buildSceneCanonRules(ch);
      const result = await callAI(
        [{ role:"user", content: `Planeje as cenas deste capítulo e retorne SOMENTE JSON array válido.\n\nCapítulo ${ch.id}: "${ch.titulo}"\nResumo: ${ch.resumo}\nObjetivo POV: ${ch.objetivo_pov||""}\nMudança ao final: ${ch.mudanca||""}\nPapel estrutural: ${ch.papel||""}\n\n${sceneRules}\n\nFormato obrigatório por cena:\n{"titulo":"...","objetivo":"...","mudanca":"...","hookEntrada":"...","hookSaida":"...","tensao":"low|medium|high","subtrama":"..."}` }],
        bible + `\n\nPlaneje cenas objetivas, progressivas e sem contradições canônicas.`, 0.35
      );
      const sceneList = safeParseAIJson(result);
      const normalized = normalizeSceneList(ch, sceneList);
      setScenes(prev => ({...prev, [ch.id]: normalized}));
    } catch(e) { setError(`Erro ao planejar cenas do cap. ${ch.id}: ${e.message}`); }
    setGeneratingScenes(false);
  }

  async function generateAllScenes() {
    setGeneratingScenes(true); setError("");
    const bible = buildBible(config, characters, canonicalSummaries);
    for (const ch of outline) {
      if (abortRef.current) break;
      try {
        const sceneRules = buildSceneCanonRules(ch);
        const result = await callAI(
          [{ role:"user", content: `Planeje as cenas deste capítulo e retorne SOMENTE JSON array válido.\n\nCapítulo ${ch.id}: "${ch.titulo}"\nResumo: ${ch.resumo}\nObjetivo POV: ${ch.objetivo_pov||""}\nMudança ao final: ${ch.mudanca||""}\nPapel estrutural: ${ch.papel||""}\n\n${sceneRules}\n\nFormato obrigatório por cena:\n{"titulo":"...","objetivo":"...","mudanca":"...","hookEntrada":"...","hookSaida":"...","tensao":"low|medium|high","subtrama":"..."}` }],
          bible, 0.35
        );
        const sceneList = safeParseAIJson(result);
        const normalized = normalizeSceneList(ch, sceneList);
        setScenes(prev => ({...prev, [ch.id]: normalized}));
      } catch{}
    }
    setGeneratingScenes(false);
  }

  async function repairFallbackScenesBook1(opts = {}) {
    const { silent = false } = opts;
    if (currentBookIdx !== 1 || !outline.length) return;
    const targetIds = collectFallbackSceneChapterIds(scenes);
    if (!targetIds.length) {
      if (!silent) setError("Nenhuma cena com fallback detectada.");
      return;
    }

    setGeneratingScenes(true);
    if (!silent) setError("");
    const bible = buildBible(config, characters, canonicalSummaries);
    let repaired = 0;

    for (const id of targetIds) {
      if (abortRef.current) break;
      const ch = outline.find(o => o.id === id);
      if (!ch) continue;
      try {
        const existing = Array.isArray(scenes[id]) ? scenes[id] : [];
        const existingCtx = existing.length
          ? `\n\nCenas atuais (ruins/incompletas, reescrever do zero):\n${existing.map((sc, i) => (
              `Cena ${i + 1}\nTítulo: ${sc.titulo || ""}\nObjetivo: ${sc.objetivo || ""}\nMudança: ${sc.mudanca || ""}\nEntrada: ${sc.hookEntrada || ""}\nSaída: ${sc.hookSaida || ""}\nSubtrama: ${sc.subtrama || ""}\nTensão: ${sc.tensao || ""}`
            )).join("\n\n")}`
          : "";
        const sceneRules = buildSceneCanonRules(ch);
        const result = await callAI(
          [{
            role: "user",
            content: `Reescreva COMPLETAMENTE o planejamento de cenas deste capítulo e retorne SOMENTE JSON array válido.\n\nCapítulo ${ch.id}: "${ch.titulo}"\nResumo: ${ch.resumo}\nObjetivo POV: ${ch.objetivo_pov || ""}\nMudança ao final: ${ch.mudanca || ""}\nPapel estrutural: ${ch.papel || ""}${existingCtx}\n\n${sceneRules}\n\nRequisitos adicionais:\n- EXATAMENTE 3 cenas\n- Proibido usar títulos genéricos como "Confronto em X" ou "Consequência de X"\n- Proibido usar placeholders e reticências de truncamento ("...")\n- Cada cena deve ter conteúdo concreto e específico\n- A cena 3 deve desembocar claramente na mudança final do capítulo\n\nFormato obrigatório por cena:\n{"titulo":"...","objetivo":"...","mudanca":"...","hookEntrada":"...","hookSaida":"...","tensao":"low|medium|high","subtrama":"..."}` 
          }],
          bible + "\n\nVocê é um editor estrutural rigoroso. Produza cenas concretas, específicas e canônicas.",
          0.25
        );
        const parsed = safeParseAIJson(result);
        const normalized = normalizeSceneList(ch, parsed);
        setScenes(prev => ({ ...prev, [ch.id]: normalized }));
        repaired += 1;
      } catch {
        // keep going so one chapter failure doesn't block whole repair batch
      }
    }

    setGeneratingScenes(false);
    if (!silent) {
      if (repaired > 0) setError(`Cenas reparadas em ${repaired} capítulo(s).`);
      else setError("Não foi possível reparar automaticamente as cenas com fallback.");
    }
  }

  // ── PANEL SECTIONS (shared between left panel and bible panel) ───────────────
  const PANEL_SECTIONS = [
    { label:"📖 Fundação", icon:"📖", fields:[
      {key:"title",            label: (config.numBooks||1) > 1 ? "Título da Série / Saga" : "Título"},
      {key:"genre",            label:"Gênero"},
      {key:"tone",             label:"Tom / Atmosfera"},
      {key:"pov",              label:"Ponto de Vista"},
      {key:"synopsis",         label:"Sinopse"},
    ]},
    { label:"🧑‍🤝‍🧑 Personagens", icon:"🧑‍🤝‍🧑", fields:[
      {key:"protagonist",      label:"Protagonista(s)"},
      {key:"protagonistWound", label:"Ferida Emocional"},
      {key:"protagonistDesire",label:"Desejo (o que quer)"},
      {key:"protagonistNeed",  label:"Necessidade (o que precisa)"},
      {key:"protagonistVoice", label:"Voz / Como fala"},
      {key:"protagonistArc",   label:"Arco de Transformação"},
      {key:"antagonist",       label:"Antagonista(s)"},
      {key:"antagonistMotive", label:"Motivação do Antagonista"},
      {key:"antagonistArc",    label:"Arco do Antagonista"},
      {key:"supportChars",     label:"Personagens de Apoio"},
    ]},
    { label:"🌍 Mundo", icon:"🌍", fields:[
      {key:"setting",          label:"Cenário"},
      {key:"worldRules",       label:"Regras / Magia / Leis"},
      {key:"keyLocations",     label:"Locais-Chave"},
      {key:"factions",         label:"Facções / Organizações"},
      {key:"significantItems", label:"Itens / Objetos Significativos"},
      {key:"worldHistory",     label:"História do Mundo"},
      {key:"sensoryAtmosphere",label:"Atmosfera Sensorial"},
    ]},
    { label:"⚔️ Narrativa", icon:"⚔️", fields:[
      {key:"conflict",         label:"Conflito Principal"},
      {key:"stakes",           label:"O que está em Jogo"},
      {key:"incitingIncident", label:"Incidente Incitante"},
      {key:"themes",           label:"Temas Centrais"},
      {key:"subplots",         label:"Subtramas"},
    ]},
    { label:"📐 Estrutura do Plot", icon:"📐", fields:[
      {key:"plotPoint1",       label:"Plot Point 1 (ponto sem retorno)"},
      {key:"midpoint",         label:"Ponto Médio (virada do Ato 2)"},
      {key:"plotPoint2",       label:"Plot Point 2 (crise máxima)"},
      {key:"climax",           label:"Clímax"},
      {key:"resolution",       label:"Resolução"},
    ]},
    { label:"✍️ Estilo", icon:"✍️", fields:[
      {key:"writingStyle",     label:"Estilo de Escrita"},
      {key:"referenceAuthors", label:"Autores de Referência"},
      {key:"influences",       label:"Influências Literárias"},
      {key:"prosePace",        label:"Ritmo da Prosa"},
      {key:"stylisticAvoid",   label:"O que Evitar"},
    ]},
    { label:"📚 Série", icon:"📚", fields:[
      {key:"seriesArc",        label:"Arco da Série"},
      {key:"bookResolution",   label:"O que se Resolve Neste Livro"},
      {key:"nextHooks",        label:"Ganchos para o Próximo Livro"},
    ]},
  ];

  // ── BIBLE TIPS (tooltips de exemplo por campo) ───────────────────────────────
  const BIBLE_TIPS = {
    title:             (config.numBooks||1) > 1 ? "Nome da saga/série inteira. Ex: 'O Sussurro do Fio Partido'. Cada livro tem seu próprio título definido na configuração do projeto (⚙️ Config)." : "Ex: 'O Acorde de Sangue' — evocativo, com duplo sentido se possível",
    genre:             "Ex: Fantasy épico, Thriller psicológico, Romance histórico",
    tone:              "Ex: Sombrio e melancólico, com lampejos de esperança amarga",
    pov:               "Ex: 3ª pessoa limitada — o leitor sabe apenas o que o protagonista sabe",
    synopsis:          "Ex: Uma ex-assassina é forçada a proteger o filho do homem que matou",
    protagonist:       "Ex: Kael, 28 anos, soldado desertor com dom para ouvir memórias de objetos",
    protagonistWound:  "Ex: Abandonou o irmão durante uma emboscada para salvar a si mesmo",
    protagonistDesire: "Ex: Quer encontrar o artefato que apagaria suas memórias de guerra",
    protagonistNeed:   "Ex: Precisa aceitar que não pode mudar o passado — só responder a ele",
    protagonistVoice:  "Ex: Fala pouco, frases curtas. Usa humor seco para desviar de emoção",
    protagonistArc:    "Ex: De fugitivo da culpa para alguém que escolhe ser responsável",
    antagonist:        "Ex: Mestra Vael, fundadora da ordem — acredita ser justa e necessária",
    antagonistMotive:  "Ex: Quer destruir o artefato para que ninguém mais sofra pelo poder dele",
    antagonistArc:     "Ex: Começa convicta, vai revelando dúvidas, termina como espelho do herói",
    supportChars:      "Ex: Suri (aliada cômica, esconde trauma), Dren (mentor morto, aparece em visões)",
    setting:           "Ex: Império decadente pós-guerra mágica, cidades flutuantes sobre ruínas",
    worldRules:        "Ex: Magia consome memórias — cada feitiço apaga um fragmento de quem você é",
    keyLocations:      "Ex: Torre do Silêncio (prisão), Planícies Cinzas (campo de batalha antigo)",
    factions:          "Ex: Ordem da Chama (governo mágico), Os Sem-Voz (resistência dos apagados)",
    significantItems:  "Ex: Fragmento de Âmbar — cristal que armazena a última memória de sua mãe",
    worldHistory:      "Ex: 200 anos atrás, a Grande Ruptura apagou metade da população. Causa: desconhecida",
    sensoryAtmosphere: "Ex: Cheira a pedra úmida e incenso. Luz sempre filtrada. Sons de sinos distantes",
    conflict:          "Ex: Kael deve entregar o artefato à Ordem ou destruí-lo — ambas têm custo devastador",
    stakes:            "Ex: Se a Ordem usar o artefato, apagam a consciência de 10.000 rebeldes",
    incitingIncident:  "Ex: O artefato se funde à mão de Kael num roubo — ele não consegue se separar",
    themes:            "Ex: Responsabilidade vs. fuga; custo da excepcionalidade; memória como identidade",
    subplots:          "Ex: Suri foi enviada pela Ordem para vigiá-lo; romance proibido com a arquivista",
    plotPoint1:        "Ex: Kael descobre que a Ordem quer usar o artefato NELE — ele é a chave",
    midpoint:          "Ex: Kael usa o artefato pela primeira vez — funciona, mas apaga o rosto da mãe",
    plotPoint2:        "Ex: Suri é capturada. Kael tem que invadir a Torre onde tudo que perdeu está guardado",
    climax:            "Ex: Kael enfrenta Vael dentro do artefato — no espaço das memórias coletivas",
    resolution:        "Ex: O artefato é selado. Kael fica sem 40% das memórias, mas escolheu quem ser",
    writingStyle:      "Ex: Prosa lírica com ritmo tenso. Frases curtas em ação. Metáforas de luz e névoa",
    referenceAuthors:  "Ex: Patrick Rothfuss (worldbuilding), Kazuo Ishiguro (subtexto), Sanderson (magia)",
    influences:        "Ex: The Name of the Wind, Never Let Me Go, Fullmetal Alchemist",
    prosePace:         "Ex: Lento e contemplativo nos interlúdios; staccato e urgente nas cenas de ação",
    stylisticAvoid:    "Ex: Adjetivos excessivos, flashbacks longos, diálogos de exposição ('Como você sabe...')",
    seriesArc:         "Ex: Livro 1 planta a ameaça. Livro 2 revela a origem. Livro 3 exige o sacrifício",
    bookResolution:    "Ex: Kael sobrevive e obtém o artefato, mas perde o senso de direção moral",
    nextHooks:         "Ex: Vael tem uma filha; o mapa que aponta para a origem da Ruptura",
  };

  // ── SUGGEST FIELD (IA fills a specific panel field) ──────────────────────────
  async function suggestField(fieldKey, label) {
    if (bibliaChatLoading) return;
    const conv = bibliaChat.map(m => `${m.role==="user"?"Autor":"IA"}: ${m.content}`).join("\n\n");
    const panelCtx = Object.entries(bibliaPanel).filter(([,v])=>v).map(([k,v])=>`${k}: ${v}`).join("\n");
    setBibliaChatLoading(true);
    const userMsg = { role:"user", content: `Sugira um(a) ${label} para a história` };
    setbibliaChat(prev => [...prev, userMsg]);
    try {
      const suggestion = await callAI(
        [{ role:"user", content: `Based on this book conversation and current bible, suggest a specific "${label}" for the story. Be creative, vivid, and consistent with everything defined. Give only the suggestion content — no explanations, no preamble.\n\nConversation:\n${conv.slice(0,4000)}\n\nCurrent bible:\n${panelCtx}` }],
        aiLang + " You are a creative co-author. Give a direct, specific, vivid suggestion for the requested field.", 0.85
      );
      const cleaned = suggestion.trim();
      setbibliaPanel(prev => ({...prev, [fieldKey]: cleaned}));
      setbibliaLocked(l => ({...l, [fieldKey]: true}));
      setbibliaExpanded(e => ({...e, [fieldKey]: true}));
      setbibliaChat(prev => [...prev, {
        role:"ai",
        content: `Para **${label}**, aqui está minha sugestão:\n\n*${cleaned}*\n\nCampo preenchido automaticamente — edite se quiser ajustar!`
      }]);
    } catch(e) {
      setbibliaChat(prev => [...prev, { role:"ai", content: "Erro ao sugerir: " + e.message }]);
    }
    setBibliaChatLoading(false);
  }

  function resetAll() {
    localStorage.removeItem("autoria_v1");
    if (user) {
      supabase.from("sessions").upsert({ user_id: user.id, data: {}, updated_at: new Date().toISOString() }, { onConflict: "user_id" }).then(()=>{});
    }
    setPhase("biblia"); setIdeaInput(""); setCharacters([]);
    setConfig({title:"",subtitle:"",author:"",genre:GENRES[0]||"",tone:TONES[0]||"",pov:POV[1]||POV[0]||"",numChapters:6,synopsis:"",writingStyle:"",setting:"",protagonist:"",antagonist:"",numBooks:1,coverUrl:""});
    setOutline([]); setChapters([]); setScenes({}); setError(""); abortRef.current=false;
    setOutlineNextToGenerate(0); setOutlineBatchSize(5); setOutlinePlanWarnings({}); setOutlineAuthorNotes("");
    setNextChapterToWrite(0);
    setWritingBatchSize(3);
    setCurrentBookIdx(1); setCanonicalSummaries([]);
    setChatMessages([]); setChatInput("");
    setbibliaChat([]); setBibliaChatInput(""); setbibliaPanel({...emptyPanel});
    setbibliaLocked({}); setbibliaExpanded({}); setbibliaReviewed(false); setbibliaEditField(null); setbibliaEditValue(""); setBibliaUpdatedKeys({});
    if (currentProjectId) sessionStorage.removeItem(`autoria_upd_${currentProjectId}`);
  }

  const doneCount = chapters.filter(c => c.status==="done").length;
  const writingInProgress = chapters.some(c => c.status==="writing");
  const progress = chapters.length ? (doneCount/chapters.length)*100 : 0;
  const roleColor = r => ({protagonista:"#b8892a",protagonist:"#b8892a",antagonista:"#8b2020",antagonist:"#8b2020","secundário":"#2e7d32",secondary:"#2e7d32"}[r]||"#7a6a50");

  const phaseIdx = {"biblia":0,"characters":0,"refinement":0,"review":0,"outline":1,"scenes":1,"writing":2,"done":2}[phase]??0;
  const phaseLabels = [T.ph0, T.ph1, T.ph2];
  // Fase de destino ao clicar numa aba já visitada
  const phaseNavTarget = ["biblia", "outline", (phase==="done"||(chapters.length>0&&chapters.every(ch=>ch.content)))?"done":"writing"];

  const planInfo = config.numBooks <= 1 ? T.plan_free : config.numBooks <= 5 ? T.plan_writer : T.plan_pro;
  const seriesComplete = phase==="done" && currentBookIdx >= config.numBooks;

  // Show loading screen while checking auth
  if (authLoading) {
    return (
      <div style={{position:"fixed",inset:0,background:"#0e0a04",zIndex:2000,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16}}>
        <div style={{fontFamily:"'Playfair Display',serif",fontSize:"2rem",fontWeight:900,color:"#f0e8d8"}}>Autor<span style={{color:"#d4a94a"}}>IA</span></div>
        <div style={{width:24,height:24,border:"3px solid rgba(184,137,42,.3)",borderTopColor:"#b8892a",borderRadius:"50%",animation:"spin .7s linear infinite"}}/>
        <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
      </div>
    );
  }

  // Show landing page when not logged in
  if (!user) {
    return <LandingPage />;
  }

  // Show home page when no project is selected
  if (!currentProjectId) {
    return <HomePage projects={allProjects} onNewProject={createProject} onOpenProject={openProject} onEditProject={editProject} user={user} onSignOut={signOut} />;
  }

  return (
    <div className="app-atelier" style={{fontFamily:"'Georgia',serif",minHeight:"100vh",background:"#131313",color:"#e5e2e1"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Crimson+Pro:ital,wght@0,400;0,600;1,400&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        body{background:#131313;}
        input,select,textarea{font-family:'Crimson Pro',Georgia,serif;font-size:1rem;background:#20201f;border:1px solid rgba(120,109,89,.85);border-radius:3px;padding:9px 13px;color:#f0e9dd;outline:none;transition:border-color .2s,box-shadow .2s,width .2s;width:100%;}
        input:focus,select:focus,textarea:focus{border-color:#e2bf81;box-shadow:0 0 0 3px rgba(226,191,129,.22);}
        textarea{resize:vertical;}
        label{font-size:.75rem;font-weight:600;color:#d1c5b4;text-transform:uppercase;letter-spacing:.8px;display:block;margin-bottom:5px;}
        .btn{font-family:'Crimson Pro',Georgia,serif;font-size:.98rem;font-weight:600;padding:10px 20px;border-radius:3px;border:none;cursor:pointer;transition:all .2s;display:inline-flex;align-items:center;gap:7px;}
        .btn:disabled{opacity:.45;cursor:not-allowed;transform:none!important;}
        .btn-gold{background:linear-gradient(135deg,#c5a059,#e9c176);color:#261900;box-shadow:0 2px 8px rgba(197,160,89,.35);}
        .btn-gold:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 4px 14px rgba(184,137,42,.4);}
        .btn-outline{background:transparent;border:1.5px solid #e2bf81;color:#f2d8a8;}
        .btn-outline:hover:not(:disabled){background:rgba(226,191,129,.14);}
        .btn-ghost{background:#2a2a2a;color:#ede2d1;border:1px solid rgba(120,109,89,.85);}
        .btn-ghost:hover:not(:disabled){background:#353535;}
        .btn-danger{background:rgba(147,0,10,.2);color:#ffb4ab;border:1px solid rgba(255,180,171,.35);}
        .btn:focus-visible{
          outline:2px solid #e2bf81;
          outline-offset:2px;
        }
        .btn-sm{padding:7px 13px;font-size:.85rem;}
        .card{background:#1c1b1b;border:1px solid rgba(78,70,57,.55);border-radius:6px;padding:26px 30px;margin-bottom:18px;box-shadow:0 16px 32px rgba(0,0,0,.35);position:relative;}
        .card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,#c5a059,#e9c176,#c5a059);}
        .card-title{font-family:'Playfair Display',serif;font-size:1.15rem;color:#e9c176;margin-bottom:16px;display:flex;align-items:center;gap:9px;}
        .grid2{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
        .full{grid-column:1/-1;}
        .fgroup{display:flex;flex-direction:column;gap:5px;}
        .hint{font-size:.76rem;color:rgba(233,223,207,.78);font-style:italic;margin-top:3px;}
        .alert{padding:11px 15px;border-radius:3px;font-size:.88rem;margin-bottom:14px;border-left:3px solid;}
        .alert-err{background:rgba(147,0,10,.2);border-color:#ffb4ab;color:#ffdad6;}
        .alert-info{background:rgba(233,193,118,.12);border-color:#c5a059;color:#e9c176;}
        .sep{border:none;border-top:1px solid rgba(78,70,57,.45);margin:18px 0;}
        .spin{width:15px;height:15px;border:2px solid rgba(184,137,42,.3);border-top-color:#b8892a;border-radius:50%;animation:spin .7s linear infinite;display:inline-block;flex-shrink:0;}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}
        @keyframes bibliaFlash{0%{background:#fff8d6;box-shadow:0 0 0 2px #d4a94a;}60%{background:#fff8d6;box-shadow:0 0 0 2px #d4a94a;}100%{background:transparent;box-shadow:none;}}
        @keyframes toastIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        .biblia-flash{animation:bibliaFlash 1.8s ease forwards;border-radius:4px;}
        .biblia-update-toast{position:fixed;bottom:22px;right:22px;z-index:9999;background:#1a1208;color:#d4a94a;border:1px solid rgba(212,169,74,.4);border-radius:8px;padding:10px 18px;font-size:.88rem;font-weight:600;display:flex;align-items:center;gap:8px;box-shadow:0 8px 24px rgba(0,0,0,.4);animation:toastIn .3s ease;}
        .pw{background:#131313;border-radius:99px;height:7px;overflow:hidden;}
        .pf{height:100%;background:linear-gradient(90deg,#c5a059,#e9c176);transition:width .5s ease;}
        .badge{display:inline-flex;align-items:center;gap:3px;padding:2px 8px;border-radius:99px;font-size:.73rem;font-weight:700;border:1px solid transparent;}
        .bp{background:#2f2f2f;color:#f1e6d5;border-color:#5b5447;}
        .bw{background:rgba(226,191,129,.24);color:#ffe8c0;border-color:rgba(226,191,129,.55);animation:pulse 1.3s infinite;}
        .bd{background:rgba(76,175,80,.22);color:#c6f3cb;border-color:rgba(126,217,132,.58);}
        .be{background:rgba(255,180,171,.22);color:#ffd6d2;border-color:rgba(255,180,171,.55);}
        .cb{padding:12px 16px;border-radius:10px;font-size:.98rem;line-height:1.7;}
        .cu{background:#c5a059;color:#261900;border-radius:10px 10px 3px 10px;margin-left:auto;max-width:80%;white-space:pre-wrap;}
        .ca{background:#20201f;border:1px solid rgba(78,70,57,.65);color:#e5e2e1;border-radius:3px 10px 10px 10px;max-width:98%;box-shadow:0 1px 6px rgba(0,0,0,.24);}
        .cr{display:flex;margin-bottom:6px;}.cr.user{justify-content:flex-end;}.cr.ai{justify-content:flex-start;}
        .cc{background:#20201f;border:1px solid rgba(78,70,57,.65);border-radius:4px;padding:16px 18px;margin-bottom:10px;}
        .ch-head{display:flex;align-items:center;gap:10px;padding:12px 16px;cursor:pointer;background:#20201f;color:#e5e2e1;border-bottom:1px solid transparent;transition:background .15s;user-select:none;}
        .ch-head:hover{background:#2a2a2a;}.ch-head.open{border-bottom-color:rgba(78,70,57,.55);}
        .ch-body{padding:18px 22px;font-size:1.02rem;line-height:1.85;white-space:pre-wrap;color:#e0d4be;}
        .ch-item{border:1px solid rgba(78,70,57,.5);border-radius:3px;background:#20201f;overflow:hidden;margin-bottom:8px;}
        .plan-badge{display:inline-flex;align-items:center;gap:6px;padding:5px 12px;border-radius:99px;font-size:.8rem;font-weight:600;background:rgba(233,193,118,.16);border:1px solid rgba(233,193,118,.45);color:#e9c176;}
        /* ── Contrast safety net (legacy inline colors on dark cards) ── */
        .app-atelier .card [style*="color:#1a1208"],
        .app-atelier .card [style*="color:#2a1e0e"]{
          color:#f3eee4 !important;
        }
        .app-atelier .card [style*="color:#7a6a50"],
        .app-atelier .card [style*="color:#9a8a6a"],
        .app-atelier .card [style*="color:#b8a88a"]{
          color:#d8ccba !important;
        }
        .app-atelier .card [style*="background:#f5ede0"],
        .app-atelier .card [style*="background:#fff8e0"]{
          background:rgba(233,193,118,.10) !important;
          border-color:rgba(197,160,89,.35) !important;
        }
        .app-atelier .card .hint{
          color:rgba(229,226,225,.72) !important;
        }
        .app-atelier .hdr-email-txt{
          color:#d1c5b4 !important;
        }
        .app-atelier input::placeholder,
        .app-atelier textarea::placeholder{
          color:rgba(209,197,180,.58);
        }
        .app-atelier .card .material-symbols-outlined{
          color:inherit;
        }
        @media(max-width:1000px){.bible-layout{grid-template-columns:1fr 300px!important;}.bible-left{display:none!important;}}
        @media(max-width:700px){.bible-layout{grid-template-columns:1fr!important;}.bible-right{display:none!important;}}
        @media(max-width:580px){.grid2{grid-template-columns:1fr;}.full{grid-column:1;}.card{padding:18px 16px;}}
        .drawer-overlay{display:none;position:fixed;inset:0;z-index:500;background:rgba(26,18,8,.5);}
        .drawer{position:fixed;top:0;bottom:0;width:88vw;max-width:340px;background:#fffdf6;z-index:501;overflow-y:auto;box-shadow:0 0 40px rgba(0,0,0,.3);transition:transform .3s ease;padding:16px;}
        .drawer-left{left:0;transform:translateX(-100%);}
        .drawer-right{right:0;transform:translateX(100%);}
        .drawer-left.open{transform:translateX(0);}
        .drawer-right.open{transform:translateX(0);}
        .drawer-overlay.open{display:block;}
        .mobile-fab{display:none;position:fixed;bottom:0;left:0;right:0;z-index:400;background:#1a1208;padding:10px 16px;gap:8px;border-top:1px solid #3a2a10;}
        @media(max-width:1000px){.mobile-fab{display:flex;}.main-pad{padding-bottom:80px!important;}}
        @media(min-width:1001px){.mobile-fab{display:none!important;}.drawer-overlay{display:none!important;}}
        @media(max-width:700px){
          .chat-scroll{height:calc(55vh)!important;min-height:180px;}
          .pricing-inner{max-height:82vh;overflow-y:auto;}
          .scenes-grid{grid-template-columns:1fr!important;}
        }
        @media(max-width:400px){.btn{padding:9px 14px;font-size:.9rem;}}
        /* ── Mobile utilities ── */
        .hdr-email-txt{font-size:.7rem;color:#d1c5b4;max-width:100px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
        .done-actions{display:flex;gap:10px;flex-wrap:wrap;align-items:center;justify-content:space-between;}
        .done-btns{display:flex;gap:10px;flex-wrap:wrap;}
        .done-next{flex-shrink:0;}
        .ch-cap{font-size:.7rem;font-weight:700;color:#b8892a;text-transform:uppercase;letter-spacing:1.5px;min-width:52px;flex-shrink:0;}
        .ch-outline-badge{display:inline-block;}
        /* ── Atelier theme for writing/done section ── */
        .atelier-wrap .card{
          background:#1c1b1b;
          border:1px solid rgba(78,70,57,.55);
          box-shadow:0 18px 36px rgba(0,0,0,.42);
        }
        .atelier-wrap .card::before{
          background:linear-gradient(90deg,#c5a059,#e9c176,#c5a059);
        }
        .atelier-wrap .card-title{
          color:#e9c176;
        }
        .atelier-wrap .pw{background:#131313;}
        .atelier-wrap .pf{background:linear-gradient(90deg,#c5a059,#e9c176);}
        .atelier-wrap .sep{border-top:1px solid rgba(78,70,57,.45);}
        .atelier-wrap .btn-outline{
          border-color:#c5a059;
          color:#e9c176;
        }
        .atelier-wrap .btn-outline:hover:not(:disabled){
          background:rgba(197,160,89,.12);
        }
        .atelier-wrap .btn-ghost{
          background:#2a2a2a;
          color:#d1c5b4;
          border:1px solid rgba(78,70,57,.7);
        }
        .atelier-wrap .btn-ghost:hover:not(:disabled){
          background:#353535;
        }
        .atelier-wrap .btn-danger{
          background:rgba(147,0,10,.18);
          color:#ffb4ab;
          border:1px solid rgba(255,180,171,.32);
        }
        .atelier-wrap .ch-item{
          background:#20201f;
          border:1px solid rgba(78,70,57,.5);
        }
        .atelier-wrap .ch-head{
          background:#20201f;
          color:#e5e2e1;
        }
        .atelier-wrap .ch-head:hover{
          background:#2a2a2a;
        }
        .atelier-wrap .ch-head.open{
          border-bottom-color:rgba(78,70,57,.5);
        }
        .atelier-wrap .ch-body{
          color:#e0d4be;
        }
        .atelier-wrap .bp{
          background:#2a2a2a;
          color:#d1c5b4;
        }
        .atelier-wrap .bw{
          background:rgba(233,193,118,.16);
          color:#e9c176;
        }
        .atelier-wrap .bd{
          background:rgba(76,175,80,.16);
          color:#8bcf8f;
        }
        .atelier-wrap .be{
          background:rgba(255,180,171,.14);
          color:#ffb4ab;
        }
        /* ── 480px breakpoint (smartphones) ── */
        @media(max-width:480px){
          .btn{padding:8px 12px;font-size:.88rem;}
          .btn-sm{padding:5px 9px!important;font-size:.78rem!important;}
          .card{padding:14px 12px!important;}
          .card-title{font-size:1rem!important;margin-bottom:10px!important;}
          .ch-head{padding:9px 12px;gap:6px;}
          .ch-cap{min-width:38px!important;letter-spacing:.5px!important;}
          .badge{font-size:.63rem;padding:2px 5px;}
          .ch-outline-badge{display:none!important;}
          .hdr-email-txt{display:none!important;}
          .done-actions{flex-direction:column;align-items:stretch;}
          .done-btns{flex-direction:column;}
          .done-btns .btn{width:100%;justify-content:center;}
          .done-next{width:100%!important;justify-content:center;margin-top:2px;}
          .grid2{grid-template-columns:1fr!important;}
          .full{grid-column:1!important;}
        }
        /* ── 360px breakpoint (very small phones) ── */
        @media(max-width:360px){
          .card{padding:12px 10px!important;}
          .card-title{font-size:.93rem!important;}
          .btn{font-size:.83rem;padding:7px 10px;}
          .ch-head{gap:4px;}
        }
        @keyframes readerPageIn{from{opacity:0;transform:translateX(24px)}to{opacity:1;transform:translateX(0)}}
        @keyframes readerPageInBack{from{opacity:0;transform:translateX(-24px)}to{opacity:1;transform:translateX(0)}}
        .reader-page-fwd{animation:readerPageIn .22s ease;}
        .reader-page-back{animation:readerPageInBack .22s ease;}
        .reader-nav-btn{display:inline-flex;align-items:center;}
        .reader-swipe-hint{display:none;}
        .reader-close-btn{background:var(--rd-gold,#c8913a);color:#fff;border:none;border-radius:99px;padding:6px 14px;cursor:pointer;font-weight:700;font-size:.8rem;white-space:nowrap;}
        .reader-bm-continue{white-space:nowrap;font-size:.6rem;padding:2px 8px;}
        .reader-topbar-right{display:flex;align-items:center;gap:8px;flex-shrink:0;}
        .reader-topbar-center{display:flex;align-items:center;gap:6px;font-size:.72rem;min-width:0;flex:1;justify-content:center;overflow:hidden;}
        .reader-topbar-title{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex-shrink:1;}
        .reader-scroll-inner{padding:70px 24px 100px;transition:max-width .3s;}
        .reader-page-inner{padding:32px 24px 48px;}
        @media(max-width:480px){
          .reader-nav-btn{display:none!important;}
          .reader-bottom-bar{display:none!important;}
          .reader-content-paged{bottom:0!important;}
          .reader-swipe-hint{display:inline!important;}
          .reader-close-btn{padding:0!important;width:36px!important;height:36px!important;border-radius:8px!important;display:flex!important;align-items:center!important;justify-content:center!important;font-size:.9rem!important;}
          .reader-topbar-right{gap:5px!important;}
          .reader-bm-continue{font-size:.52rem!important;padding:2px 5px!important;}
          .reader-topbar-title{max-width:90px!important;font-size:.78rem!important;}
          .reader-scroll-inner{padding:70px 16px 120px!important;}
          .reader-page-inner{padding:24px 16px 40px!important;}
        }
        @media(max-width:360px){
          .reader-topbar-title{max-width:65px!important;}
          .reader-bm-continue{display:none!important;}
        }
      `}</style>

      {/* HEADER */}
      <div style={{background:"#1a1208",position:"sticky",top:0,zIndex:100}}>
        <div style={{maxWidth:800,margin:"0 auto",padding:"12px 16px"}}>
          {/* Linha 1: logo + auth */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
            <div style={{display:"flex",flexDirection:"column",gap:1}}>
              <div>
                <span style={{fontFamily:"'Playfair Display',serif",fontSize:"1.5rem",fontWeight:900,color:"#f5ede0"}}>Autor</span>
                <span style={{fontFamily:"'Playfair Display',serif",fontSize:"1.5rem",fontWeight:900,color:"#d4a94a"}}>IA</span>
              </div>
              <button onClick={goToHome} style={{background:"rgba(212,169,74,.12)",border:"1px solid rgba(212,169,74,.3)",color:"#d4a94a",cursor:"pointer",fontSize:".72rem",fontFamily:"'Crimson Pro',Georgia,serif",padding:"3px 10px",borderRadius:99,display:"flex",alignItems:"center",gap:4,lineHeight:1,marginTop:2,transition:"all .15s"}} onMouseEnter={e=>{e.currentTarget.style.background="rgba(212,169,74,.22)";}} onMouseLeave={e=>{e.currentTarget.style.background="rgba(212,169,74,.12)";}}>
                🏠 Projetos
              </button>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
              {/* Save status indicator */}
              {saveStatus !== "idle" && (
                <span style={{display:"flex",alignItems:"center",gap:4,fontSize:".65rem",color:saveStatus==="saving"?"#9a8a6a":saveStatus==="saved"?"#4caf50":"#e57373",transition:"all .3s"}}>
                  {saveStatus==="saving" && <span className="spin" style={{width:9,height:9,borderWidth:1.5}}/>}
                  {saveStatus==="saving" && "Salvando..."}
                  {saveStatus==="saved" && "✓ Salvo"}
                  {saveStatus==="error" && "⚠ Erro ao salvar"}
                </span>
              )}
              <div style={{width:8,height:8,borderRadius:"50%",background:"#4caf50",flexShrink:0}}/>
              <span className="hdr-email-txt">{user.email}</span>
              <button onClick={signOut} style={{background:"none",border:"1px solid #4a3a20",color:"#9a8a6a",borderRadius:99,padding:"4px 10px",cursor:"pointer",fontSize:".72rem",fontFamily:"'Crimson Pro',Georgia,serif"}}>Sair</button>
            </div>
          </div>
          {/* Linha 2: fases + planos */}
          <div style={{display:"flex",alignItems:"center",gap:6,marginTop:8,flexWrap:"wrap"}}>
            {phaseLabels.map((l,i) => (
              <div key={i}
                onClick={i < phaseIdx ? ()=>setPhase(phaseNavTarget[i]) : undefined}
                title={i < phaseIdx ? `Voltar para ${l}` : undefined}
                style={{padding:"3px 10px",borderRadius:99,fontSize:".7rem",fontWeight:600,
                  background:phaseIdx===i?"#d4a94a":phaseIdx>i?"rgba(212,169,74,.22)":"rgba(255,255,255,.07)",
                  color:phaseIdx===i?"#1a1208":phaseIdx>i?"#d4a94a":"#6a5a40",
                  whiteSpace:"nowrap",
                  cursor:i<phaseIdx?"pointer":"default",
                  transition:"background .15s"}}
                onMouseEnter={i<phaseIdx?e=>{e.currentTarget.style.background="rgba(212,169,74,.38)";}:undefined}
                onMouseLeave={i<phaseIdx?e=>{e.currentTarget.style.background="rgba(212,169,74,.22)";}:undefined}
              >{l}</div>
            ))}
            <button onClick={()=>setShowPricing(true)} style={{background:"none",border:"1px solid #4a3a20",color:"#9a8a6a",borderRadius:99,padding:"3px 10px",fontSize:".7rem",cursor:"pointer",fontFamily:"'Crimson Pro',Georgia,serif",marginLeft:"auto",whiteSpace:"nowrap"}}>Planos ✦</button>
          </div>
        </div>
      </div>

      <div className="main-pad" style={{maxWidth:phase==="biblia"&&bibliaChat.length>0?"100%":800,margin:"0 auto",padding:phase==="biblia"&&bibliaChat.length>0?"28px 24px 80px":"28px 18px 80px",transition:"max-width .3s"}}>
        {error && (
          <div className="alert alert-err">
            ⚠️ {error}
            <button onClick={()=>setError("")} style={{marginLeft:8,background:"none",border:"none",cursor:"pointer",color:"inherit",fontWeight:700}}>✕</button>
          </div>
        )}

        {/* ══ BÍBLIA ══ */}
        {phase==="biblia" && bibliaChat.length === 0 && (
          <div className="card">
            <div style={{textAlign:"center",marginBottom:28}}>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:"2.2rem",fontWeight:900,color:"#1a1208"}}>Autor<span style={{color:"#d4a94a"}}>IA</span></div>
              <div style={{fontSize:"1rem",color:"#7a6a50",fontStyle:"italic",marginTop:4}}>{T.biblia_sub}</div>
            </div>
            <div className="fgroup">
              <label>{T.biblia_label}</label>
              <textarea
                rows={8}
                value={ideaInput}
                onChange={e=>setIdeaInput(e.target.value)}
                placeholder={T.biblia_ph}
                onKeyDown={e=>{if(e.key==="Enter"&&e.ctrlKey){e.preventDefault();startBibliaChat();}}}
                autoFocus
              />
            </div>
            <div className="hint" style={{marginTop:5}}>{T.biblia_hint}</div>
            <div style={{display:"flex",justifyContent:"flex-end",marginTop:16}}>
              <button className="btn btn-gold" onClick={startBibliaChat} disabled={!ideaInput.trim()||bibliaChatLoading} style={{width:"100%",justifyContent:"center",fontSize:"1.05rem",padding:"13px 20px"}}>
                {bibliaChatLoading ? <><span className="spin"/>{T.load_building}</> : T.biblia_btn}
              </button>
            </div>
          </div>
        )}

        {/* ══ BÍBLIA CHAT ══ */}
        {phase==="biblia" && bibliaChat.length > 0 && (() => {
          const allFields = PANEL_SECTIONS.flatMap(s => s.fields);
          const filledCount = allFields.filter(f => bibliaPanel[f.key]).length;
          const totalCount = allFields.length;

          const PanelField = ({fkey, label}) => {
            const val     = bibliaPanel[fkey];
            const locked  = !!bibliaLocked[fkey];
            const expanded= !!bibliaExpanded[fkey];
            const editing = bibliaEditField === fkey;

            const borderColor = locked ? "#c8a840" : val ? "#d4a94a" : "#ddd0b0";
            const bgColor     = locked ? "#fffbee" : val ? "#fffdf6" : "#f9f6f1";
            const dotColor    = locked ? "#c8a840" : val ? "#b8892a" : "#ddd0b0";

            const toggle = () => {
              if (!val) return; // campos vazios não expandem
              setbibliaExpanded(p => ({...p, [fkey]: !p[fkey]}));
            };

            const saveEdit = () => {
              setbibliaPanel(p=>({...p,[fkey]:bibliaEditValue}));
              setbibliaLocked(p=>({...p,[fkey]:true}));
              setbibliaExpanded(p=>({...p,[fkey]:true}));
              setbibliaEditField(null); setbibliaEditValue("");
            };

            const isFlashing = !!bibliaFlashFields[fkey];
            const isUpdated = !isFlashing && !!bibliaUpdatedKeys[fkey];
            const updBorder = isUpdated ? "#4caf50" : borderColor;
            const updBg     = isUpdated ? "rgba(76,175,80,.07)" : bgColor;
            return (
              <div className={isFlashing ? "biblia-flash" : undefined} style={{marginBottom:3,borderRadius:4,border:`1px solid ${updBorder}`,borderLeft:`3px solid ${updBorder}`,background:updBg,transition:"border-color .2s,background .2s",overflow:"hidden"}}>

                {/* Header — sempre visível, clicável */}
                <div onClick={toggle} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 8px",gap:4,cursor:val?"pointer":"default",userSelect:"none"}}>
                  <div style={{display:"flex",alignItems:"center",gap:5,flex:1,minWidth:0}}>
                    <div style={{width:5,height:5,borderRadius:"50%",background:isUpdated?"#4caf50":dotColor,flexShrink:0}}/>
                    <span style={{fontSize:".6rem",fontWeight:800,color:val?"#f3e7d3":"#e2d2b8",textTransform:"uppercase",letterSpacing:".8px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{label}</span>
                    {isUpdated && <span title="Atualizado pela IA nesta sessão" style={{fontSize:".5rem",color:"#2e7d32",background:"#e8f5e9",border:"1px solid #a5d6a7",borderRadius:99,padding:"0 5px",fontWeight:700,flexShrink:0,lineHeight:"14px"}}>✓ novo</span>}
                    {BIBLE_TIPS[fkey] && (
                      <span title={BIBLE_TIPS[fkey]} onClick={e=>e.stopPropagation()} style={{cursor:"help",fontSize:".55rem",color:"#b8892a",background:"#fff8e0",border:"1px solid #e0cc80",borderRadius:"50%",width:12,height:12,display:"inline-flex",alignItems:"center",justifyContent:"center",fontWeight:800,flexShrink:0,lineHeight:1}}>?</span>
                    )}
                  </div>
                  <div style={{display:"flex",gap:1,alignItems:"center",flexShrink:0}}>
                    {val && <>
                      {val.length > 200 && (
                        <button title="Copiar conteúdo" onClick={e=>{e.stopPropagation();navigator.clipboard.writeText(val).catch(()=>{});}}
                          style={{background:"none",border:"none",cursor:"pointer",padding:"2px 3px",fontSize:".65rem",opacity:.4,lineHeight:1,transition:"opacity .15s"}}
                          onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=.4}>📋</button>
                      )}
                      <button title="Editar" onClick={e=>{e.stopPropagation();
                        if(editing){setbibliaEditField(null);setbibliaEditValue("");}
                        else{setbibliaEditField(fkey);setbibliaEditValue(val);setbibliaExpanded(p=>({...p,[fkey]:true}));}
                        // Editar destrava para IA poder re-sugerir depois
                        if(!editing) setbibliaLocked(p=>({...p,[fkey]:false}));
                      }} style={{background:"none",border:"none",cursor:"pointer",padding:"2px 3px",fontSize:".7rem",opacity:.5,lineHeight:1,transition:"opacity .15s"}}
                        onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=.5}>✏️</button>
                      <span style={{fontSize:".65rem",color:locked?"#c8a840":"#c8b89a"}}>{locked?"🔒":"·"}</span>
                    </>}
                    {val && <span style={{fontSize:".55rem",color:"#b8a88a",marginLeft:2}}>{expanded?"▲":"▼"}</span>}
                  </div>
                </div>

                {/* Conteúdo — só visível quando expandido */}
                {(expanded || editing) && (
                  <div style={{padding:"0 10px 10px 13px",borderTop:"1px solid "+borderColor}}>
                    {editing ? (
                      <div style={{display:"flex",gap:5,marginTop:8}}>
                        <div style={{flex:1,display:"flex",flexDirection:"column",gap:4}}>
                          <textarea
                            value={bibliaEditValue}
                            onChange={e=>{
                              setbibliaEditValue(e.target.value);
                              e.target.style.height="auto";
                              e.target.style.height=Math.min(e.target.scrollHeight, 600)+"px";
                            }}
                            style={{width:"100%",fontSize:".85rem",padding:"8px 10px",resize:"vertical",background:"#20201f",color:"#f0e9dd",
                              lineHeight:1.65,minHeight:80,fontFamily:"'Crimson Pro',Georgia,serif",
                              border:"1px solid rgba(120,109,89,.9)",borderRadius:4,boxSizing:"border-box"}}
                            ref={el=>{if(el){el.focus();el.selectionStart=el.selectionEnd=el.value.length;
                              el.style.height="auto";el.style.height=Math.min(el.scrollHeight,600)+"px";}}}
                            onKeyDown={e=>{
                              // Ctrl+Enter ou Cmd+Enter salva — Enter puro = nova linha (suporte multi-linha)
                              if(e.key==="Enter"&&(e.ctrlKey||e.metaKey)){e.preventDefault();saveEdit();}
                            }}
                          />
                          <div style={{display:"flex",gap:6,justifyContent:"space-between",alignItems:"center"}}>
                            <span style={{fontSize:".6rem",color:"#d1c5b4"}}>Enter = nova linha &nbsp;·&nbsp; Ctrl+Enter = salvar</span>
                            <div style={{display:"flex",gap:4}}>
                              <button className="btn btn-ghost" style={{padding:"4px 10px",fontSize:".72rem"}} onClick={()=>{setbibliaEditField(null);setbibliaEditValue("");}}>✕ Cancelar</button>
                              <button className="btn btn-gold" style={{padding:"4px 10px",fontSize:".72rem"}} onClick={saveEdit}>✓ Salvar</button>
                            </div>
                          </div>
                          {bibliaEditValue.length > 100 && (
                            <div style={{fontSize:".6rem",color:"#b8892a",textAlign:"right"}}>{bibliaEditValue.length} caracteres</div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div style={{position:"relative"}}>
                        <div
                          style={{
                            fontSize:".82rem",color:"#f0e9dd",lineHeight:1.6,marginTop:6,whiteSpace:"pre-wrap",
                            ...(val && val.length > 400
                              ? {maxHeight:180,overflowY:"auto",paddingRight:4,paddingBottom:4,
                                 scrollbarWidth:"thin",scrollbarColor:"#d4a94a #20201f",
                                 borderBottom:"1px solid rgba(120,109,89,.5)"}
                              : {}),
                          }}
                        >{val}</div>
                        {val && val.length > 400 && (
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:4}}>
                            <span style={{fontSize:".6rem",color:"#d1c5b4",fontStyle:"italic"}}>Role para ver mais ↕</span>
                            <span style={{fontSize:".6rem",color:"#b8892a",fontWeight:700}}>{val.length} caracteres</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          };

          // Section progress for left panel
          const sectionProgress = PANEL_SECTIONS.map(sec => ({
            ...sec,
            filled: sec.fields.filter(f => bibliaPanel[f.key]).length,
            total: sec.fields.length,
          }));
          const emptyFields = PANEL_SECTIONS.flatMap(s => s.fields).filter(f => !bibliaPanel[f.key]);

          const LeftPanelContent = () => (<>
            <div className="card" style={{marginBottom:12,padding:"16px 14px"}}>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:".9rem",fontWeight:700,color:"#f3eee4",marginBottom:12}}>Progresso</div>
              {sectionProgress.map(sec => {
                const pct = sec.total ? Math.round((sec.filled/sec.total)*100) : 0;
                return (
                  <div key={sec.label} style={{marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                      <span style={{fontSize:".7rem",fontWeight:700,color:"#d1c5b4",textTransform:"uppercase",letterSpacing:".6px"}}>{sec.icon} {sec.label.replace(/^.+\s/,"")}</span>
                      <span style={{fontSize:".65rem",fontWeight:700,color:pct===100?"#2e7d32":"#b8892a"}}>{sec.filled}/{sec.total}</span>
                    </div>
                    <div style={{height:5,background:"#131313",borderRadius:99,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${pct}%`,background:pct===100?"#4caf50":"linear-gradient(90deg,#b8892a,#d4a94a)",borderRadius:99,transition:"width .4s"}}/>
                    </div>
                  </div>
                );
              })}
            </div>
            {emptyFields.length > 0 && (
              <div className="card" style={{padding:"14px 14px"}}>
                <div style={{fontSize:".72rem",fontWeight:800,color:"#b8892a",textTransform:"uppercase",letterSpacing:"1px",marginBottom:10}}>✦ Pedir sugestão</div>
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  {emptyFields.slice(0,8).map(f => (
                    <button key={f.key} onClick={()=>{suggestField(f.key, f.label);setShowMobileLeft(false);}} disabled={bibliaChatLoading}
                      style={{background:"#20201f",border:"1px solid rgba(120,109,89,.7)",borderRadius:4,padding:"8px 10px",fontSize:".82rem",color:"#f0e9dd",cursor:"pointer",textAlign:"left",fontFamily:"'Crimson Pro',Georgia,serif",display:"flex",alignItems:"center",gap:6}}>
                      <span style={{color:"#b8892a"}}>→</span>{f.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="card" style={{marginTop:12,padding:"14px 14px"}}>
              <div style={{fontSize:".72rem",fontWeight:800,color:"#b8892a",textTransform:"uppercase",letterSpacing:"1px",marginBottom:10}}>📍 Próximas Etapas</div>
              {[
                {num:1, label:"Converse com a IA", desc:"Desenvolva personagens, mundo e conflito no chat", done: bibliaChat.length > 2},
                {num:2, label:"Revise a Bíblia", desc:"Valide inconsistências antes de gerar o roteiro", done: bibliaReviewed},
                {num:3, label:"Crie o Roteiro", desc:"A IA gera o esboço capítulo a capítulo", done: phase!=="biblia"&&phase!=="characters"&&phase!=="refinement"&&phase!=="review"},
                {num:4, label:"Escreva o Livro", desc:"Com o copiloto ao seu lado, cena a cena", done: false},
              ].map(step=>(
                <div key={step.num} style={{display:"flex",gap:8,marginBottom:10,alignItems:"flex-start",opacity:step.done?0.55:1}}>
                  <div style={{width:20,height:20,borderRadius:"50%",background:step.done?"#2e7d32":"#b8892a",color:"#fff",fontSize:".65rem",fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:2}}>
                    {step.done ? "✓" : step.num}
                  </div>
                  <div>
                    <div style={{fontSize:".75rem",fontWeight:700,color:"#f0e9dd",textDecoration:step.done?"line-through":"none"}}>{step.label}</div>
                    <div style={{fontSize:".68rem",color:"#d1c5b4",lineHeight:1.4,marginTop:1}}>{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </>);

          const currentBookTitle = (config.bookTitles && config.bookTitles[currentBookIdx-1]) || "";
          const isMultiBook = (config.numBooks || 1) > 1;
          const chronicleName = config.chronicleName || "";
          const seriesTitle = bibliaPanel.title || config.title || "";

          const RightPanelContent = () => (<>
            {/* Book identity header */}
            <div style={{marginBottom:10,borderBottom:"1px solid rgba(120,109,89,.5)",paddingBottom:10}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom: isMultiBook ? 8 : 0}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:".95rem",fontWeight:700,color:"#f3eee4"}}>Bíblia do Livro</div>
                <div style={{fontSize:".72rem",fontWeight:700,color:filledCount===totalCount?"#2e7d32":"#b8892a",background:filledCount===totalCount?"#e8f5e9":"#fff8e0",padding:"2px 8px",borderRadius:99}}>
                  {filledCount}/{totalCount}
                </div>
              </div>
              {/* Hierarchy banner — only for series */}
              {isMultiBook && (
                <div style={{background:"#20201f",border:"1px solid rgba(212,169,74,.6)",borderRadius:6,padding:"8px 10px"}}>
                  {/* Chronicle level */}
                  {chronicleName && (
                    <div style={{fontSize:".65rem",color:"#e9c176",fontWeight:700,letterSpacing:".5px",marginBottom:3}}>
                      🌌 {chronicleName}
                    </div>
                  )}
                  {/* Series level */}
                  {seriesTitle && (
                    <div style={{fontSize:".72rem",color:"#f0e9dd",marginBottom:4,paddingLeft: chronicleName ? 8 : 0, borderLeft: chronicleName ? "2px solid #d4a94a" : "none", paddingTop: chronicleName ? 2 : 0}}>
                      {chronicleName && <span style={{marginRight:4,color:"#c8a840"}}>└</span>}
                      <em>{seriesTitle}</em>
                    </div>
                  )}
                  {/* Current book level */}
                  <div style={{paddingLeft: seriesTitle ? 16 : 0, borderLeft: seriesTitle ? "2px solid #d4a94a" : "none", paddingTop: seriesTitle ? 2 : 0}}>
                    {seriesTitle && <span style={{fontSize:".7rem",color:"#c8a840",marginRight:4}}>└</span>}
                    <span style={{fontSize:".6rem",fontWeight:800,color:"#f2d8a8",textTransform:"uppercase",letterSpacing:"1px",marginRight:6}}>
                      Livro {currentBookIdx}/{config.numBooks}
                    </span>
                    {currentBookTitle ? (
                      <span style={{fontFamily:"'Playfair Display',serif",fontSize:".88rem",fontWeight:700,color:"#f3eee4"}}>
                        {currentBookTitle}
                      </span>
                    ) : (
                      <span style={{fontSize:".78rem",color:"#b8892a",fontStyle:"italic",cursor:"pointer"}} title="Defina o título em ⚙️ Config">
                        sem título — adicione em ⚙️
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div style={{maxHeight:"75vh",overflowY:"auto",paddingRight:2}}>
              {PANEL_SECTIONS.map((sec, si) => (
                <div key={sec.label} style={{marginBottom:14}}>
                  <div style={{fontSize:".68rem",fontWeight:800,textTransform:"uppercase",letterSpacing:"1.2px",color:"#9a7a3a",marginBottom:7,marginTop:si>0?4:0,display:"flex",alignItems:"center",gap:6}}>
                    <span>{sec.label}</span><div style={{flex:1,height:1,background:"#e8d8b0"}}/>
                  </div>
                  {sec.fields.map(f => (
                    <React.Fragment key={f.key}>
                      {PanelField({fkey:f.key, label:f.label})}
                    </React.Fragment>
                  ))}
                </div>
              ))}
            </div>
          </>);

          return (
          <>
            <div className="bible-layout" style={{display:"grid",gridTemplateColumns:"220px 1fr 340px",gap:14,alignItems:"start"}}>

              {/* LEFT: Progress + Quick Actions */}
              <div className="bible-left" style={{position:"sticky",top:20}}>
                {LeftPanelContent()}
              </div>

              {/* CENTER: Chat */}
              <div className="card" style={{marginBottom:0}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,flexWrap:"wrap",gap:8}}>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.2rem",fontWeight:700,color:"#1a1208"}}>Autor<span style={{color:"#d4a94a"}}>IA</span></div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {bibliaChat.length > 0 && (
                      <button className="btn btn-ghost btn-sm" onClick={()=>setShowBibliaReview(true)} style={{fontSize:".75rem",borderColor:"#d4a94a",color:"#b8892a"}}>📋 Ver Bíblia Completa</button>
                    )}
                    <button className="btn btn-ghost btn-sm" onClick={()=>{if(window.confirm("Isso vai apagar toda a conversa da Bíblia e começar do zero. Tem certeza?"))setbibliaChat([]);}} style={{fontSize:".75rem"}}>🔄 Resetar Ideia</button>
                  </div>
                </div>
                <div className="chat-scroll" style={{height:460,overflowY:"auto",marginBottom:12,padding:"2px 4px 2px 0"}}>
                  {bibliaChat.map((m,i) => (
                    <div key={i} ref={i===bibliaChat.length-1&&m.role==="ai"?bibliaChatLastMsgRef:null} className={`cr ${m.role==="user"?"user":"ai"}`} style={{marginBottom:14}}>
                      {m.role==="user"
                        ? <div className="cb cu" style={{fontSize:"1rem",lineHeight:1.65}}>{m.content}</div>
                        : <div className="cb ca" style={{padding:"16px 18px",maxWidth:"95%",fontSize:"1rem"}}
                            dangerouslySetInnerHTML={{__html: renderMsg(m.content)}}/>
                      }
                    </div>
                  ))}
                  {bibliaChatLoading && (
                    <div className="cr ai">
                      <div className="cb ca" style={{color:"#7a6a50",fontStyle:"italic",display:"flex",alignItems:"center",gap:8}}>
                        <span className="spin"/>Pensando...
                      </div>
                    </div>
                  )}
                  <div ref={bibliaChatEndRef}/>
                </div>
                <div style={{display:"flex",gap:8,marginBottom:12}}>
                  <textarea
                    ref={bibliaChatInputRef}
                    value={bibliaChatInput}
                    onChange={e=>{
                      setBibliaChatInput(e.target.value);
                      const el = bibliaChatInputRef.current;
                      if(el){el.style.height="auto";el.style.height=el.scrollHeight+"px";}
                    }}
                    placeholder="Responda, acrescente detalhes ou pergunte algo... (Enter para enviar)"
                    onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendBibliaChat();}}}
                    style={{flex:1,resize:"none",minHeight:"42px",maxHeight:"300px",overflowY:"auto",lineHeight:"1.5"}}
                    disabled={bibliaChatLoading}
                  />
                  <button className="btn btn-gold" onClick={sendBibliaChat} disabled={!bibliaChatInput.trim()||bibliaChatLoading} style={{alignSelf:"flex-end",padding:"10px 16px"}}>→</button>
                </div>
                {bibliaReviewed ? (
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    <div style={{background:"#e8f5e9",border:"1px solid #4caf50",borderRadius:4,padding:"10px 14px",fontSize:".88rem",color:"#2e7d32",display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:"1.1rem"}}>✅</span>
                      <span>Bíblia aprovada — pronta para o roteiro!</span>
                    </div>
                    <button className="btn btn-gold" onClick={buildOutlineFromChat} disabled={loading} style={{width:"100%",justifyContent:"center",padding:"12px 20px",fontSize:"1rem"}}>
                      {loading ? <><span className="spin"/>{loadingMsg}</> : "✨ Criar Roteiro →"}
                    </button>
                    <button onClick={reviewBible} disabled={bibliaChatLoading} style={{background:"none",border:"none",color:"#7a6a50",fontSize:".78rem",cursor:"pointer",textDecoration:"underline",textAlign:"center",padding:"2px 0"}}>
                      {bibliaChatLoading ? "Revisando..." : "🔍 Revisar novamente (opcional)"}
                    </button>
                  </div>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    <button className="btn btn-gold" onClick={()=>setShowPreOutlineModal(true)} disabled={loading||bibliaChatLoading} style={{width:"100%",justifyContent:"center",padding:"12px 20px",fontSize:"1rem"}}>
                      {loading ? <><span className="spin"/>{loadingMsg}</> : "✨ Criar Roteiro →"}
                    </button>
                  </div>
                )}
              </div>

              {/* RIGHT: Bible Panel */}
              <div className="card bible-right" style={{marginBottom:0,padding:"18px 16px"}}>
                {RightPanelContent()}
              </div>

            </div>

            {/* MOBILE DRAWERS */}
            {/* Overlay esquerda */}
            <div className={`drawer-overlay${showMobileLeft?" open":""}`} onClick={()=>setShowMobileLeft(false)}/>
            <div className={`drawer drawer-left${showMobileLeft?" open":""}`}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,paddingBottom:10,borderBottom:"1px solid #ddd0b0"}}>
                <span style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:"1rem"}}>Progresso & Sugestões</span>
                <button onClick={()=>setShowMobileLeft(false)} style={{background:"none",border:"none",fontSize:"1.2rem",cursor:"pointer",color:"#7a6a50",padding:"4px 8px"}}>✕</button>
              </div>
              {LeftPanelContent()}
            </div>

            {/* Overlay direita */}
            <div className={`drawer-overlay${showMobileRight?" open":""}`} onClick={()=>setShowMobileRight(false)}/>
            <div className={`drawer drawer-right${showMobileRight?" open":""}`}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,paddingBottom:10,borderBottom:"1px solid #ddd0b0"}}>
                <span style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:"1rem"}}>Bíblia do Livro</span>
                <button onClick={()=>setShowMobileRight(false)} style={{background:"none",border:"none",fontSize:"1.2rem",cursor:"pointer",color:"#7a6a50",padding:"4px 8px"}}>✕</button>
              </div>
              {RightPanelContent()}
            </div>

            {/* FLOATING MOBILE BAR */}
            <div className="mobile-fab">
              <button onClick={()=>{setShowMobileLeft(true);setShowMobileRight(false);}}
                style={{flex:1,background:"#2a1e0e",border:"1px solid #4a3a20",color:"#d4a94a",borderRadius:8,padding:"10px 0",fontSize:".8rem",fontWeight:700,cursor:"pointer",fontFamily:"'Crimson Pro',Georgia,serif",display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
                📊
                {emptyFields.length > 0 && <span style={{background:"#b8892a",color:"#fff",borderRadius:99,fontSize:".6rem",fontWeight:800,padding:"1px 5px"}}>{emptyFields.length}</span>}
              </button>
              {bibliaReviewed ? (
                <button onClick={buildOutlineFromChat} disabled={loading}
                  style={{flex:3,background:"linear-gradient(135deg,#b8892a,#d4a94a)",border:"none",color:"#1a1208",borderRadius:8,padding:"10px 0",fontSize:".88rem",fontWeight:800,cursor:"pointer",fontFamily:"'Crimson Pro',Georgia,serif",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                  {loading ? <><span className="spin"/>...</> : "✨ Criar Roteiro →"}
                </button>
              ) : (
                <button onClick={()=>setShowPreOutlineModal(true)} disabled={bibliaChatLoading||loading}
                  style={{flex:3,background:"linear-gradient(135deg,#b8892a,#d4a94a)",border:"none",color:"#1a1208",borderRadius:8,padding:"10px 0",fontSize:".88rem",fontWeight:800,cursor:"pointer",fontFamily:"'Crimson Pro',Georgia,serif",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                  ✨ Criar Roteiro →
                </button>
              )}
              <button onClick={()=>{setShowMobileRight(true);setShowMobileLeft(false);}}
                style={{flex:1,background:"#2a1e0e",border:"1px solid #4a3a20",color:"#d4a94a",borderRadius:8,padding:"10px 0",fontSize:".8rem",fontWeight:700,cursor:"pointer",fontFamily:"'Crimson Pro',Georgia,serif",display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
                📖
                <span style={{background:filledCount===totalCount?"#4caf50":"#7a6a50",color:"#fff",borderRadius:99,fontSize:".6rem",fontWeight:800,padding:"1px 5px"}}>{filledCount}/{totalCount}</span>
              </button>
            </div>
          </>
          );
        })()}

        {/* ══ CHARACTERS ══ */}
        {phase==="characters" && (
          <div className="card">
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:10}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <button className="btn btn-ghost btn-sm" onClick={()=>setPhase("biblia")}>← Bíblia</button>
                <div className="card-title" style={{margin:0}}>🎭 {T.chars_title}</div>
              </div>
              <button className="btn btn-outline btn-sm" onClick={doGenerateCharacters} disabled={generatingChar}>
                {generatingChar ? <><span className="spin"/>...</> : T.chars_regen}
              </button>
            </div>
            {generatingChar && characters.length===0 && (
              <div style={{textAlign:"center",padding:"36px 0",color:"#d1c5b4"}}>
                <span className="spin" style={{width:24,height:24,borderWidth:3}}/><br/><br/>{T.load_chars}
              </div>
            )}
            {characters.map((ch,idx) => (
              <div key={idx} className="cc">
                <div style={{fontSize:".7rem",fontWeight:700,letterSpacing:"1.5px",textTransform:"uppercase",color:roleColor(ch.papel||ch.role),marginBottom:3}}>{ch.papel||ch.role||"Character"}</div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.15rem",fontWeight:700,marginBottom:8}}>{ch.nome||ch.name}</div>
                {[["Idade/Age",ch.idade||ch.age],["Aparência/Appearance",ch.aparencia||ch.appearance],["Personalidade/Personality",ch.personalidade||ch.personality],["Motivação/Motivation",ch.motivacao||ch.motivation],["Segredo/Secret",ch.segredo||ch.secret],["Arco/Arc",ch.arco||ch.arc],["Relações/Relations",ch.relacoes||ch.relations]].filter(([,v])=>v).map(([k,v])=>(
                  <div key={k} style={{fontSize:".88rem",color:"#d1c5b4",marginBottom:2,lineHeight:1.5}}><strong style={{color:"#f0e9dd"}}>{k.split("/")[0]}:</strong> {v}</div>
                ))}
                {editingChar===idx ? (
                  <div style={{marginTop:10,padding:"10px",background:"#20201f",borderRadius:3,border:"1px solid rgba(120,109,89,.7)"}}>
                    <label>{T.chars_what}</label>
                    <textarea rows={2} value={charFeedback} onChange={e=>setCharFeedback(e.target.value)} style={{marginBottom:7}}/>
                    <div style={{display:"flex",gap:7}}>
                      <button className="btn btn-gold btn-sm" onClick={()=>refineCharacter(idx)} disabled={generatingChar||!charFeedback.trim()}>
                        {generatingChar ? <><span className="spin"/>...</> : T.chars_confirm}
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={()=>{setEditingChar(null);setCharFeedback("");}}>{T.chars_cancel}</button>
                    </div>
                  </div>
                ) : (
                  <button className="btn btn-ghost btn-sm" style={{marginTop:10}} onClick={()=>{setEditingChar(idx);setCharFeedback("");}}>{T.chars_adjust}</button>
                )}
              </div>
            ))}
            {characters.length > 0 && <>
              <hr className="sep"/>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
                <div style={{fontSize:".88rem",color:"#d1c5b4",fontStyle:"italic"}}>{T.chars_hint}</div>
                <button className="btn btn-gold" onClick={()=>setPhase("refinement")} disabled={generatingChar}>{T.chars_next}</button>
              </div>
            </>}
          </div>
        )}

        {/* ══ REFINEMENT CHAT ══ */}
        {phase==="refinement" && (
          <div className="card">
            <div className="card-title">🌍 {T.refine_title}</div>
            <div style={{maxHeight:420,overflowY:"auto",marginBottom:14,padding:"4px 0"}}>
              {chatMessages.map((m,i) => (
                <div key={i} className={`cr ${m.role==="user"?"user":"ai"}`}>
                  <div className={`cb ${m.role==="user"?"cu":"ca"}`}>{m.content}</div>
                </div>
              ))}
              {chatLoading && (
                <div className="cr ai">
                  <div className="cb ca" style={{color:"#d1c5b4",fontStyle:"italic"}}>
                    <span className="spin" style={{marginRight:8}}/>...
                  </div>
                </div>
              )}
              <div ref={chatEndRef}/>
            </div>
            <div style={{display:"flex",gap:8}}>
              <textarea
                rows={2}
                value={chatInput}
                onChange={e=>setChatInput(e.target.value)}
                placeholder={T.refine_ph}
                onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendRefinementChat();}}}
                style={{flex:1,resize:"none"}}
              />
              <button className="btn btn-gold" onClick={sendRefinementChat} disabled={!chatInput.trim()||chatLoading} style={{alignSelf:"flex-end",whiteSpace:"nowrap"}}>→</button>
            </div>
            <hr className="sep"/>
            <div className="hint" style={{marginBottom:12}}>{T.refine_hint}</div>
            <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
              <button className="btn btn-ghost btn-sm" onClick={()=>setPhase("characters")}>{T.refine_back}</button>
              <button className="btn btn-gold" onClick={()=>setPhase("review")}>{T.refine_next}</button>
            </div>
          </div>
        )}

        {/* ══ REVIEW ══ */}
        {phase==="review" && (
          <div className="card">
            <div className="card-title">📝 {T.review_title}</div>
            <div className="alert alert-info">{T.review_info}</div>
            <div className="grid2">
              <div className="fgroup full"><label>{T.f_title}</label><input value={config.title} onChange={e=>cfg("title",e.target.value)}/></div>
              <div className="fgroup full"><label>{T.f_subtitle}</label><input value={config.subtitle} onChange={e=>cfg("subtitle",e.target.value)}/></div>
              <div className="fgroup"><label>{T.f_author}</label><input value={config.author} onChange={e=>cfg("author",e.target.value)} placeholder={T.author_placeholder}/></div>
              <div className="fgroup"><label>{T.f_genre}</label><select value={config.genre} onChange={e=>cfg("genre",e.target.value)}>{GENRES.map(g=><option key={g}>{g}</option>)}</select></div>
              <div className="fgroup"><label>{T.f_tone}</label><select value={config.tone} onChange={e=>cfg("tone",e.target.value)}>{TONES.map(t=><option key={t}>{t}</option>)}</select></div>
              <div className="fgroup"><label>{T.f_pov}</label><select value={config.pov} onChange={e=>cfg("pov",e.target.value)}>{POV.map(p=><option key={p}>{p}</option>)}</select></div>
              <div className="fgroup"><label>{T.f_chapters}</label><input type="number" min={3} max={100} value={config.numChapters} onChange={e=>cfg("numChapters",Math.min(100,Math.max(3,+e.target.value)))}/></div>
              <div className="fgroup">
                <label>{T.f_num_books}</label>
                <select value={config.numBooks} onChange={e=>cfg("numBooks",+e.target.value)}>
                  {[1,2,3,4,5].map(n=><option key={n} value={n}>{n}</option>)}
                </select>
                <div className="hint">{T.f_num_books_hint}</div>
              </div>
              <div className="fgroup full"><label>{T.f_setting}</label><input value={config.setting} onChange={e=>cfg("setting",e.target.value)}/></div>
              <div className="fgroup full"><label>{T.f_synopsis}</label><textarea rows={3} value={config.synopsis} onChange={e=>cfg("synopsis",e.target.value)}/></div>
              <div className="fgroup full"><label>{T.f_style}</label><textarea rows={2} value={config.writingStyle} onChange={e=>cfg("writingStyle",e.target.value)} placeholder={T.f_style_ph}/></div>
            </div>
            {config.numBooks > 0 && (
              <div style={{marginTop:14,display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:".8rem",color:"#d1c5b4"}}>{T.plan_label}</span>
                <span className="plan-badge">📚 {planInfo}</span>
              </div>
            )}
            <hr className="sep"/>
            <div style={{display:"flex",gap:10,justifyContent:"space-between",flexWrap:"wrap"}}>
              <button className="btn btn-ghost btn-sm" onClick={()=>setPhase("biblia")}>← Voltar à Bíblia</button>
              <button className="btn btn-gold" onClick={()=>generateOutline()} disabled={loading}>
                {loading ? <><span className="spin"/>{loadingMsg}</> : T.review_gen}
              </button>
            </div>
          </div>
        )}

        {/* ══ OUTLINE ══ */}
        {phase==="outline" && (
          <div className="card">
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:10}}>
              <div className="card-title" style={{margin:0}}>📋 {T.outline_title(currentBookIdx, config.numBooks)}</div>
              <button className="btn btn-ghost btn-sm" onClick={()=>setEditingOutline(!editingOutline)}>
                {editingOutline ? T.outline_save : T.outline_edit}
              </button>
            </div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap",marginBottom:10}}>
              <div style={{fontSize:".78rem",color:"rgba(229,226,225,.72)"}}>
                Roteiro gerado: <strong style={{color:"#f0e9dd"}}>{outline.length}</strong> / {config.numChapters} capítulos
              </div>
              <div style={{display:"flex",alignItems:"center",gap:7}}>
                <span style={{fontSize:".7rem",textTransform:"uppercase",letterSpacing:".7px",color:"rgba(229,226,225,.55)"}}>Lote</span>
                {[3,5,8].map(size => (
                  <button
                    key={size}
                    className="btn btn-ghost btn-sm"
                    onClick={()=>setOutlineBatchSize(size)}
                    disabled={loading}
                    style={{
                      padding:"3px 10px",
                      minWidth:36,
                      justifyContent:"center",
                      borderColor: outlineBatchSize===size ? "rgba(233,193,118,.55)" : undefined,
                      background: outlineBatchSize===size ? "rgba(233,193,118,.16)" : undefined,
                      color: outlineBatchSize===size ? "#e9c176" : undefined,
                    }}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
            <div className="alert alert-info">{T.outline_info}</div>
            {/* 3-act legend — matches chapter border colors */}
            <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}}>
              {[
                {ato:1,label:"Ato 1 · Ruptura",sub:"Apresentação + Incidente incitante",color:"#4caf50",bg:"rgba(76,175,80,.16)",tc:"#b8edbc"},
                {ato:2,label:"Ato 2 · Confronto",sub:"Escalada + Ponto médio + Crise máxima",color:"#b8892a",bg:"rgba(233,193,118,.16)",tc:"#ffe8c0"},
                {ato:3,label:"Ato 3 · Transformação",sub:"Clímax + Resolução + Novo normal",color:"#8b2020",bg:"rgba(147,0,10,.2)",tc:"#ffd6d2"},
              ].map(a=>(
                <div key={a.ato} style={{flex:"1 1 160px",background:a.bg,border:`1px solid ${a.color}`,borderLeft:`4px solid ${a.color}`,borderRadius:4,padding:"8px 10px"}}>
                  <div style={{fontSize:".7rem",fontWeight:800,color:a.tc,marginBottom:2}}>{a.label}</div>
                  <div style={{fontSize:".62rem",color:a.tc,opacity:.8,lineHeight:1.4}}>{a.sub}</div>
                </div>
              ))}
            </div>
            {outline.map((ch,idx) => (
              <div key={ch.id} style={{background:"#20201f",border:"1px solid rgba(120,109,89,.6)",borderLeft:`3px solid ${ch.ato===1?"#4caf50":ch.ato===2?"#b8892a":"#8b2020"}`,borderRadius:3,padding:"13px 16px",marginBottom:8}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                  <span style={{fontSize:".7rem",fontWeight:700,color:"#b8892a",textTransform:"uppercase",letterSpacing:"1.5px"}}>Cap. {ch.id}</span>
                  {ch.ato && <span style={{fontSize:".65rem",fontWeight:700,padding:"1px 7px",borderRadius:99,background:ch.ato===1?"rgba(76,175,80,.18)":ch.ato===2?"rgba(233,193,118,.2)":"rgba(147,0,10,.22)",color:ch.ato===1?"#c6f3cb":ch.ato===2?"#ffe8c0":"#ffd6d2"}}>Ato {ch.ato}</span>}
                  {ch.papel && <span style={{fontSize:".6rem",fontWeight:600,padding:"1px 7px",borderRadius:99,background:"#2a2a2a",color:"#d1c5b4",textTransform:"uppercase",letterSpacing:".5px"}}>{ch.papel.replace(/_/g," ")}</span>}
                </div>
                {editingOutline ? (
                  <>
                    <input value={ch.titulo} onChange={e=>setOutline(prev=>prev.map((o,i)=>i===idx?{...o,titulo:e.target.value}:o))} style={{marginBottom:7,fontWeight:600}}/>
                    <textarea rows={2} value={ch.resumo} onChange={e=>setOutline(prev=>prev.map((o,i)=>i===idx?{...o,resumo:e.target.value}:o))} style={{fontSize:".9rem"}}/>
                  </>
                ) : (
                  <>
                    <div style={{fontSize:"1.02rem",fontWeight:600,marginBottom:4}}>{ch.titulo}</div>
                    <div style={{fontSize:".9rem",color:"#d1c5b4",fontStyle:"italic",lineHeight:1.5,marginBottom:ch.objetivo_pov||ch.mudanca?6:0}}>{ch.resumo}</div>
                    {ch.objetivo_pov && <div style={{fontSize:".78rem",color:"#f0e9dd",marginBottom:2}}><strong style={{color:"#b8892a"}}>Objetivo POV:</strong> {ch.objetivo_pov}</div>}
                    {ch.mudanca && <div style={{fontSize:".78rem",color:"#f0e9dd"}}><strong style={{color:"#b8892a"}}>Mudança:</strong> {ch.mudanca}</div>}
                    {isOutlineChapterEmpty(ch) && (
                      <div style={{marginTop:8,display:"flex",gap:8,flexWrap:"wrap"}}>
                        <button
                          onClick={()=>fixOutlineDeviation(ch, idx, "Capítulo vazio/incompleto. Gere conteúdo narrativo-estrutural completo para este capítulo mantendo canon e continuidade.")}
                          disabled={fixingOutlineChapter !== null || loading}
                          style={{background:"#4a3a20",color:"#ffe8c0",border:"1px solid rgba(233,193,118,.45)",borderRadius:4,padding:"5px 10px",fontSize:".74rem",fontWeight:700,cursor:(fixingOutlineChapter !== null || loading)?"wait":"pointer",opacity:(fixingOutlineChapter !== null || loading)?0.65:1}}
                        >
                          {fixingOutlineChapter===ch.id ? "✍️ Gerando..." : "✨ Gerar capítulo"}
                        </button>
                      </div>
                    )}
                    {outlinePlanWarnings[ch.id] && (
                      <div style={{marginTop:8,background:"rgba(230,81,0,.14)",border:"1px solid rgba(230,81,0,.55)",borderLeft:"3px solid #e65100",borderRadius:4,padding:"7px 9px",fontSize:".75rem",lineHeight:1.5,color:"#ffd9b0"}}>
                        <strong style={{display:"block",color:"#ffbe84",marginBottom:3}}>⚠️ Possível desvio da bíblia</strong>
                        {outlinePlanWarnings[ch.id]}
                        <div style={{marginTop:8,display:"flex",gap:8,flexWrap:"wrap"}}>
                          <button
                            onClick={()=>fixOutlineDeviation(ch, idx, outlinePlanWarnings[ch.id])}
                            disabled={fixingOutlineChapter !== null || loading}
                            style={{background:"#e65100",color:"#fff",border:"none",borderRadius:4,padding:"5px 10px",fontSize:".74rem",fontWeight:700,cursor:(fixingOutlineChapter !== null || loading)?"wait":"pointer",opacity:(fixingOutlineChapter !== null || loading)?0.65:1}}
                          >
                            {fixingOutlineChapter===ch.id ? "✍️ Corrigindo..." : "🔧 Corrigir desvio"}
                          </button>
                          <button
                            onClick={()=>setOutlinePlanWarnings(prev=>{const next={...prev}; delete next[ch.id]; return next;})}
                            disabled={fixingOutlineChapter===ch.id}
                            style={{background:"none",border:"1px solid rgba(230,81,0,.55)",borderRadius:4,padding:"5px 10px",fontSize:".74rem",color:"#ffd9b0",cursor:"pointer"}}
                          >
                            Ignorar alerta
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
            <hr className="sep"/>
            <div style={{display:"flex",gap:10,justifyContent:"space-between",flexWrap:"wrap",alignItems:"center"}}>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                <button className="btn btn-ghost btn-sm" onClick={()=>setPhase("biblia")} title="Voltar à Bíblia — nenhum dado será perdido">← Bíblia</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>setPhase("review")} style={{fontSize:".75rem",color:"#d1c5b4"}}>{T.outline_back}</button>
              </div>
              <div style={{display:"flex",gap:8}}>
                {outline.length > 0 && currentBookIdx === 1 && (
                  <button className="btn btn-outline btn-sm" onClick={applyCanonPatchBook1} disabled={loading || fixingOutlineChapter !== null}>
                    🛠 Alinhar Livro 1 (1–25)
                  </button>
                )}
                {outline.length >= 10 && (
                  <button className="btn btn-outline btn-sm" onClick={applyCanonPatch6to10} disabled={loading || fixingOutlineChapter !== null}>
                    🛠 Corrigir 6–10
                  </button>
                )}
                {outline.length >= 25 && (
                  <button className="btn btn-outline btn-sm" onClick={applyCanonPatch11to25} disabled={loading || fixingOutlineChapter !== null}>
                    🛠 Corrigir 11–25
                  </button>
                )}
                {outline.some(isOutlineChapterEmpty) && (
                  <button className="btn btn-outline btn-sm" onClick={fillEmptyOutlineChapters} disabled={loading || fixingOutlineChapter !== null}>
                    {fixingOutlineChapter !== null ? "Preenchendo..." : "⚡ Preencher capítulos vazios"}
                  </button>
                )}
                {outline.length < config.numChapters && (
                  <button className="btn btn-outline btn-sm" onClick={continueOutlineBatch} disabled={loading}>
                    {loading ? <><span className="spin"/>...</> : `▶ Gerar próximos ${Math.min(outlineBatchSize, config.numChapters - outline.length)} capítulos`}
                  </button>
                )}
                <button className="btn btn-outline btn-sm" onClick={()=>generateOutline()} disabled={loading}>
                  {loading ? <><span className="spin"/>...</> : T.outline_regen}
                </button>
                <button className="btn btn-gold" onClick={()=>setPhase("scenes")} disabled={outline.length < config.numChapters}>
                  {outline.length < config.numChapters ? "Complete o roteiro para seguir" : T.outline_write}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══ SCENES ══ */}
        {phase==="scenes" && (
          <div className="card">
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,flexWrap:"wrap",gap:10}}>
              <div className="card-title" style={{margin:0}}>🎬 Planejamento de Cenas</div>
              <div style={{display:"flex",gap:8}}>
                {currentBookIdx === 1 && outline.length > 0 && (
                  <button className="btn btn-outline btn-sm" onClick={applySceneCanonBook1} disabled={generatingScenes}>
                    🛠 Alinhar cenas ao roteiro
                  </button>
                )}
                {currentBookIdx === 1 && outline.length > 0 && (
                  <button className="btn btn-outline btn-sm" onClick={() => repairFallbackScenesBook1()} disabled={generatingScenes}>
                    ✨ Reparar cenas incompletas
                  </button>
                )}
                <button className="btn btn-outline btn-sm" onClick={generateAllScenes} disabled={generatingScenes}>
                  {generatingScenes ? <><span className="spin"/>Gerando...</> : "⚡ Gerar Todas"}
                </button>
              </div>
            </div>
            <div className="alert alert-info" style={{marginBottom:14}}>Planeje as cenas de cada capítulo antes de escrever. Cada cena tem objetivo, mudança, hook de entrada e saída.</div>
            {outline.map((ch, idx) => (
              <div key={ch.id} style={{marginBottom:16,border:"1px solid rgba(120,109,89,.6)",borderRadius:4,overflow:"hidden"}}>
                <div style={{background:"#20201f",padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:".7rem",fontWeight:700,color:"#b8892a",textTransform:"uppercase",letterSpacing:"1px"}}>Cap. {ch.id}</span>
                    {ch.ato && <span style={{fontSize:".65rem",padding:"1px 7px",borderRadius:99,background:ch.ato===1?"rgba(76,175,80,.18)":ch.ato===2?"rgba(233,193,118,.2)":"rgba(147,0,10,.22)",color:ch.ato===1?"#c6f3cb":ch.ato===2?"#ffe8c0":"#ffd6d2",fontWeight:700}}>Ato {ch.ato}</span>}
                    <span style={{fontSize:".95rem",fontWeight:600}}>{ch.titulo}</span>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={()=>generateScenesForChapter(ch, idx)} disabled={generatingScenes} style={{fontSize:".75rem"}}>
                    {scenes[ch.id] ? "↺ Regenerar" : "+ Planejar Cenas"}
                  </button>
                </div>
                {scenes[ch.id] ? (
                  <div style={{padding:"12px 16px",display:"flex",flexDirection:"column",gap:8}}>
                    {scenes[ch.id].map((sc, si) => (
                      <div key={si} style={{background:"#1c1b1b",border:"1px solid rgba(120,109,89,.55)",borderRadius:3,padding:"10px 14px"}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
                          <span style={{fontSize:".72rem",fontWeight:800,color:"#b8892a",textTransform:"uppercase",letterSpacing:".8px"}}>Cena {si+1}</span>
                          {sc.tensao && <span style={{fontSize:".62rem",fontWeight:700,padding:"1px 6px",borderRadius:99,background:sc.tensao==="high"?"rgba(147,0,10,.22)":sc.tensao==="medium"?"rgba(233,193,118,.22)":"rgba(76,175,80,.18)",color:sc.tensao==="high"?"#ffd6d2":sc.tensao==="medium"?"#ffe8c0":"#c6f3cb"}}>{sc.tensao==="high"?"Alta tensão":sc.tensao==="medium"?"Média tensão":"Baixa tensão"}</span>}
                          {sc.subtrama && <span style={{fontSize:".62rem",color:"#d1c5b4",fontStyle:"italic"}}>{sc.subtrama}</span>}
                          <strong style={{fontSize:".88rem"}}>{sc.titulo}</strong>
                        </div>
                        <div className="scenes-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                          {sc.hookEntrada && <div style={{fontSize:".78rem",color:"#f0e9dd"}}><strong style={{color:"#b8892a"}}>↪ Entrada:</strong> {sc.hookEntrada}</div>}
                          {sc.hookSaida && <div style={{fontSize:".78rem",color:"#f0e9dd"}}><strong style={{color:"#b8892a"}}>↩ Saída:</strong> {sc.hookSaida}</div>}
                          {sc.objetivo && <div style={{fontSize:".78rem",color:"#f0e9dd"}}><strong style={{color:"#b8892a"}}>Objetivo POV:</strong> {sc.objetivo}</div>}
                          {sc.mudanca && <div style={{fontSize:".78rem",color:"#f0e9dd"}}><strong style={{color:"#b8892a"}}>Mudança:</strong> {sc.mudanca}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{padding:"10px 16px",fontSize:".85rem",color:"#d1c5b4",fontStyle:"italic"}}>Clique em "+ Planejar Cenas" para gerar o planejamento com IA.</div>
                )}
              </div>
            ))}
            <hr className="sep"/>
            <div style={{display:"flex",gap:10,justifyContent:"space-between",flexWrap:"wrap",alignItems:"center"}}>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                <button className="btn btn-ghost btn-sm" onClick={()=>setPhase("biblia")} title="Voltar à Bíblia — nenhum dado será perdido">← Bíblia</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>setPhase("outline")} style={{fontSize:".75rem",color:"#d1c5b4"}}>← Roteiro</button>
              </div>
              <button className="btn btn-gold" onClick={startWriting}>✍️ Começar a Escrever →</button>
            </div>
          </div>
        )}

        {/* ══ WRITING / DONE ══ */}
        {(phase==="writing" || phase==="done") && (
          <div className="atelier-wrap">
            <div className="card">
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,flexWrap:"wrap",gap:10}}>
                <div className="card-title" style={{margin:0}}>
                  {seriesComplete
                    ? T.series_done
                    : phase==="writing"
                      ? T.writing_title(currentBookIdx, config.numBooks)
                      : T.done_title(currentBookIdx, config.numBooks)}
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {phase==="writing" && (
                    <button className="btn btn-danger btn-sm" onClick={()=>{abortRef.current=true;}}>{T.done_stop}</button>
                  )}
                  {phase==="done" && (
                    <button className="btn btn-ghost btn-sm" onClick={resetAll}>{T.done_new}</button>
                  )}
                  {doneCount > 0 && (
                    <button className="btn btn-outline btn-sm" onClick={()=>setShowDraftReader(true)}>📖 Ler Rascunho</button>
                  )}
                  {doneCount > 0 && (
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={analyzeFullBook}
                      disabled={analyzingBook}
                      style={{borderColor:"#4caf50",color:"#2e7d32",display:"flex",alignItems:"center",gap:5}}
                    >
                      {analyzingBook
                        ? <><span className="spin" style={{borderTopColor:"#2e7d32",width:12,height:12}}/> Analisando…</>
                        : "📋 Analisar Livro"}
                    </button>
                  )}
                  {currentBookIdx === 1 && (
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={applyBook1FinalAlignment}
                      disabled={writingInProgress || generatingScenes || fixingChapter !== null}
                      style={{borderColor:"#c5a059",color:"#e9c176"}}
                    >
                      🧹 Alinhamento final Livro 1
                    </button>
                  )}
                </div>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:".8rem",color:"rgba(229,226,225,.62)",marginBottom:7}}>
                <span>{T.of_chapters(doneCount, chapters.length)}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="pw"><div className="pf" style={{width:`${progress}%`}}/></div>
              {phase==="writing" && (
                <div style={{marginTop:10,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:".74rem",color:"rgba(229,226,225,.62)",textTransform:"uppercase",letterSpacing:".7px"}}>
                      Tamanho do lote
                    </span>
                    {[2,3,4].map(size => (
                      <button
                        key={size}
                        className="btn btn-ghost btn-sm"
                        onClick={()=>setWritingBatchSize(size)}
                        disabled={writingInProgress}
                        style={{
                          padding:"3px 10px",
                          minWidth:34,
                          justifyContent:"center",
                          borderColor: writingBatchSize===size ? "rgba(233,193,118,.55)" : undefined,
                          background: writingBatchSize===size ? "rgba(233,193,118,.16)" : undefined,
                          color: writingBatchSize===size ? "#e9c176" : undefined,
                          opacity: writingInProgress ? 0.65 : 1
                        }}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                  <span style={{fontSize:".76rem",color:"rgba(229,226,225,.68)"}}>
                    {writingInProgress
                      ? `Gerando bloco atual de até ${writingBatchSize} capítulos...`
                      : nextChapterToWrite >= outline.length
                        ? "Todos os capítulos foram gerados."
                        : `Bloco concluído. Revise os capítulos e continue quando quiser.`}
                  </span>
                  {nextChapterToWrite < outline.length && (
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={continueWritingBatch}
                      disabled={writingInProgress || fixingChapter !== null}
                    >
                      {writingInProgress
                        ? "Gerando..."
                        : `▶ Gerar próximos ${Math.min(writingBatchSize, outline.length - nextChapterToWrite)} capítulos`}
                    </button>
                  )}
                </div>
              )}
              {phase==="done" && (
                <>
                  <hr className="sep"/>
                  <div className="done-actions">
                    <div className="done-btns">
                      <button className="btn btn-gold" onClick={()=>generatePDF(config,characters,chapters,currentBookIdx)}>{T.done_pdf}</button>
                      <button className="btn btn-outline" onClick={()=>generateEPUB(config,characters,chapters,currentBookIdx)}>{T.done_epub}</button>
                      <button className="btn btn-outline" onClick={()=>setShowBookReader(true)} style={{borderColor:"#8b2020",color:"#8b2020"}}>📖 Ler Livro</button>
                    </div>
                    {!seriesComplete && config.numBooks > 1 && (
                      <button className="btn btn-gold done-next" onClick={startNextBook} disabled={loading} style={{background:"linear-gradient(135deg,#2e7d32,#4caf50)",color:"#fff",boxShadow:"0 2px 8px rgba(46,125,50,.3)"}}>
                        {loading ? <><span className="spin" style={{borderTopColor:"#fff"}}/>{loadingMsg}</> : T.done_next(currentBookIdx, config.numBooks)}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="card">
              <div className="card-title">📚 {T.chaps_title}</div>
              {chapters.map((ch, chIdx) => (
                <div key={ch.id} className="ch-item">
                  <div className={`ch-head${openChapter===ch.id?" open":""}`} onClick={()=>setOpenChapter(openChapter===ch.id?null:ch.id)}>
                    <span className="ch-cap">Cap. {ch.id}</span>
                    <span style={{flex:1,fontSize:".98rem"}}>{ch.title.replace(/^(Capítulo|Chapter) \d+:\s*/,"")}</span>
                    <span className={`badge ${ch.status==="pending"?"bp":ch.status==="writing"?"bw":ch.status==="done"?"bd":"be"}`}>
                      {ch.status==="pending" && T.ch_pending}
                      {ch.status==="writing" && T.ch_writing}
                      {ch.status==="done" && T.ch_done}
                      {ch.status==="error" && T.ch_error}
                    </span>
                    {outlineAdherence[ch.id] && (
                      <span
                        className="ch-outline-badge"
                        title={outlineAdherence[ch.id].ok ? "Segue o roteiro ✅" : outlineAdherence[ch.id].detail}
                        style={{fontSize:".78rem",padding:"2px 7px",borderRadius:10,fontWeight:700,cursor:"default",
                          background: outlineAdherence[ch.id].ok ? "#e8f5e9" : "#fff3e0",
                          color: outlineAdherence[ch.id].ok ? "#2e7d32" : "#e65100",
                          border: `1px solid ${outlineAdherence[ch.id].ok ? "#a5d6a7" : "#ffcc80"}`}}
                      >
                        {outlineAdherence[ch.id].ok ? "✅ Roteiro" : "⚠️ Desvio"}
                      </span>
                    )}
                    <span style={{color:"rgba(229,226,225,.45)",fontSize:".85rem"}}>{openChapter===ch.id?"▲":"▼"}</span>
                  </div>
                  {openChapter===ch.id && ch.content && <div className="ch-body">{ch.content}</div>}
                  {continuityWarnings[ch.id] && (
                    <div style={{margin:"0 12px 10px",background:"#fff8e0",border:"1px solid #d4a94a",borderLeft:"3px solid #b8892a",borderRadius:4,padding:"8px 12px",fontSize:".78rem",color:"#6b5000",lineHeight:1.6}}>
                      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8}}>
                        <div><strong style={{display:"block",marginBottom:3}}>⚠️ Alerta de continuidade:</strong>{continuityWarnings[ch.id]}</div>
                        <button onClick={()=>setContinuityWarnings(p=>({...p,[ch.id]:null}))} style={{background:"none",border:"none",color:"#b8892a",cursor:"pointer",fontSize:".8rem",padding:"0 2px",flexShrink:0}} title="Dispensar">✕</button>
                      </div>
                      <div style={{marginTop:8,display:"flex",gap:8}}>
                        <button
                          onClick={()=>rewriteChapter(ch, chIdx, continuityWarnings[ch.id])}
                          disabled={fixingChapter !== null}
                          style={{background:"#b8892a",color:"#fff",border:"none",borderRadius:4,padding:"5px 12px",fontSize:".76rem",fontWeight:700,cursor:fixingChapter?'wait':'pointer',opacity:fixingChapter?0.6:1,display:"flex",alignItems:"center",gap:5}}
                        >
                          {fixingChapter===ch.id ? "✍️ Reescrevendo…" : "🔧 Corrigir automaticamente"}
                        </button>
                        <button
                          onClick={()=>setContinuityWarnings(p=>({...p,[ch.id]:null}))}
                          style={{background:"none",border:"1px solid #d4a94a",borderRadius:4,padding:"5px 10px",fontSize:".76rem",color:"#6b5000",cursor:"pointer"}}
                        >
                          Ignorar
                        </button>
                      </div>
                    </div>
                  )}
                  {outlineAdherence[ch.id] && !outlineAdherence[ch.id].ok && outlineAdherence[ch.id].detail && (
                    <div style={{margin:"0 12px 10px",background:"#fff8f0",border:"1px solid #ffcc80",borderLeft:"3px solid #e65100",borderRadius:4,padding:"8px 12px",fontSize:".78rem",color:"#6b3000",lineHeight:1.6}}>
                      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8}}>
                        <div><strong style={{display:"block",marginBottom:3}}>⚠️ Desvio do roteiro:</strong>{outlineAdherence[ch.id].detail}</div>
                        <button onClick={()=>setOutlineAdherence(p=>({...p,[ch.id]:null}))} style={{background:"none",border:"none",color:"#e65100",cursor:"pointer",fontSize:".8rem",padding:"0 2px",flexShrink:0}} title="Dispensar">✕</button>
                      </div>
                      <div style={{marginTop:8}}>
                        <button
                          onClick={()=>rewriteChapter(ch, chIdx, outlineAdherence[ch.id].detail)}
                          disabled={fixingChapter !== null}
                          style={{background:"#e65100",color:"#fff",border:"none",borderRadius:4,padding:"5px 12px",fontSize:".76rem",fontWeight:700,cursor:fixingChapter?'wait':'pointer',opacity:fixingChapter?0.6:1}}
                        >
                          {fixingChapter===ch.id ? "✍️ Reescrevendo…" : "🔧 Corrigir desvio"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ══ BOOK ANALYSIS MODAL ══ */}
      {showBookAnalysis && bookAnalysis && (
        <div style={{position:"fixed",inset:0,background:"rgba(26,18,8,.85)",zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",padding:20,overflowY:"auto"}}>
          <div style={{background:"#1c1b1b",border:"1px solid rgba(120,109,89,.6)",borderRadius:8,padding:"28px 24px",maxWidth:680,width:"100%",boxShadow:"0 24px 64px rgba(0,0,0,.5)",maxHeight:"90vh",overflowY:"auto"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.3rem",fontWeight:900,color:"#f3eee4"}}>📋 Análise do Livro</div>
              <button onClick={()=>setShowBookAnalysis(false)} style={{background:"none",border:"none",fontSize:"1.4rem",cursor:"pointer",color:"#d1c5b4"}}>✕</button>
            </div>

            {/* Overall */}
            {bookAnalysis.overall && (
              <div style={{background:"#20201f",border:"1px solid rgba(120,109,89,.45)",borderRadius:6,padding:"12px 14px",marginBottom:18,fontSize:".88rem",color:"#f0e9dd",lineHeight:1.7}}>
                <strong style={{display:"block",marginBottom:4,color:"#b8892a"}}>Avaliação Geral</strong>
                {bookAnalysis.overall}
              </div>
            )}

            {/* 3 Acts */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:10,marginBottom:18}}>
              {[
                {key:"ato1",label:"Ato 1 · Ruptura",color:"#8bcf8f",bg:"rgba(76,175,80,.16)"},
                {key:"ato2",label:"Ato 2 · Confronto",color:"#e9c176",bg:"rgba(233,193,118,.16)"},
                {key:"ato3",label:"Ato 3 · Transformação",color:"#ffb4ab",bg:"rgba(147,0,10,.20)"},
              ].map(a => {
                const act = bookAnalysis[a.key];
                if (!act) return null;
                const isOk = act.status === "ok";
                return (
                  <div key={a.key} style={{background:a.bg,borderRadius:6,padding:"10px 12px",border:`1px solid ${a.color}33`}}>
                    <div style={{fontSize:".72rem",fontWeight:800,color:a.color,marginBottom:5}}>{a.label}</div>
                    <div style={{fontSize:".75rem",fontWeight:700,color:isOk?"#b8edbc":"#ffd6d2",marginBottom:4}}>{isOk?"✅ OK":"⚠️ Problemas"}</div>
                    <div style={{fontSize:".78rem",color:"#e6dccb",lineHeight:1.6}}>{act.detail}</div>
                  </div>
                );
              })}
            </div>

            {/* Inconsistências */}
            {bookAnalysis.inconsistencias && bookAnalysis.inconsistencias.length > 0 && (
              <div style={{marginBottom:18}}>
                <div style={{fontSize:".8rem",fontWeight:800,color:"#8b2020",textTransform:"uppercase",letterSpacing:"1px",marginBottom:8}}>🔍 Inconsistências Detectadas</div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {bookAnalysis.inconsistencias.map((inc, i) => {
                    const gravColors = {
                      critica:  {bg:"rgba(229,57,53,.16)",border:"#e57373",dot:"#ff8a80",label:"Crítica"},
                      grave:    {bg:"rgba(251,140,0,.16)",border:"#ffb74d",dot:"#ffcc80",label:"Grave"},
                      media:    {bg:"rgba(249,168,37,.16)",border:"#ffd54f",dot:"#ffe082",label:"Média"},
                      menor:    {bg:"rgba(124,179,66,.16)",border:"#aed581",dot:"#c5e1a5",label:"Menor"},
                    };
                    const g = gravColors[inc.gravidade] || gravColors.media;
                    const caps = Array.isArray(inc.capitulos) ? inc.capitulos.map(n=>`Cap.${n}`).join(" → ") : "";
                    return (
                      <div key={i} style={{background:g.bg,border:`1px solid ${g.border}`,borderLeft:`3px solid ${g.dot}`,borderRadius:4,padding:"8px 12px"}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                          <span style={{fontSize:".68rem",fontWeight:800,color:g.dot,textTransform:"uppercase",letterSpacing:"1px"}}>{g.label}</span>
                          {caps && <span style={{fontSize:".68rem",color:"#f0e9dd",background:"rgba(0,0,0,.18)",borderRadius:3,padding:"1px 6px"}}>{caps}</span>}
                        </div>
                        <div style={{fontSize:".78rem",color:"#f0e9dd",lineHeight:1.6}}>{inc.descricao}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {bookAnalysis.inconsistencias && bookAnalysis.inconsistencias.length === 0 && (
              <div style={{background:"rgba(76,175,80,.16)",border:"1px solid rgba(126,217,132,.55)",borderRadius:6,padding:"10px 14px",marginBottom:18,fontSize:".83rem",color:"#c6f3cb",fontWeight:600}}>
                ✅ Nenhuma inconsistência detectada — narrativa consistente!
              </div>
            )}

            {/* Por Capítulo */}
            {bookAnalysis.chapters && bookAnalysis.chapters.length > 0 && (
              <>
                <div style={{fontSize:".8rem",fontWeight:800,color:"#b8892a",textTransform:"uppercase",letterSpacing:"1px",marginBottom:8}}>Por Capítulo</div>
                <div style={{display:"flex",flexDirection:"column",gap:4}}>
                  {bookAnalysis.chapters.map(c => (
                    <div key={c.id} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"6px 10px",borderRadius:4,background:c.ok?"rgba(76,175,80,.14)":"rgba(147,0,10,.2)",border:`1px solid ${c.ok?"rgba(126,217,132,.48)":"rgba(255,180,171,.5)"}`}}>
                      <span style={{fontSize:".72rem",fontWeight:800,color:"#f0e9dd",minWidth:52,paddingTop:1}}>Cap. {c.id}</span>
                      <span style={{fontSize:".8rem",color:c.ok?"#c6f3cb":"#ffd6d2",fontWeight:700,minWidth:18}}>{c.ok?"✅":"⚠️"}</span>
                      <span style={{fontSize:".78rem",color:"#f0e9dd",lineHeight:1.5,flex:1}}>{c.ok ? "Segue o roteiro" : c.issue}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div style={{marginTop:20,display:"flex",justifyContent:"flex-end",gap:10}}>
              <button
                onClick={analyzeFullBook}
                disabled={analyzingBook}
                style={{background:"#20201f",border:"1px solid #d4a94a",borderRadius:4,padding:"8px 16px",fontSize:".82rem",fontWeight:700,color:"#e9c176",cursor:"pointer"}}
              >
                {analyzingBook ? "Analisando…" : "🔄 Reanalisar"}
              </button>
              <button
                onClick={()=>setShowBookAnalysis(false)}
                style={{background:"#b8892a",color:"#fff",border:"none",borderRadius:4,padding:"8px 18px",fontSize:".82rem",fontWeight:700,cursor:"pointer"}}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ BOOK TRANSITION SCREEN ══ */}
      {showBookTransition && transitionData && (
        <div style={{position:"fixed",inset:0,background:"rgba(26,18,8,.85)",zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",padding:20,overflowY:"auto"}}>
          <div style={{background:"#1c1b1b",border:"1px solid rgba(120,109,89,.6)",borderRadius:8,padding:"32px 28px",maxWidth:640,width:"100%",boxShadow:"0 24px 64px rgba(0,0,0,.5)",maxHeight:"90vh",overflowY:"auto"}}>

            {/* Header */}
            <div style={{textAlign:"center",marginBottom:28}}>
              <div style={{fontSize:"2rem",marginBottom:8}}>📚</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.6rem",fontWeight:900,color:"#f3eee4"}}>
                Livro {currentBookIdx} Concluído!
              </div>
              <div style={{fontSize:".95rem",color:"#d1c5b4",marginTop:6,fontStyle:"italic"}}>
                Preparando o terreno para o Livro {transitionData.nextIdx}
                {transitionData.nextTitle && <strong style={{color:"#b8892a"}}> — {transitionData.nextTitle}</strong>}
              </div>
            </div>

            {/* Canonical summary — editable */}
            <div style={{marginBottom:20}}>
              <label style={{fontSize:".72rem",fontWeight:800,color:"#b8892a",textTransform:"uppercase",letterSpacing:"1px",display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
                📋 Resumo Canônico do Livro {currentBookIdx}
                <span style={{fontSize:".65rem",fontWeight:400,color:"#d1c5b4",fontStyle:"italic",textTransform:"none",letterSpacing:0}}>(editável — será a memória permanente da série)</span>
              </label>
              <textarea
                value={editableTransitionSummary}
                onChange={e=>setEditableTransitionSummary(e.target.value)}
                rows={10}
                style={{width:"100%",fontFamily:"'Crimson Pro',Georgia,serif",fontSize:".95rem",lineHeight:1.7,padding:"12px 14px",border:"1px solid rgba(120,109,89,.8)",borderRadius:4,background:"#20201f",color:"#f0e9dd",resize:"vertical"}}
              />
              <div style={{fontSize:".72rem",color:"#d1c5b4",marginTop:4}}>A IA gerou este resumo. Edite se quiser corrigir, acrescentar detalhes ou ajustar o que ficou canônico.</div>
            </div>

            {/* Hooks planted (from Bible) */}
            {bibliaPanel.nextHooks && (
              <div style={{marginBottom:20,background:"rgba(233,193,118,.14)",border:"1px solid rgba(212,169,74,.6)",borderLeft:"3px solid #b8892a",borderRadius:4,padding:"12px 14px"}}>
                <div style={{fontSize:".72rem",fontWeight:800,color:"#b8892a",textTransform:"uppercase",letterSpacing:"1px",marginBottom:6}}>🪝 Ganchos Plantados na Bíblia</div>
                <div style={{fontSize:".9rem",color:"#f0e9dd",lineHeight:1.65}}>{bibliaPanel.nextHooks}</div>
              </div>
            )}

            {/* Previous books trail */}
            {canonicalSummaries.length > 0 && (
              <div style={{marginBottom:20,background:"#20201f",border:"1px solid rgba(120,109,89,.55)",borderRadius:4,padding:"12px 14px"}}>
                <div style={{fontSize:".72rem",fontWeight:800,color:"#d1c5b4",textTransform:"uppercase",letterSpacing:"1px",marginBottom:8}}>📖 Livros anteriores na série</div>
                {canonicalSummaries.map((s,i) => (
                  <div key={i} style={{fontSize:".82rem",color:"#f0e9dd",lineHeight:1.6,marginBottom:i<canonicalSummaries.length-1?8:0}}>
                    <strong style={{color:"#b8892a"}}>Livro {i+1}{(config.bookTitles && config.bookTitles[i]) ? ` — ${config.bookTitles[i]}` : ""}:</strong> {s.slice(0,180)}…
                  </div>
                ))}
              </div>
            )}

            {/* Author notes for next book */}
            <div style={{marginBottom:24}}>
              <label style={{fontSize:".72rem",fontWeight:800,color:"#b8892a",textTransform:"uppercase",letterSpacing:"1px",display:"block",marginBottom:8}}>
                ✍️ Suas Notas para o Livro {transitionData.nextIdx} <span style={{fontWeight:400,color:"#9a8a6a",fontStyle:"italic",textTransform:"none",letterSpacing:0}}>(opcional)</span>
              </label>
              <textarea
                value={nextBookNotes}
                onChange={e=>setNextBookNotes(e.target.value)}
                rows={4}
                placeholder={`Ex: No Livro ${transitionData.nextIdx}, quero focar em revelar a origem da Ruptura. O protagonista deve começar já diferente, mais cansado. A aliada Suri precisa de um arco próprio neste volume...`}
                style={{width:"100%",fontFamily:"'Crimson Pro',Georgia,serif",fontSize:".95rem",lineHeight:1.7,padding:"12px 14px",border:"1px solid rgba(120,109,89,.8)",borderRadius:4,background:"#20201f",color:"#f0e9dd",resize:"vertical"}}
              />
              <div style={{fontSize:".72rem",color:"#d1c5b4",marginTop:4}}>Estas notas são injetadas no prompt de geração do roteiro como prioridade máxima.</div>
            </div>

            {/* Actions */}
            <div style={{display:"flex",gap:10,justifyContent:"space-between",flexWrap:"wrap"}}>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowBookTransition(false)}>← Voltar ao Livro {currentBookIdx}</button>
              <button className="btn btn-gold" onClick={proceedToNextBook} disabled={loading} style={{fontSize:"1rem",padding:"12px 24px"}}>
                {loading ? <><span className="spin"/>{loadingMsg}</> : `✨ Gerar Roteiro do Livro ${transitionData.nextIdx} →`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ PRE-OUTLINE MODAL ══ */}
      {/* ══ MODAL: BÍBLIA COMPLETA ══ */}
      {showBibliaReview && (
        <div style={{position:"fixed",inset:0,background:"rgba(10,7,3,.82)",zIndex:910,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-start",padding:"0"}}>
          <div style={{width:"100%",maxWidth:720,height:"100%",display:"flex",flexDirection:"column",background:"#fdf8f0"}}>

            {/* Header fixo */}
            <div style={{padding:"20px 28px 16px",borderBottom:"2px solid #e8d8b0",background:"#1a1208",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
              <div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.3rem",fontWeight:900,color:"#d4a94a"}}>📋 Bíblia do Livro</div>
                <div style={{fontSize:".75rem",color:"#9a8a6a",marginTop:2}}>Revise todas as informações antes de autorizar o roteiro</div>
              </div>
              <button onClick={()=>setShowBibliaReview(false)} style={{background:"none",border:"1px solid #4a3a20",color:"#9a8a6a",borderRadius:99,width:32,height:32,cursor:"pointer",fontSize:"1rem",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
            </div>

            {/* Conteúdo rolável */}
            <div style={{flex:1,overflowY:"auto",padding:"24px 28px 12px"}}>
              {PANEL_SECTIONS.map((group, gi) => {
                const filledFields = group.fields.filter(f => bibliaPanel[f.key]?.trim());
                if (filledFields.length === 0) return null;
                return (
                  <div key={gi} style={{marginBottom:28}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
                      <span style={{fontSize:"1.1rem"}}>{group.icon}</span>
                      <span style={{fontFamily:"'Playfair Display',serif",fontSize:"1rem",fontWeight:800,color:"#1a1208",textTransform:"uppercase",letterSpacing:"1px"}}>{group.label.replace(/^[^\s]+\s/,"")}</span>
                      <div style={{flex:1,height:1,background:"#e8d8b0",marginLeft:4}}/>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:12}}>
                      {filledFields.map((f, fi) => (
                        <div key={fi} style={{background:"#fff",border:"1px solid #ede0c0",borderRadius:8,padding:"12px 16px"}}>
                          <div style={{fontSize:".67rem",fontWeight:800,color:"#b8892a",textTransform:"uppercase",letterSpacing:"1.2px",marginBottom:5}}>{f.label}</div>
                          <div style={{fontSize:".93rem",color:"#2a1e0a",lineHeight:1.7,whiteSpace:"pre-wrap"}}>{bibliaPanel[f.key]}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {/* Campos vazios */}
              {(() => {
                const allFilled = PANEL_SECTIONS.flatMap(g=>g.fields).filter(f=>!bibliaPanel[f.key]?.trim());
                if (allFilled.length === 0) return null;
                return (
                  <div style={{background:"#fff8ec",border:"1px dashed #d4a94a",borderRadius:8,padding:"12px 16px",marginBottom:24}}>
                    <div style={{fontSize:".7rem",fontWeight:700,color:"#b8892a",marginBottom:6}}>⚠️ Campos ainda não preenchidos ({allFilled.length})</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                      {allFilled.map((f,i)=>(
                        <span key={i} style={{background:"rgba(212,169,74,.15)",borderRadius:99,padding:"2px 10px",fontSize:".72rem",color:"#7a6a50"}}>{f.label}</span>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Footer fixo — ação */}
            <div style={{padding:"16px 28px 20px",borderTop:"2px solid #e8d8b0",background:"#fdf8f0",flexShrink:0,display:"flex",flexDirection:"column",gap:10}}>
              {bibliaReviewed ? (
                <div style={{display:"flex",gap:10}}>
                  <button
                    onClick={()=>{setShowBibliaReview(false); buildOutlineFromChat();}}
                    disabled={loading}
                    style={{flex:1,background:"linear-gradient(135deg,#b8892a,#d4a94a)",border:"none",borderRadius:8,padding:"13px 20px",color:"#1a1208",fontFamily:"'Crimson Pro',Georgia,serif",fontSize:"1rem",fontWeight:800,cursor:"pointer"}}>
                    {loading ? <><span className="spin"/> Criando...</> : "✨ Criar Roteiro →"}
                  </button>
                  <button onClick={()=>setShowBibliaReview(false)} style={{background:"none",border:"1px solid #ddd0b0",borderRadius:8,padding:"13px 18px",color:"#7a6a50",cursor:"pointer",fontSize:".88rem"}}>Fechar</button>
                </div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <button
                    onClick={()=>{ setbibliaReviewed(true); setShowBibliaReview(false); }}
                    style={{background:"linear-gradient(135deg,#2e7d32,#4caf50)",border:"none",borderRadius:8,padding:"14px 20px",color:"#fff",fontFamily:"'Crimson Pro',Georgia,serif",fontSize:"1rem",fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                    ✅ Autorizar Produção do Roteiro
                  </button>
                  <div style={{display:"flex",gap:8}}>
                    <button
                      onClick={()=>{ setShowBibliaReview(false); reviewBible(); }}
                      disabled={bibliaChatLoading}
                      style={{flex:1,background:"none",border:"1px solid #4caf50",borderRadius:8,padding:"10px 16px",color:"#2e7d32",cursor:"pointer",fontSize:".88rem",fontWeight:600}}>
                      🔍 Revisar com IA primeiro
                    </button>
                    <button onClick={()=>setShowBibliaReview(false)} style={{background:"none",border:"1px solid #ddd0b0",borderRadius:8,padding:"10px 16px",color:"#7a6a50",cursor:"pointer",fontSize:".88rem"}}>Fechar</button>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {showPreOutlineModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(26,18,8,.75)",zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"#1c1b1b",border:"1px solid rgba(120,109,89,.6)",borderRadius:10,padding:"32px 28px",maxWidth:480,width:"100%",boxShadow:"0 24px 64px rgba(0,0,0,.45)"}}>

            <div style={{textAlign:"center",marginBottom:24}}>
              <div style={{fontSize:"2rem",marginBottom:10}}>📋</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.4rem",fontWeight:900,color:"#f3eee4",marginBottom:8}}>
                Antes de criar o Roteiro
              </div>
              <div style={{fontSize:".95rem",color:"#e6dccb",lineHeight:1.7}}>
                Quer que a IA revise sua Bíblia antes? Ela verifica consistência, preenche lacunas e garante que o roteiro será gerado com a base mais sólida possível.
              </div>
            </div>

            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {/* Option 1 — Review first */}
              <button
                onClick={()=>{ setShowPreOutlineModal(false); reviewBible(); }}
                disabled={bibliaChatLoading}
                style={{background:"linear-gradient(135deg,#2e7d32,#4caf50)",border:"none",color:"#fff",borderRadius:7,padding:"14px 20px",cursor:"pointer",fontFamily:"'Crimson Pro',Georgia,serif",fontSize:"1rem",fontWeight:700,display:"flex",alignItems:"center",gap:10,textAlign:"left",boxShadow:"0 2px 8px rgba(46,125,50,.3)"}}>
                <span style={{fontSize:"1.3rem",flexShrink:0}}>🔍</span>
                <div>
                  <div style={{fontWeight:800}}>Revisar a Bíblia primeiro</div>
                  <div style={{fontSize:".8rem",opacity:.9,fontWeight:400,marginTop:2}}>Recomendado — a IA corrige inconsistências e valida todos os campos</div>
                </div>
              </button>

              {/* Option 2 — Go directly */}
              <button
                onClick={()=>{ setShowPreOutlineModal(false); setbibliaReviewed(true); buildOutlineFromChat(); }}
                disabled={loading}
                style={{background:"linear-gradient(135deg,#b8892a,#d4a94a)",border:"none",color:"#1a1208",borderRadius:7,padding:"14px 20px",cursor:"pointer",fontFamily:"'Crimson Pro',Georgia,serif",fontSize:"1rem",fontWeight:700,display:"flex",alignItems:"center",gap:10,textAlign:"left",boxShadow:"0 2px 8px rgba(184,137,42,.25)"}}>
                <span style={{fontSize:"1.3rem",flexShrink:0}}>✨</span>
                <div>
                  <div style={{fontWeight:800}}>Ir direto ao Roteiro</div>
                  <div style={{fontSize:".8rem",opacity:.8,fontWeight:400,marginTop:2}}>A Bíblia já está completa — gerar o roteiro agora</div>
                </div>
              </button>

              {/* Cancel */}
              <button
                onClick={()=>setShowPreOutlineModal(false)}
                style={{background:"none",border:"1px solid #ddd0b0",color:"#7a6a50",borderRadius:7,padding:"10px 20px",cursor:"pointer",fontFamily:"'Crimson Pro',Georgia,serif",fontSize:".9rem",marginTop:4}}>
                Cancelar — continuar na Bíblia
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ BÍBLIA UPDATE TOAST ══ */}
      {bibliaUpdateToast && (
        <div className="biblia-update-toast">
          <span>✨</span>
          <span>{bibliaUpdateToast.count === 1 ? "1 campo atualizado" : `${bibliaUpdateToast.count} campos atualizados`} na Bíblia</span>
        </div>
      )}

      {/* ══ DRAFT READER MODAL ══ */}
      {/* ══ LEITOR LIVRO COMPLETO ══ */}
      {showBookReader && (()=>{
        const dark = bookReaderDark;
        const fontSizes   = [".88rem","1rem","1.07rem","1.15rem","1.25rem","1.38rem","1.52rem"];
        const lineHeights = [1.6, 1.85, 2.0, 2.2];
        const maxWidths   = [560, 700, 860];
        const fSz  = fontSizes[bookReaderFontIdx];
        const lH   = lineHeights[bookReaderLineHIdx];
        const mxW  = maxWidths[bookReaderWidthIdx];
        const bri  = bookReaderBrightness;

        // Paletas claro/escuro
        const bg       = dark ? "#131313"               : "#faf6ef";
        const bgBar    = dark ? "rgba(19,19,19,.97)"      : "rgba(250,246,239,.97)";
        const bgMenu   = dark ? "#1c1b1b"               : "#fff8ee";
        const border   = dark ? "#3a2a10"               : "#e0d0b0";
        const gold     = "#b8892a";
        const titleClr = dark ? "#f6f1e8"               : "#1a1208";
        const textClr  = dark ? "#ece3d4"               : "#2a1e0e";
        const subClr   = dark ? "#ddb977"               : "#7a5a20";
        const roleClr  = dark ? "#bfa57a"               : "#9a7a40";
        const metaClr  = dark ? "#c9b394"               : "#7a6040";
        const sepClr   = dark ? "#4a3820"               : "#d4b880";
        const barTitle = dark ? "#d4a94a"               : "#8b5e10";
        const menuText = dark ? "#e0d4be"               : "#2a1e0e";
        const menuSub  = dark ? "#c9b394"               : "#8a7050";

        const stripMdR = (t="") => t
          .replace(/\*\*\*(.+?)\*\*\*/g,"$1").replace(/\*\*(.+?)\*\*/g,"$1")
          .replace(/\*(.+?)\*/g,"$1").replace(/_{2}(.+?)_{2}/g,"$1")
          .replace(/^#+\s+/gm,"").replace(/^[-*]\s+/gm,"").trim();
        const translateRoleR = (r="") => {
          const m={"protagonist":"Protagonista","protagonista":"Protagonista","antagonist":"Antagonista","antagonista":"Antagonista","supporting":"Coadjuvante","support":"Coadjuvante","mentor":"Mentor","ally":"Aliado","love interest":"Interesse Amoroso","sidekick":"Parceiro","villain":"Vilao"};
          return m[r.toLowerCase().trim()] || r;
        };

        const seriesName = config.chronicleName || "";
        const bookTitle  = (config.bookTitles && config.bookTitles[currentBookIdx-1]) || config.title || "";
        const bookSuffix = config.numBooks > 1 ? ` · Livro ${currentBookIdx}` : "";
        const doneChaps  = chapters.filter(c=>c.status==="done"&&c.content);
        const hasChars   = characters && characters.length > 0;

        // ── Paginação ────────────────────────────────────────────────────
        const pages = [
          'cover',
          ...(hasChars ? ['chars'] : []),
          ...doneChaps.map((ch, chIdx) => ({ ch, chIdx }))
        ];
        const totalPages  = pages.length;
        const safePage    = Math.min(bookReaderPage, totalPages - 1);
        const curPageData = pages[safePage];
        const curIsChap   = curPageData && typeof curPageData === 'object';

        // ── Marcador de leitura ──────────────────────────────────────────
        const bmKey  = 'autoria_bm_' + (currentProjectId || 'default');
        void bookReaderBmTs; // garante re-render ao salvar
        const _bmRaw = localStorage.getItem(bmKey);
        let bmData = null;
        try { bmData = _bmRaw ? JSON.parse(_bmRaw) : null; } catch(e) { bmData = null; }
        // retrocompatibilidade: formato antigo era só um número
        if (!bmData && _bmRaw) { const n = parseInt(_bmRaw); if (n) bmData = { chId: n }; }
        const bmChId    = bmData?.chId ?? null;
        const bmPageIdx = bmChId ? pages.findIndex(p => p && typeof p === 'object' && p.ch && p.ch.id === bmChId) : -1;
        const saveBookmark = (chId) => {
          const d = { chId };
          if (bookReaderPageMode) {
            d.pageIdx = safePage;
            const pEl = document.querySelector('.reader-content-paged');
            d.pageScrollTop = pEl ? pEl.scrollTop : 0;
          } else {
            const el = document.getElementById('reader-scroll-outer');
            d.scrollTop = el ? el.scrollTop : 0;
          }
          localStorage.setItem(bmKey, JSON.stringify(d));
          setBookReaderBmTs(Date.now());
        };
        const removeBookmark = () => { localStorage.removeItem(bmKey); setBookReaderBmTs(Date.now()); };
        // chId atual conforme modo
        const curChId = bookReaderPageMode
          ? (curIsChap ? curPageData.ch.id : null)
          : bookReaderScrollChId;
        const hasBm   = !!bmData;
        // true quando o usuário está no mesmo capítulo/página do marcador
        const bmAtCurPos = hasBm && (
          bookReaderPageMode
            ? bmData?.pageIdx === safePage
            : bookReaderScrollChId === bmChId
        );

        // ── Progresso ────────────────────────────────────────────────────
        const progressPct = bookReaderPageMode && totalPages > 1
          ? Math.round(((safePage + bookReaderPageScroll) / (totalPages - 1)) * 100)
          : bookReaderScrollPct;

        // ── Página corrente (nome curto) ─────────────────────────────────
        const pageName = safePage === 0 ? "Capa"
          : curPageData === 'chars' ? "Personagens"
          : curIsChap   ? `Cap. ${curPageData.ch.id} de ${doneChaps.length}`
          : "";

        // ── Render helpers ───────────────────────────────────────────────
        const renderCover = () => (
          <div style={{textAlign:"center",minHeight:bookReaderPageMode?"auto":"60vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",borderBottom:bookReaderPageMode?"none":`1px solid ${border}`,marginBottom:bookReaderPageMode?0:72,paddingBottom:bookReaderPageMode?0:72}}>
            {seriesName && seriesName!==bookTitle && (
              <div style={{fontFamily:"'Crimson Pro',Georgia,serif",fontSize:fSz,fontStyle:"italic",color:subClr,marginBottom:16,letterSpacing:1}}>{seriesName}</div>
            )}
            <div style={{width:60,height:1,background:gold,marginBottom:28}}/>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:`calc(${fSz} * 2.2)`,fontWeight:900,color:titleClr,lineHeight:1.2,marginBottom:bookSuffix?10:0}}>{bookTitle||config.title}</div>
            {bookSuffix && <div style={{fontFamily:"'Crimson Pro',Georgia,serif",fontSize:`calc(${fSz} * 1.1)`,color:gold,marginTop:8}}>{bookSuffix.trim()}</div>}
            {config.subtitle && <div style={{fontFamily:"'Crimson Pro',Georgia,serif",fontSize:fSz,fontStyle:"italic",color:subClr,marginTop:14}}>{config.subtitle}</div>}
            <div style={{margin:"32px 0",color:gold,letterSpacing:12,fontSize:"1rem"}}>* * *</div>
            {config.genre && <div style={{fontSize:".72rem",letterSpacing:"4px",textTransform:"uppercase",color:roleClr,marginBottom:20}}>{config.genre}</div>}
            {config.synopsis && <div style={{fontFamily:"'Crimson Pro',Georgia,serif",fontSize:fSz,fontStyle:"italic",color:metaClr,lineHeight:lH,maxWidth:520,textAlign:"center",margin:"0 auto 32px"}}>"{config.synopsis}"</div>}
            {config.author && <div style={{fontFamily:"'Playfair Display',serif",fontSize:fSz,color:gold,marginTop:8}}>por {config.author}</div>}
          </div>
        );

        const renderChars = () => (
          <div style={{marginBottom:bookReaderPageMode?0:80,borderBottom:bookReaderPageMode?"none":`1px solid ${border}`,paddingBottom:bookReaderPageMode?0:72}}>
            <div style={{textAlign:"center",marginBottom:40}}>
              <div style={{fontSize:".65rem",letterSpacing:"5px",textTransform:"uppercase",color:roleClr,marginBottom:12}}>* * *</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.5rem",fontWeight:700,color:titleClr}}>Personagens</div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:28}}>
              {characters.map((c,i)=>(
                <div key={i} style={{borderLeft:`2px solid ${gold}`,paddingLeft:20}}>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.05rem",fontWeight:700,color:gold,marginBottom:3}}>{c.nome||c.name}</div>
                  <div style={{fontSize:".7rem",letterSpacing:"2px",textTransform:"uppercase",color:roleClr,marginBottom:8}}>{translateRoleR(c.papel||c.role||"")}</div>
                  {(c.personalidade||c.personality) && <div style={{fontFamily:"'Crimson Pro',Georgia,serif",fontSize:fSz,fontStyle:"italic",color:textClr,lineHeight:lH,marginBottom:4}}>{stripMdR(c.personalidade||c.personality||"")}</div>}
                  {(c.motivacao||c.motivation) && <div style={{fontFamily:"'Crimson Pro',Georgia,serif",fontSize:fSz,color:metaClr,lineHeight:lH}}><span style={{color:roleClr,fontWeight:600}}>Motivação:</span> {stripMdR(c.motivacao||c.motivation||"")}</div>}
                  {(c.arco||c.arc) && <div style={{fontFamily:"'Crimson Pro',Georgia,serif",fontSize:fSz,color:metaClr,lineHeight:lH,marginTop:2}}><span style={{color:roleClr,fontWeight:600}}>Arco:</span> {stripMdR(c.arco||c.arc||"")}</div>}
                </div>
              ))}
            </div>
          </div>
        );

        const renderChapter = (ch, chIdx, withDivider) => {
          const paras = ch.content.split(/\n+/).map(p=>stripMdR(p.trim())).filter(Boolean);
          const chTitle = ch.title.replace(/^(Capítulo|Chapter)\s*\d+:?\s*/i,"");
          return (
            <div key={ch.id} style={{marginBottom:bookReaderPageMode?0:80}}>
              <div style={{textAlign:"center",marginBottom:40}}>
                <div style={{fontSize:".65rem",letterSpacing:"5px",textTransform:"uppercase",color:roleClr,marginBottom:10}}>Capítulo {ch.id}</div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.6rem",fontWeight:700,color:titleClr,lineHeight:1.25}}>{chTitle}</div>
                <div style={{marginTop:18,color:gold,letterSpacing:10,fontSize:".9rem"}}>* * *</div>
              </div>
              <div style={{fontFamily:"'Crimson Pro',Georgia,serif",fontSize:fSz,lineHeight:lH,color:textClr,textAlign:"justify",transition:"font-size .2s, line-height .2s"}}>
                {paras.map((para, pi) => (
                  <p key={pi} style={{margin:"0 0 1.1em 0",textIndent:pi===0?0:"1.8em"}}>
                    {pi===0 ? (
                      <>
                        <span style={{fontFamily:"'Playfair Display',serif",fontSize:"3.2em",fontWeight:900,color:gold,float:"left",lineHeight:.75,marginRight:".1em",marginTop:".05em"}}>{para.charAt(0)}</span>
                        {para.slice(1)}
                      </>
                    ) : para}
                  </p>
                ))}
              </div>
              {withDivider && (
                <div style={{textAlign:"center",marginTop:56,color:sepClr,letterSpacing:12,fontSize:"1rem"}}>* * *</div>
              )}
            </div>
          );
        };

        return (
          <div
            id="reader-scroll-outer"
            style={{position:"fixed",inset:0,zIndex:820,background:bg,overflowY:bookReaderPageMode?"hidden":"auto",transition:"background .3s"}}
            onScroll={!bookReaderPageMode ? (e => {
              const el = e.target;
              const pct = el.scrollHeight <= el.clientHeight ? 0 : Math.round((el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100);
              setBookReaderScrollPct(pct);
              // Detecta seção visível no topo da tela
              const threshold = 130; // px abaixo do header+strip
              const ids = [
                {id:'reader-cover', name:'Capa', chId: null},
                ...(hasChars ? [{id:'reader-chars', name:'Personagens', chId: null}] : []),
                ...doneChaps.map(ch => ({id:`reader-ch-${ch.id}`, name:`Cap. ${ch.id} de ${doneChaps.length}`, chId: ch.id}))
              ];
              let sec = ids[0]?.name || ''; let secChId = null;
              for (const s of ids) {
                const node = document.getElementById(s.id);
                if (node && node.getBoundingClientRect().top <= threshold) { sec = s.name; secChId = s.chId; }
              }
              setBookReaderScrollSec(sec);
              setBookReaderScrollChId(secChId);
            }) : undefined}
          >

            {/* ── Barra superior ── */}
            <div style={{position:"fixed",top:0,left:0,right:0,height:50,background:bgBar,borderBottom:`1px solid ${border}`,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 12px",zIndex:822,backdropFilter:"blur(8px)",gap:6}}>
              <div className="reader-topbar-title" style={{color:barTitle,fontFamily:"'Playfair Display',serif",fontSize:".9rem",fontWeight:700}}>{bookTitle||config.title}</div>

              {/* Centro: seção atual + ir para marcador */}
              <div className="reader-topbar-center" style={{color:menuSub}}>
                <span style={{color:gold,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontSize:".72rem"}}>{bookReaderPageMode ? pageName : (bookReaderScrollSec || 'Capa')}</span>
                {hasBm && !bmAtCurPos && (
                  <button
                    className="reader-bm-continue"
                    onClick={()=>{
                      if (bookReaderPageMode) {
                        const targetPage      = bmData?.pageIdx != null ? bmData.pageIdx : bmPageIdx;
                        const targetScrollTop = bmData?.pageScrollTop ?? 0;
                        setBookReaderPageDir(0); setBookReaderPageScroll(0);
                        setBookReaderPage(targetPage);
                        if (targetScrollTop > 0) {
                          const tryScroll = (n) => {
                            const el = document.querySelector('.reader-content-paged');
                            if (el && el.scrollHeight > 200) el.scrollTo({ top: targetScrollTop, behavior: 'instant' });
                            else if (n > 0) setTimeout(() => tryScroll(n - 1), 60);
                          };
                          setTimeout(() => tryScroll(5), 60);
                        }
                      } else {
                        const outer = document.getElementById('reader-scroll-outer');
                        if (outer && bmData?.scrollTop > 0) outer.scrollTo({ top: bmData.scrollTop, behavior: 'smooth' });
                        else if (bmChId) { const el = document.getElementById('reader-ch-' + bmChId); if (el) el.scrollIntoView({ behavior: 'smooth' }); }
                      }
                    }}
                    title="Ir para onde parei"
                    style={{background:"none",border:`1px solid ${gold}`,borderRadius:99,color:gold,cursor:"pointer",fontWeight:700,flexShrink:0}}>🔖 Continuar</button>
                )}
              </div>

              {/* Botões direita */}
              <div className="reader-topbar-right">
                {/* 🔖 Salvar posição — sempre visível */}
                <button
                  onClick={()=>{ bmAtCurPos ? removeBookmark() : saveBookmark(curChId); }}
                  title={bmAtCurPos ? "Toque para remover marcador" : hasBm ? "Mover marcador para aqui" : "Salvar posição atual"}
                  style={{background:bmAtCurPos?gold:(hasBm?(dark?"#2a1f08":"#fff8ec"):"none"),border:`1px solid ${hasBm?gold:border}`,borderRadius:6,width:36,height:36,cursor:"pointer",color:bmAtCurPos?"#fff":(hasBm?gold:menuSub),fontSize:"1rem",display:"flex",alignItems:"center",justifyContent:"center",transition:"all .2s",flexShrink:0}}
                >🔖</button>
                <button onClick={()=>setBookReaderMenu(m=>!m)} title="Configurações de leitura"
                  style={{background:bookReaderMenu?(dark?"#3a2a10":"#fde9c4"):"none",border:`1px solid ${bookReaderMenu?gold:border}`,borderRadius:6,width:36,height:36,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4,transition:"all .2s",flexShrink:0}}>
                  {[0,1,2].map(i=>(
                    <span key={i} style={{display:"block",width:16,height:1.5,background:bookReaderMenu?gold:barTitle,borderRadius:2,transition:"all .2s"}}/>
                  ))}
                </button>
                <button className="reader-close-btn" onClick={()=>{setShowBookReader(false);setBookReaderMenu(false);}}>✕</button>
              </div>
            </div>

            {/* ── Barra de progresso (linha fina abaixo do topbar) ── */}
            <div style={{position:"fixed",top:50,left:0,right:0,height:16,background:dark?"#1e1508":"#ede0c8",zIndex:823,display:"flex",flexDirection:"column",justifyContent:"flex-end"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"center",lineHeight:"13px",gap:6}}>
                {bookReaderPageMode && <span className="reader-swipe-hint" style={{fontSize:".5rem",color:menuSub,opacity:.45,letterSpacing:".3px",fontStyle:"italic"}}>← retroceder</span>}
                <span style={{fontSize:".58rem",fontWeight:700,color:gold,letterSpacing:".5px"}}>{progressPct}%</span>
                {bookReaderPageMode && <span className="reader-swipe-hint" style={{fontSize:".5rem",color:menuSub,opacity:.45,letterSpacing:".3px",fontStyle:"italic"}}>avançar →</span>}
              </div>
              <div style={{height:3,background:dark?"#3a2e1a":"#d4c0a0",position:"relative",overflow:"hidden"}}>
                <div style={{position:"absolute",top:0,left:0,height:"100%",background:gold,width:`${progressPct}%`,transition:bookReaderPageMode?"width .35s ease":"none",borderRadius:"0 2px 2px 0"}}/>
              </div>
            </div>

            {/* ── Painel de configurações (dropdown) ── */}
            {bookReaderMenu && (
              <div style={{position:"fixed",top:66,right:0,width:280,background:bgMenu,border:`1px solid ${border}`,borderTop:"none",borderRadius:"0 0 0 10px",zIndex:824,padding:"18px 18px 20px",boxShadow:dark?"0 8px 32px rgba(0,0,0,.6)":"0 8px 32px rgba(0,0,0,.15)",maxHeight:"80vh",overflowY:"auto"}}>

                {/* Modo de leitura */}
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:".68rem",letterSpacing:"2px",textTransform:"uppercase",color:menuSub,marginBottom:8}}>Modo de Leitura</div>
                  <div style={{display:"flex",gap:6}}>
                    {[["📜","Rolagem",false],["📄","Paginado",true]].map(([icon,lbl,val])=>(
                      <button key={lbl} onClick={()=>setBookReaderPageMode(val)}
                        style={{flex:1,padding:"9px 4px",fontSize:".75rem",fontWeight:bookReaderPageMode===val?700:400,cursor:"pointer",borderRadius:6,border:`1px solid ${bookReaderPageMode===val?gold:border}`,background:bookReaderPageMode===val?(dark?"#3a2a10":"#fde9c4"):"none",color:bookReaderPageMode===val?gold:menuSub,transition:"all .15s",display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                        <span style={{fontSize:"1.15rem"}}>{icon}</span>
                        <span>{lbl}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{height:1,background:border,margin:"4px 0 16px"}}/>

                {/* Tema */}
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:".68rem",letterSpacing:"2px",textTransform:"uppercase",color:menuSub,marginBottom:8}}>Tema</div>
                  <button onClick={()=>setBookReaderDark(d=>!d)} style={{width:"100%",padding:"8px 12px",background:"none",border:`1px solid ${border}`,borderRadius:6,cursor:"pointer",color:menuText,fontSize:".85rem",fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",gap:8,transition:"all .2s"}}>
                    {dark ? "☀️  Mudar para Modo Claro" : "🌙  Mudar para Modo Escuro"}
                  </button>
                </div>

                <div style={{height:1,background:border,margin:"4px 0 16px"}}/>

                {/* Tamanho da fonte */}
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:".68rem",letterSpacing:"2px",textTransform:"uppercase",color:menuSub,marginBottom:8}}>
                    Tamanho da Fonte &nbsp;<span style={{color:gold,fontWeight:700}}>{fSz}</span>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <button onClick={()=>setBookReaderFontIdx(i=>Math.max(0,i-1))} disabled={bookReaderFontIdx===0}
                      style={{width:36,height:36,borderRadius:6,border:`1px solid ${border}`,background:"none",cursor:"pointer",fontSize:"1.1rem",fontWeight:700,color:bookReaderFontIdx===0?menuSub:gold,lineHeight:1}}>A−</button>
                    <div style={{flex:1,height:4,background:border,borderRadius:2,position:"relative"}}>
                      <div style={{position:"absolute",left:0,top:0,height:"100%",width:`${(bookReaderFontIdx/(fontSizes.length-1))*100}%`,background:gold,borderRadius:2,transition:"width .2s"}}/>
                    </div>
                    <button onClick={()=>setBookReaderFontIdx(i=>Math.min(fontSizes.length-1,i+1))} disabled={bookReaderFontIdx===fontSizes.length-1}
                      style={{width:36,height:36,borderRadius:6,border:`1px solid ${border}`,background:"none",cursor:"pointer",fontSize:"1.1rem",fontWeight:700,color:bookReaderFontIdx===fontSizes.length-1?menuSub:gold,lineHeight:1}}>A+</button>
                  </div>
                </div>

                <div style={{height:1,background:border,margin:"4px 0 16px"}}/>

                {/* Espaçamento */}
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:".68rem",letterSpacing:"2px",textTransform:"uppercase",color:menuSub,marginBottom:8}}>Espaçamento</div>
                  <div style={{display:"flex",gap:4}}>
                    {["Compacto","Normal","Espaçado","Amplo"].map((lbl,i)=>(
                      <button key={i} onClick={()=>setBookReaderLineHIdx(i)}
                        style={{flex:1,padding:"5px 0",fontSize:".72rem",fontWeight:i===bookReaderLineHIdx?700:400,cursor:"pointer",borderRadius:4,border:`1px solid ${i===bookReaderLineHIdx?gold:border}`,background:i===bookReaderLineHIdx?(dark?"#3a2a10":"#fde9c4"):"none",color:i===bookReaderLineHIdx?gold:menuSub,transition:"all .15s"}}>{lbl}</button>
                    ))}
                  </div>
                </div>

                <div style={{height:1,background:border,margin:"4px 0 16px"}}/>

                {/* Largura */}
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:".68rem",letterSpacing:"2px",textTransform:"uppercase",color:menuSub,marginBottom:8}}>Largura do Texto</div>
                  <div style={{display:"flex",gap:4}}>
                    {["Estreita","Normal","Larga"].map((lbl,i)=>(
                      <button key={i} onClick={()=>setBookReaderWidthIdx(i)}
                        style={{flex:1,padding:"5px 0",fontSize:".72rem",fontWeight:i===bookReaderWidthIdx?700:400,cursor:"pointer",borderRadius:4,border:`1px solid ${i===bookReaderWidthIdx?gold:border}`,background:i===bookReaderWidthIdx?(dark?"#3a2a10":"#fde9c4"):"none",color:i===bookReaderWidthIdx?gold:menuSub,transition:"all .15s"}}>{lbl}</button>
                    ))}
                  </div>
                </div>

                <div style={{height:1,background:border,margin:"4px 0 16px"}}/>

                {/* Brilho */}
                <div>
                  <div style={{fontSize:".68rem",letterSpacing:"2px",textTransform:"uppercase",color:menuSub,marginBottom:8}}>
                    🔆 Brilho &nbsp;<span style={{color:gold,fontWeight:700}}>{bri}%</span>
                  </div>
                  <input type="range" min={40} max={130} step={5} value={bri}
                    onChange={e=>setBookReaderBrightness(Number(e.target.value))}
                    style={{width:"100%",accentColor:gold,cursor:"pointer"}}
                  />
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:".65rem",color:menuSub,marginTop:3}}>
                    <span>Escuro</span><span>Normal</span><span>Brilhante</span>
                  </div>
                </div>

                {hasBm && (
                  <>
                    <div style={{height:1,background:border,margin:"16px 0"}}/>
                    <div>
                      <div style={{fontSize:".68rem",letterSpacing:"2px",textTransform:"uppercase",color:menuSub,marginBottom:8}}>Marcador</div>
                      <button
                        onClick={()=>{ removeBookmark(); setBookReaderMenu(false); }}
                        style={{width:"100%",padding:"9px 12px",background:"none",border:"1px solid #c0392b",borderRadius:6,cursor:"pointer",color:"#e05555",fontSize:".82rem",fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",gap:6,transition:"all .2s"}}>
                        🗑️ Remover marcador
                      </button>
                    </div>
                  </>
                )}

              </div>
            )}

            {/* ══ MODO ROLAGEM ══ */}
            {!bookReaderPageMode && (
              <div className="reader-scroll-inner" style={{maxWidth:mxW,margin:"0 auto",filter:`brightness(${bri}%)`}} onClick={()=>bookReaderMenu&&setBookReaderMenu(false)}>
                <div id="reader-cover">{renderCover()}</div>
                {hasChars && <div id="reader-chars">{renderChars()}</div>}
                {doneChaps.map((ch, idx) => (
                  <div key={ch.id} id={`reader-ch-${ch.id}`}>
                    {renderChapter(ch, idx, idx < doneChaps.length - 1)}
                  </div>
                ))}
              </div>
            )}

            {/* ══ MODO PAGINADO ══ */}
            {bookReaderPageMode && (
              <>
                {/* Área de conteúdo (ocupa entre topbar+barra e navbar) */}
                <div
                  key={safePage}
                  className={`${bookReaderPageDir >= 0 ? "reader-page-fwd" : "reader-page-back"} reader-content-paged`}
                  style={{position:"fixed",top:66,left:0,right:0,bottom:60,overflowY:"auto",filter:`brightness(${bri}%)`}}
                  onClick={()=>bookReaderMenu&&setBookReaderMenu(false)}
                  onScroll={e=>{
                    const el = e.target;
                    const ratio = el.scrollHeight <= el.clientHeight ? 0 : el.scrollTop / (el.scrollHeight - el.clientHeight);
                    setBookReaderPageScroll(ratio);
                  }}
                  onTouchStart={e=>{
                    window._rSwX = e.touches[0].clientX;
                    window._rSwY = e.touches[0].clientY;
                  }}
                  onTouchEnd={e=>{
                    const dx = (window._rSwX||0) - e.changedTouches[0].clientX;
                    const dy = Math.abs((window._rSwY||0) - e.changedTouches[0].clientY);
                    if(Math.abs(dx) > 48 && Math.abs(dx) > dy * 1.2) {
                      if(dx > 0 && safePage < totalPages-1) {
                        setBookReaderPageDir(1); setBookReaderPageScroll(0);
                        setBookReaderPage(p=>p+1);
                      } else if(dx < 0 && safePage > 0) {
                        setBookReaderPageDir(-1); setBookReaderPageScroll(0);
                        setBookReaderPage(p=>p-1);
                      }
                    }
                  }}
                >
                  <div className="reader-page-inner" style={{maxWidth:mxW,margin:"0 auto"}}>
                    {curPageData === 'cover' && renderCover()}
                    {curPageData === 'chars' && renderChars()}
                    {curIsChap && renderChapter(curPageData.ch, curPageData.chIdx, false)}
                  </div>
                </div>

                {/* ── Barra de navegação inferior — desktop only ── */}
                <div className="reader-bottom-bar" style={{position:"fixed",bottom:0,left:0,right:0,height:60,background:bgBar,borderTop:`1px solid ${border}`,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 14px",zIndex:822,backdropFilter:"blur(8px)"}}>
                  <button className="reader-nav-btn"
                    onClick={()=>{ setBookReaderPageDir(-1); setBookReaderPageScroll(0); setBookReaderPage(p=>Math.max(0,p-1)); }}
                    disabled={safePage===0}
                    style={{background:"none",border:`1px solid ${safePage===0?border:gold}`,borderRadius:6,padding:"7px 18px",cursor:safePage===0?"default":"pointer",color:safePage===0?menuSub:gold,fontSize:".82rem",fontWeight:600,transition:"all .2s",minWidth:80}}
                  >← Anterior</button>
                  <button className="reader-nav-btn"
                    onClick={()=>{ setBookReaderPageDir(1); setBookReaderPageScroll(0); setBookReaderPage(p=>Math.min(totalPages-1,p+1)); }}
                    disabled={safePage===totalPages-1}
                    style={{background:safePage===totalPages-1?"none":gold,border:`1px solid ${safePage===totalPages-1?border:gold}`,borderRadius:6,padding:"7px 18px",cursor:safePage===totalPages-1?"default":"pointer",color:safePage===totalPages-1?menuSub:"#fff",fontSize:".82rem",fontWeight:600,transition:"all .2s",minWidth:80}}
                  >Próximo →</button>
                </div>

              </>
            )}
          </div>
        );
      })()}

      {showDraftReader && (
        <div style={{position:"fixed",inset:0,zIndex:800,background:"#f5ede0",overflowY:"auto"}}>
          <div style={{maxWidth:680,margin:"0 auto",padding:"60px 32px 120px"}}>
            {/* Close */}
            <button onClick={()=>setShowDraftReader(false)} style={{position:"fixed",top:20,right:24,background:"#1a1208",color:"#d4a94a",border:"none",borderRadius:99,padding:"8px 18px",cursor:"pointer",fontFamily:"'Crimson Pro',Georgia,serif",fontSize:".85rem",fontWeight:700,zIndex:801}}>✕ Fechar</button>
            {/* Title block */}
            <div style={{textAlign:"center",marginBottom:60}}>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:"2rem",fontWeight:900,color:"#1a1208"}}>{bibliaPanel.title||config.title||"Rascunho"}</div>
              {config.author && <div style={{fontSize:".95rem",color:"#7a6a50",marginTop:8,fontStyle:"italic"}}>por {config.author}</div>}
              <div style={{marginTop:20,color:"#b8892a",fontSize:"1.2rem",letterSpacing:8}}>✦ ✦ ✦</div>
            </div>
            {/* Chapters */}
            {chapters.filter(c=>c.status==="done"&&c.content).map((ch,idx,arr)=>(
              <div key={ch.id} style={{marginBottom:64}}>
                <div style={{textAlign:"center",marginBottom:28}}>
                  <div style={{fontSize:".72rem",color:"#b8892a",letterSpacing:"3px",textTransform:"uppercase",marginBottom:8}}>Capítulo {ch.id}</div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.4rem",fontWeight:700,color:"#1a1208"}}>{ch.title.replace(/^(Capítulo|Chapter)\s*\d+:\s*/i,"")}</div>
                  <div style={{color:"#b8892a",marginTop:12,fontSize:"1rem"}}>✦</div>
                </div>
                <div style={{fontFamily:"'Crimson Pro',Georgia,serif",fontSize:"1.15rem",lineHeight:1.9,color:"#1a1208",whiteSpace:"pre-wrap",textAlign:"justify"}}>{ch.content}</div>
                {idx<arr.length-1&&<div style={{textAlign:"center",marginTop:48,color:"#c8b08a",fontSize:"1.2rem",letterSpacing:8}}>✦ ✦ ✦</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══ ONBOARDING TOUR ══ */}
      {onboardingStep && onboardingStep > 0 && phase==="biblia" && bibliaChat.length > 0 && (
        <div style={{position:"fixed",inset:0,zIndex:900,pointerEvents:"none"}}>
          <div style={{position:"fixed",bottom:90,left:"50%",transform:"translateX(-50%)",background:"#1a1208",border:"2px solid #d4a94a",borderRadius:10,padding:"18px 22px",maxWidth:340,width:"calc(100% - 40px)",boxShadow:"0 8px 32px rgba(0,0,0,.5)",pointerEvents:"all",zIndex:901}}>
            <div style={{fontSize:".62rem",color:"#d4a94a",textTransform:"uppercase",letterSpacing:"1px",marginBottom:8}}>Passo {onboardingStep} de 3</div>
            <div style={{color:"#f0e8d8",fontSize:"1rem",fontFamily:"'Crimson Pro',Georgia,serif",lineHeight:1.65,marginBottom:16}}>
              {onboardingStep===1 && "Esta é sua Bíblia. Converse com a IA para desenvolver sua história — personagens, mundo e estrutura. A IA preenche os campos da direita automaticamente."}
              {onboardingStep===2 && "Quando terminar de conversar, clique em 🔍 Revisar Bíblia. A IA vai checar inconsistências, corrigir campos vazios e aprovar sua bíblia."}
              {onboardingStep===3 && "Após a revisão, clique em ✨ Criar Roteiro para gerar o esboço capítulo a capítulo. Depois é só escrever!"}
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{display:"flex",gap:5}}>
                {[1,2,3].map(i=><span key={i} style={{width:6,height:6,borderRadius:"50%",background:i===onboardingStep?"#d4a94a":"#4a3a20",display:"inline-block"}}/>)}
              </div>
              <button style={{background:"linear-gradient(135deg,#b8892a,#d4a94a)",border:"none",borderRadius:4,padding:"8px 18px",color:"#1a1208",fontWeight:700,cursor:"pointer",fontFamily:"'Crimson Pro',Georgia,serif",fontSize:".9rem"}}
                onClick={()=>{if(onboardingStep<3){setOnboardingStep(s=>s+1);}else{setOnboardingStep(null);localStorage.setItem("autoria_onboarding_done","1");}}}>
                {onboardingStep<3?"Próximo →":"Entendi! ✓"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPricing && (
        <div style={{position:"fixed",inset:0,background:"rgba(26,18,8,.7)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setShowPricing(false)}>
          <div className="pricing-inner" style={{background:"#1c1b1b",border:"1px solid rgba(120,109,89,.6)",borderRadius:6,padding:"28px 22px",maxWidth:560,width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,.4)"}} onClick={e=>e.stopPropagation()}>
            <div style={{textAlign:"center",marginBottom:24}}>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.8rem",fontWeight:900,color:"#f3eee4"}}>Autor<span style={{color:"#d4a94a"}}>IA</span></div>
              <div style={{fontSize:".9rem",color:"#d1c5b4",marginTop:4}}>Escolha o plano ideal para sua criação</div>
            </div>
            {[
              {name:"Iniciante",price:"Grátis",desc:"Experimente sem compromisso",color:"#4caf50",features:["1 livro completo (trial)","Bíblia do livro completa","Roteiro com estrutura de 3 atos","Exportação PDF e EPUB"]},
              {name:"Escritor",price:"R$ 59/mês",desc:"Para escritores dedicados",color:"#b8892a",features:["Até 5 livros por mês","Séries de até 3 volumes","Planejamento de cenas por capítulo","Todos os campos da bíblia","Personagens com profundidade total","Revisão editorial por IA"],highlight:true},
              {name:"Profissional",price:"R$ 119/mês",desc:"Para autores produtivos",color:"#8b2020",features:["Até 15 livros por mês","Séries ilimitadas","Verificação de consistência","Exportação profissional PDF e EPUB","Suporte prioritário","Acesso antecipado a novas features"]},
            ].map(plan => (
              <div key={plan.name} style={{border:`2px solid ${plan.highlight?"#b8892a":"rgba(120,109,89,.55)"}`,borderRadius:5,padding:"18px 20px",marginBottom:12,background:plan.highlight?"rgba(233,193,118,.12)":"#20201f",position:"relative"}}>
                {plan.highlight && <div style={{position:"absolute",top:-10,right:16,background:"#b8892a",color:"#fff",fontSize:".65rem",fontWeight:800,padding:"2px 10px",borderRadius:99,letterSpacing:"1px"}}>MAIS POPULAR</div>}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.1rem",fontWeight:700,color:"#f3eee4"}}>{plan.name}</div>
                    <div style={{fontSize:".8rem",color:"#d1c5b4"}}>{plan.desc}</div>
                  </div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:"1.3rem",fontWeight:900,color:plan.color}}>{plan.price}</div>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:3}}>
                  {plan.features.map(f=>(
                    <div key={f} style={{fontSize:".82rem",color:"#f0e9dd",display:"flex",alignItems:"center",gap:6}}>
                      <span style={{color:plan.color,fontWeight:700}}>✓</span>{f}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div style={{textAlign:"center",marginTop:8}}>
              <div style={{fontSize:".75rem",color:"#d1c5b4",marginBottom:12}}>Cancele a qualquer momento. Sem taxa de cancelamento.</div>
              <button onClick={()=>setShowPricing(false)} style={{background:"none",border:"none",color:"#e9c176",cursor:"pointer",fontSize:".85rem",textDecoration:"underline"}}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
